import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

const canonicalBuilderPath = '/pages/board-customizer';
const canonicalBuilderView = 'board-customizer-original';
const normalizeBuilderRoute = () => {
  const currentUrl = new URL(window.location.href);
  const canonicalizePath = pathname => {
    const match = pathname.match(/^(.*)\/pages\/board-customizer(?:-live)?\/?$/);
    return match ? `${match[1]}${canonicalBuilderPath}` : null;
  };

  document.querySelectorAll('a[href]').forEach(anchor => {
    const anchorUrl = new URL(anchor.href, window.location.origin);
    if (anchorUrl.origin !== window.location.origin) return;
    const canonicalPath = canonicalizePath(anchorUrl.pathname);
    if (!canonicalPath) return;
    anchorUrl.pathname = canonicalPath;
    if (anchorUrl.searchParams.get('view') === canonicalBuilderView) anchorUrl.searchParams.delete('view');
    anchor.setAttribute('href', `${anchorUrl.pathname}${anchorUrl.search}${anchorUrl.hash}`);
  });

  const canonicalPath = canonicalizePath(currentUrl.pathname);
  if (!canonicalPath) return false;

  if (currentUrl.pathname !== canonicalPath) {
    currentUrl.pathname = canonicalPath;
    if (currentUrl.searchParams.get('view') === canonicalBuilderView) currentUrl.searchParams.delete('view');
    window.location.replace(currentUrl.toString());
    return true;
  }

  if (currentUrl.searchParams.get('view') === canonicalBuilderView) {
    currentUrl.searchParams.delete('view');
    window.history.replaceState(window.history.state, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    return false;
  }

  const isCachedLegacyDocument = document.querySelector('[id*="board_customizer_ready_20260721"]');
  if (!currentUrl.searchParams.has('view') && isCachedLegacyDocument) {
    currentUrl.searchParams.set('view', canonicalBuilderView);
    window.location.replace(currentUrl.toString());
    return true;
  }

  return false;
};

normalizeBuilderRoute();

const fallbackMotion = {
  killTweensOf() {},
  to(target, vars = {}) {
    if (target) {
      ['x', 'y', 'z'].forEach(key => {
        if (!(key in vars)) return;
        const value = vars[key];
        if (typeof value === 'number') {
          target[key] = value;
          return;
        }
        const relative = typeof value === 'string' ? value.match(/^([+-])=(\d+(?:\.\d+)?)$/) : null;
        if (relative) {
          const delta = Number.parseFloat(relative[2]) * (relative[1] === '-' ? -1 : 1);
          target[key] = (Number(target[key]) || 0) + delta;
        }
      });
    }
    vars.onUpdate?.();
    vars.onComplete?.();
    return { kill() {} };
  }
};

const getMotion = () => window.gsap || fallbackMotion;

class BoardCustomizer extends HTMLElement {
  constructor() {
    super();
    this.decks = this.enrichFilterItems(this.parseJsonAttribute('data-decks'), 'deck');
    this.grips = this.normalizePriceItems(this.parseJsonAttribute('data-grips'));
    this.wheels = this.enrichFilterItems(this.parseJsonAttribute('data-wheels'), 'wheel');
    this.bearings = this.normalizePriceItems(this.parseJsonAttribute('data-bearings'));
    this.trucks = this.enrichFilterItems(this.parseJsonAttribute('data-trucks'), 'truck');
    this.hardware = this.normalizePriceItems(this.parseJsonAttribute('data-hardware'));
    this.bolts = this.normalizePriceItems(this.parseJsonAttribute('data-bolts'));
    if (!this.hardware.length) this.hardware = this.bolts;
    this.metals = this.parseJsonAttribute('data-metals');
    this.modelUrl = this.getAttribute('data-model-url');
    this.rootUrl = this.getAttribute('data-root-url') || '/';
    this.fallbackCurrency = this.getAttribute('data-fallback-currency') || 'SGD';
    this.boardPromotionEnabled = this.getAttribute('data-board-promotion-enabled') === 'true';
    this.boardPromotionPercent = this.parsePriceCents(this.getAttribute('data-board-promotion-percent')) || 20;
    const memberDiscountPercent = this.parsePriceCents(this.getAttribute('data-member-discount'));
    this.memberDiscountPercent = [10, 15, 20].includes(memberDiscountPercent) ? memberDiscountPercent : 0;
    this.memberTier = this.getAttribute('data-member-tier') || 'Member';
    this.defaultDeckUrl = this.getAttribute('data-default-deck');
    this.defaultGripUrl = this.getAttribute('data-default-grip');
    this.defaultWheelUrl = this.getAttribute('data-default-wheel');
    this.defaultGripColor = this.getAttribute('data-default-grip-color');
    this.defaultTruckColor = this.getAttribute('data-default-truck-color');
    this.defaultBearingColor = this.getAttribute('data-default-bearing-color');
    this.defaultHardwareColor = this.getAttribute('data-default-hardware-color');
    this.defaultBoltColor = this.getAttribute('data-default-bolt-color');
    this.productVariantId = this.getAttribute('data-product-variant-id');
    this.selectedDeck = null;
    this.selectedGrip = null;
    this.selectedWheel = null;
    this.selectedBearing = null;
    this.selectedTruck = null;
    this.selectedHardware = null;
    this.selectedSkateTool = null;
    this.selectedAssembly = null;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.textureLoader = new THREE.TextureLoader();
    this.deckMesh = null;
    this.gripTapeMesh = null;
    this.boltsMesh = null;
    this.baseplatesMesh = null;
    this.bearingRefs = [];
    this.truck1Mesh = null;
    this.truck2Mesh = null;
    this.wheelRefs = [];
    this.deckMaterial = null;
    this.gripMaterial = null;
    this.wheelMaterial = null;
    this.bearingMaterial = null;
    this.truckHangerMaterial = null;
    this.truckBaseplateMaterial = null;
    this.truckBushingTopMaterial = null;
    this.truckBushingBottomMaterial = null;
    this.truckWasherTopMaterial = null;
    this.truckWasherBottomMaterial = null;
    this.boltMaterial = null;
    this.axleNutMaterial = null;
    this.kingpinNutMaterial = null;
    this.bearingCapGeometry = null;
    this.defaultCameraPosition = new THREE.Vector3(3.375, 1.425, 0);
    this.defaultCameraTarget = new THREE.Vector3(0, 0.3, 0);
    this.textureCache = {};
    this.texturePromises = {};
    this.textureUsage = new Map();
    this.textureUsageSequence = 0;
    this.textureCacheLimit = 12;
    this.textureWarmQueue = [];
    this.textureWarmQueuedUrls = new Set();
    this.textureWarmActive = 0;
    this.textureWarmConcurrency = 2;
    this.textureWarmQueueLimit = 12;
    this.fastSurfaceTextureWidth = 256;
    this.textureFetchAccept = 'image/webp';
    this.drawerTextureObservers = new Map();
    this.drawerTextureGenerations = {};
    this.textureLifecycleGeneration = 0;
    this.deckTextureRequestId = 0;
    this.gripTextureRequestId = 0;
    this.wheelTextureRequestId = 0;
    THREE.Cache.enabled = true;
    this.scrollLockY = 0;
    this.scrollLockCount = 0;
    this.scrollLockUsesFixedBody = false;
    this.lastFocusedElement = null;
    this.pendingCartRemovals = new Set();
    this.cartChangeQueue = Promise.resolve();
    this.cartActiveChanges = 0;
    this.cartLoadRequestId = 0;
    this.cartRefreshAfterChangeId = 0;
    this.cartDrawerItemOrder = [];
    this.cartDrawerCloseTimer = 0;
    this.cartDrawerSuppressCloseUntil = 0;
    this.cartDrawerSuppressOpenUntil = 0;
    this.cartDrawerIsOpenOrOpening = false;
    this.cartDrawerScrollLocked = false;
    this.cartViewportListenersBound = false;
    this.cartClearReturnFocus = null;
    this.cartClearRenderToken = 0;
    this.cartLastFetchedAt = 0;
    this.latestCart = null;
    this.initialAssemblyChoice = '';
    this.isAddingToCart = false;
    this.boardAddAttempt = null;
    this.boardAddGeneration = 0;
    this.boardAddSuspended = false;
    this.boardAddWork = null;
    this.activeDrawerFilters = {};
    this.activeDrawerSearch = {};
    this.activeDrawerSort = {};
    this.imageModalRequestId = 0;
    this.loadedDrawerImages = new Set();
    this.imagePreloadPromises = new Map();
    this.drawerImageObservers = new Map();
    this.persistentScrollbarCleanups = [];
    this.persistentScrollbarSyncs = [];
    this.imageModalLastFocusedElement = null;
    this.imageModalBackgroundDrawer = null;
    this.imageModalImages = [];
    this.imageModalGallerySources = [];
    this.imageModalIndex = 0;
    this.imageModalSwipe = null;
    this.imageModalScrollTarget = null;
    this.imageModalScrollTimer = 0;
    this.imageModalSwipeCleanup = null;
    this.collectionDrawerOpeners = new WeakMap();
    this.drawerOpenRequestId = 0;
    this.menuLastFocusedElement = null;
    this.hoveredProductCard = null;
    this.animationFrameId = null;
    this.canvasResizeObserver = null;
    this.resizeFrameId = 0;
    this.lastResizeWidth = 0;
    this.lastResizeHeight = 0;
    this.lastScaleSignature = '';
    this.handleWindowResize = () => this.scheduleResize();
    this.handleCartViewportResize = () => this.syncCartDrawerViewportHeight();
    this.onCollectionDrawerKeydown = event => this.handleCollectionDrawerKeydown(event);
  }
  parseJsonAttribute(attributeName) {
    const rawValue = this.getAttribute(attributeName) || '[]';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = rawValue;
    try {
      return JSON.parse(textarea.value);
    } catch (error) {
      console.error(`Board customizer could not parse ${attributeName}:`, error);
      return [];
    }
  }
  parsePriceCents(value) {
    if (typeof value === 'number') {
      return Number.isSafeInteger(value) && value >= 0 ? value : null;
    }
    if (typeof value !== 'string') return null;
    const normalized = value.trim();
    if (!/^\d+$/.test(normalized)) return null;
    const cents = Number(normalized);
    return Number.isSafeInteger(cents) ? cents : null;
  }
  normalizePriceItem(item) {
    if (!item || typeof item !== 'object') return item;
    const legacyPriceCents = this.parsePriceCents(item.priceCents);
    const displayPriceCents = this.parsePriceCents(item.displayPriceCents) ?? legacyPriceCents;
    const variantPriceCents = this.parsePriceCents(item.variantPriceCents);
    const deckVariants = Array.isArray(item.deckVariants)
      ? item.deckVariants.map(variant => this.normalizePriceItem(variant))
      : item.deckVariants;
    return {
      ...item,
      displayPriceCents: displayPriceCents ?? variantPriceCents,
      variantPriceCents,
      ...(Array.isArray(deckVariants) ? { deckVariants } : {})
    };
  }
  normalizePriceItems(items) {
    return Array.isArray(items) ? items.map(item => this.normalizePriceItem(item)) : [];
  }
  getBoardPromotionPriceState(type, value, memberDiscountPercent = this.memberDiscountPercent) {
    const rawCents = this.parsePriceCents(value);
    if (rawCents === null) {
      return {
        rawCents: null,
        boardDiscountCents: null,
        afterBoardCents: null,
        memberDiscountCents: null,
        finalCents: null
      };
    }
    const normalizedType = String(type || '').toLowerCase();
    const isBoardComponent = ['deck', 'grip', 'truck', 'wheel', 'bearing', 'hardware', 'skate_tool'].includes(normalizedType);
    const promotionEnabled = this.boardPromotionEnabled !== false;
    const promotionPercent = this.parsePriceCents(this.boardPromotionPercent) || 20;
    const normalizedMemberPercent = this.parsePriceCents(memberDiscountPercent);
    const memberPercent = [10, 15, 20].includes(normalizedMemberPercent) ? normalizedMemberPercent : 0;
    const boardDiscountCents = promotionEnabled && isBoardComponent
      ? Math.floor((rawCents * promotionPercent) / 100)
      : 0;
    const afterBoardCents = rawCents - boardDiscountCents;
    const memberDiscountCents = Math.floor((afterBoardCents * memberPercent) / 100);
    return {
      rawCents,
      boardDiscountCents,
      afterBoardCents,
      memberDiscountCents,
      finalCents: afterBoardCents - memberDiscountCents
    };
  }
  getSummaryPriceState(parts = []) {
    let selectedCount = 0;
    let subtotalCents = 0;
    let boardDiscountCents = 0;
    let afterBoardDiscountCents = 0;
    let memberDiscountCents = 0;
    let discountedSubtotalCents = 0;
    let hasUnknownPrice = false;

    (Array.isArray(parts) ? parts : []).forEach(part => {
      const selection = part?.sel;
      if (!selection || selection.isPlaceholder) return;

      selectedCount += 1;
      const priceState = this.getBoardPromotionPriceState(part?.key, selection.variantPriceCents);
      if (priceState.rawCents === null) {
        hasUnknownPrice = true;
        return;
      }
      subtotalCents += priceState.rawCents;
      boardDiscountCents += priceState.boardDiscountCents;
      afterBoardDiscountCents += priceState.afterBoardCents;
      memberDiscountCents += priceState.memberDiscountCents;
      discountedSubtotalCents += priceState.finalCents;
    });

    return {
      selectedCount,
      subtotalCents: hasUnknownPrice ? null : subtotalCents,
      boardDiscountCents: hasUnknownPrice ? null : boardDiscountCents,
      afterBoardDiscountCents: hasUnknownPrice ? null : afterBoardDiscountCents,
      memberDiscountCents: hasUnknownPrice ? null : memberDiscountCents,
      discountedSubtotalCents: hasUnknownPrice ? null : discountedSubtotalCents,
      hasUnknownPrice
    };
  }
  formatSummaryMoney(cents) {
    const priceCents = this.parsePriceCents(cents);
    if (priceCents === null) return null;
    return this.formatMoney(priceCents).replace(/\s+/g, ' ').trim();
  }
  normalizeTagList(tags) {
    if (Array.isArray(tags)) return tags.map(tag => String(tag).trim()).filter(Boolean);
    if (!tags) return [];
    return String(tags).split('|').map(tag => tag.trim()).filter(Boolean);
  }
  getBuilderTagValue(tags, key) {
    const prefix = `${key}:`;
    const match = this.normalizeTagList(tags).find(tag => tag.toLowerCase().startsWith(prefix));
    return match ? match.slice(prefix.length).trim() : '';
  }
  normalizeWidthValue(value) {
    const match = String(value || '').replace(',', '.').match(/(\d{1,2}(?:\.\d{1,3})?)/);
    if (!match) return '';
    const width = Number.parseFloat(match[1]);
    if (!Number.isFinite(width) || width < 6 || width > 12) return '';
    const label = width.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
    return label.includes('.') ? label : `${label}.0`;
  }
  normalizeDeckFitRange(value) {
    if (Array.isArray(value)) return this.normalizeDeckFitRange(value.join('-'));
    const text = String(value || '').replace(/[–—]/g, '-').replace(/\bto\b/gi, '-');
    const widths = (text.match(/\d{1,2}(?:[.,]\d{1,3})?/g) || [])
      .map(part => this.normalizeWidthValue(part))
      .filter(Boolean);
    if (!widths.length) return '';
    if (widths.length === 1) return widths[0];
    const range = widths.slice(0, 2).map(part => Number.parseFloat(part)).sort((a, b) => a - b);
    return `${this.normalizeWidthValue(range[0])}-${this.normalizeWidthValue(range[1])}`;
  }
  parseDeckFitRange(value) {
    const label = this.normalizeDeckFitRange(value);
    if (!label) return null;
    const parts = label.split('-').map(part => Number.parseFloat(part)).filter(Number.isFinite);
    if (!parts.length) return null;
    const min = Math.min(parts[0], parts[1] ?? parts[0]);
    const max = Math.max(parts[0], parts[1] ?? parts[0]);
    return { min, max, label };
  }
  extractWidthFromName(name, requireDimension = false) {
    const text = String(name || '');
    const dimensionMatch = text.match(/(\d{1,2}(?:\.\d{1,3})?)\s*(?:"|″|”|in|inch)?\s*[x×]\s*\d/i);
    if (dimensionMatch) return this.normalizeWidthValue(dimensionMatch[1]);
    if (requireDimension) return '';
    const looseMatch = text.match(/(?:^|[\s(/-])(\d{1,2}(?:\.\d{1,3})?)\s*(?:"|″|”|in|inch)?(?:[\s)/-]|$)/i);
    return looseMatch ? this.normalizeWidthValue(looseMatch[1]) : '';
  }
  extractTruckWidthFromName(name) {
    const text = String(name || '');
    const parts = text.split(/\s+-\s+/).map(part => part.trim()).filter(Boolean);
    if (parts.length > 1) {
      const variantWidth = this.extractWidthFromName(parts[parts.length - 1], false);
      if (variantWidth) return variantWidth;
    }
    const parsedWidth = this.extractWidthFromName(name, false);
    if (parsedWidth) return parsedWidth;
    const lowerText = text.toLowerCase();
    const independentMatch = lowerText.match(/\bindependent\b[\s\S]*\b(139|144)\b|\b(139|144)\b[\s\S]*\bindependent\b/);
    const independentSize = independentMatch ? (independentMatch[1] || independentMatch[2]) : '';
    if (independentSize === '139') return '8.0';
    if (independentSize === '144') return '8.25';
    return '';
  }
  escapeRegExp(text) {
    return String(text || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  getVariantTitle(item) {
    const title = String(item?.variantTitle || '').trim();
    return title && title !== 'Default Title' ? title : '';
  }
  getVariantSizeCandidate(item = {}) {
    const variantTitle = this.getVariantTitle(item);
    const sourceText = [
      variantTitle,
      Array.isArray(item?.variantOptions) ? item.variantOptions.join(' ') : '',
      item?.selectedSize || ''
    ].filter(Boolean).join(' ');
    const cleanedText = sourceText
      .replace(/\+\s*\$?\d+(?:\.\d{1,2})?/g, ' ')
      .replace(/\b(?:no|without|add|with)\s+grip(?:\s*tape)?\b/gi, ' ')
      .replace(/\bgriptape\b|\bgrip\s*tape\b|\bdeck\s*only\b/gi, ' ')
      .replace(/\bdefault\s+title\b/gi, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    const width = this.normalizeWidthValue(cleanedText) || this.extractWidthFromName(cleanedText, false);
    const rawMatch = cleanedText.match(/\d{1,3}(?:[.,]\d{1,3})?/);
    const raw = rawMatch ? rawMatch[0].replace(',', '.') : '';
    return {
      raw,
      width,
      label: width ? this.formatSpecValue('width', width) : raw
    };
  }
  getVariantDimensionLabel(item = {}) {
    const sourceText = [
      this.getVariantTitle(item),
      Array.isArray(item?.variantOptions) ? item.variantOptions.join(' ') : '',
      item?.selectedSize || ''
    ].filter(Boolean).join(' ');
    const cleanedText = sourceText
      .replace(/\+\s*\$?\d+(?:\.\d{1,2})?/g, ' ')
      .replace(/\b(?:no|without|add|with)\s+grip(?:\s*tape)?\b/gi, ' ')
      .replace(/\bgriptape\b|\bgrip\s*tape\b|\bdeck\s*only\b/gi, ' ')
      .replace(/\bdefault\s+title\b/gi, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    const match = cleanedText.match(/\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?\s*[x×]\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?/i);
    return match ? match[0].replace(/\s+/g, ' ').trim() : '';
  }
  hasVariantSizeGroup(title) {
    const text = String(title || '');
    return (
      /\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?\s*[x×]\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?(?:\s*\/\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?\s*[x×]\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?)+/i.test(text) ||
      /\b\d{1,3}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?(?:\s*\/\s*\d{1,3}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?)+/i.test(text)
    );
  }
  insertDeckDimension(title, dimensionLabel) {
    const baseTitle = String(title || '').trim();
    const dimension = String(dimensionLabel || '').trim();
    if (!baseTitle || !dimension) return baseTitle;
    if (baseTitle.toLowerCase().includes(dimension.toLowerCase())) return baseTitle;
    if (/\bskateboard\s+deck\b/i.test(baseTitle)) {
      return baseTitle.replace(/\bskateboard\s+deck\b/i, `${dimension} Skateboard Deck`);
    }
    if (/\bdeck\b/i.test(baseTitle)) {
      return baseTitle.replace(/\bdeck\b/i, `${dimension} Deck`);
    }
    return `${baseTitle} - ${dimension}`;
  }
  normalizeNumericToken(value) {
    const match = String(value || '').replace(',', '.').match(/\d{1,3}(?:\.\d{1,3})?/);
    if (!match) return '';
    const number = Number.parseFloat(match[0]);
    if (!Number.isFinite(number)) return '';
    return number.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  }
  chooseVariantSegment(segments, sizeCandidate) {
    const wanted = this.normalizeNumericToken(sizeCandidate.width || sizeCandidate.raw || sizeCandidate.label);
    if (!wanted) return '';
    return segments.find(segment => {
      const segmentNumber = this.normalizeNumericToken(segment);
      return segmentNumber && segmentNumber === wanted;
    }) || '';
  }
  chooseTruckVariantSegment(segments, sizeCandidate) {
    const exact = this.chooseVariantSegment(segments, sizeCandidate);
    if (exact) return exact;
    const wanted = Number.parseFloat(this.normalizeNumericToken(sizeCandidate.width || sizeCandidate.raw || sizeCandidate.label));
    if (!Number.isFinite(wanted) || segments.length !== 2) return '';
    const segmentNumbers = segments
      .map(segment => Number.parseFloat(this.normalizeNumericToken(segment)))
      .filter(Number.isFinite);
    if (segmentNumbers.length !== 2) return '';
    const maxSegment = Math.max(...segmentNumbers);
    if (maxSegment <= 6 || maxSegment >= 100) {
      return wanted <= 8 ? segments[0] : segments[1];
    }
    return '';
  }
  getVariantSpecificTitle(productTitle, item = {}) {
    const title = String(productTitle || '').trim();
    if (!title) return '';
    const sizeCandidate = this.getVariantSizeCandidate(item);
    if (!sizeCandidate.raw && !sizeCandidate.width) return title;
    const chooseSegment = segments => (
      item?.type === 'truck'
        ? this.chooseTruckVariantSegment(segments, sizeCandidate)
        : this.chooseVariantSegment(segments, sizeCandidate)
    );
    let nextTitle = title;
    nextTitle = nextTitle.replace(
      /\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?\s*[x×]\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?(?:\s*\/\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?\s*[x×]\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?)+/gi,
      match => {
        const chosen = chooseSegment(match.split(/\s*\/\s*/));
        return chosen || match;
      }
    );
    nextTitle = nextTitle.replace(
      /\b\d{1,3}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?(?:\s*\/\s*\d{1,3}(?:[.,]\d{1,3})?\s*(?:"|″|”|in|inch)?)+/gi,
      match => {
        const chosen = chooseSegment(match.split(/\s*\/\s*/));
        return chosen || match;
      }
    );
    return nextTitle.replace(/\s{2,}/g, ' ').trim();
  }
  stripVariantMeasurementsFromTitle(title, variantTitle) {
    let base = String(title || '').trim();
    if (!base) return '';
    base = base
      .replace(/\s+\d{1,2}(?:\.\d{1,3})?\s*(?:"|″|”|in|inch)?\s*[x×]\s*\d{1,2}(?:\.\d{1,3})?\s*(?:"|″|”|in|inch)?(?:\s*\/\s*\d{1,2}(?:\.\d{1,3})?\s*(?:"|″|”|in|inch)?\s*[x×]\s*\d{1,2}(?:\.\d{1,3})?\s*(?:"|″|”|in|inch)?)+/gi, ' ')
      .replace(/\s+\d{1,2}(?:\.\d{1,3})?\s*(?:"|″|”|in|inch)?\s*[x×]\s*\d{1,2}(?:\.\d{1,3})?\s*(?:"|″|”|in|inch)?/gi, ' ');
    const width = this.normalizeWidthValue(variantTitle);
    if (width) {
      const widthPattern = this.escapeRegExp(width).replace(/\\\.0$/, '(?:\\.0)?');
      base = base.replace(new RegExp(`\\s+${widthPattern}\\s*(?:"|″|”|in|inch)?(?=\\s*(?:skateboard\\s+deck|deck|$))`, 'i'), ' ');
    }
    return base.replace(/\s{2,}/g, ' ').trim();
  }
  getItemDisplayName(item) {
    if (!item) return '';
    const variantTitle = this.getVariantTitle(item);
    if (!variantTitle) return item.displayName || item.name || '';
    const productTitle = item.productTitle || String(item.name || '').split(/\s+-\s+/)[0] || '';
    if (productTitle && !this.hasVariantSizeGroup(productTitle)) return productTitle;
    const variantSpecificTitle = this.getVariantSpecificTitle(productTitle, item);
    if (variantSpecificTitle && variantSpecificTitle !== productTitle) return variantSpecificTitle;
    if (item?.type === 'truck' && productTitle && this.hasVariantSizeGroup(productTitle)) return productTitle;
    const dimensionLabel = this.getVariantDimensionLabel(item);
    if (dimensionLabel) return this.insertDeckDimension(productTitle, dimensionLabel);
    const sizeCandidate = this.getVariantSizeCandidate(item);
    const baseTitle = this.stripVariantMeasurementsFromTitle(productTitle, sizeCandidate.label || variantTitle) || productTitle;
    return sizeCandidate.label ? `${baseTitle} - ${sizeCandidate.label}` : baseTitle;
  }
  normalizeWheelDiameter(value) {
    const match = String(value || '').match(/(\d{2})\s*mm/i);
    if (!match) return '';
    const diameter = Number.parseInt(match[1], 10);
    if (!Number.isFinite(diameter) || diameter < 40 || diameter > 80) return '';
    return `${diameter}mm`;
  }
  normalizeWheelHardness(value) {
    const match = String(value || '').match(/(\d{2,3})\s*([ab])/i);
    if (!match) return '';
    const hardness = Number.parseInt(match[1], 10);
    if (!Number.isFinite(hardness) || hardness < 70 || hardness > 110) return '';
    return `${hardness}${match[2].toUpperCase()}`;
  }
  enrichFilterItem(item, type) {
    const normalizedItem = this.normalizePriceItem(item);
    const tags = this.normalizeTagList(normalizedItem?.tags);
    const name = normalizedItem?.name || '';
    const specs = { ...(normalizedItem?.specs || {}) };
    if (type === 'deck') {
      specs.width =
        this.normalizeWidthValue(specs.width) ||
        this.normalizeWidthValue(this.getBuilderTagValue(tags, 'builder_width')) ||
        this.normalizeWidthValue(normalizedItem?.variantTitle) ||
        this.extractWidthFromName(normalizedItem?.variantTitle, false) ||
        this.extractWidthFromName(name, false);
    } else if (type === 'truck') {
      specs.width = this.normalizeWidthValue(specs.width) || this.normalizeWidthValue(this.getBuilderTagValue(tags, 'builder_width')) || this.extractTruckWidthFromName(name);
      specs.fitRange = this.normalizeDeckFitRange(
        specs.fitRange ||
        normalizedItem?.fitRange ||
        this.getBuilderTagValue(tags, 'compatible_deck_width_range') ||
        this.getBuilderTagValue(tags, 'deck_width_range') ||
        this.getBuilderTagValue(tags, 'builder_fit_range') ||
        this.getBuilderTagValue(tags, 'truck_fit_range')
      );
    } else if (type === 'wheel') {
      specs.diameter = this.normalizeWheelDiameter(specs.diameter) || this.normalizeWheelDiameter(this.getBuilderTagValue(tags, 'builder_diameter')) || this.normalizeWheelDiameter(name);
      specs.hardness = this.normalizeWheelHardness(specs.hardness) || this.normalizeWheelHardness(this.getBuilderTagValue(tags, 'builder_hardness')) || this.normalizeWheelHardness(name);
    }
    const enriched = { ...normalizedItem, type: normalizedItem?.type || type, tags, specs };
    return { ...enriched, displayName: this.getItemDisplayName(enriched) };
  }
  enrichFilterItems(items, type) {
    return Array.isArray(items) ? items.map(item => this.enrichFilterItem(item, type)) : [];
  }
  getDeckVariantText(variant = {}) {
    return [
      variant.title,
      ...(Array.isArray(variant.options) ? variant.options : [])
    ].filter(Boolean).join(' ');
  }
  getDeckVariantWidth(variant = {}, item = {}) {
    const optionWidth = Array.isArray(variant.options)
      ? variant.options.map(option => this.normalizeWidthValue(option) || this.extractWidthFromName(option, false)).find(Boolean)
      : '';
    return (
      this.normalizeWidthValue(variant.width) ||
      optionWidth ||
      this.normalizeWidthValue(variant.title) ||
      this.extractWidthFromName(variant.title, false) ||
      this.normalizeWidthValue(item?.specs?.width) ||
      this.extractWidthFromName(item?.name, false)
    );
  }
  getDeckVariantPreferenceScore(variant = {}) {
    const text = this.getDeckVariantText(variant).toLowerCase();
    let score = 0;
    if (!text || text === 'default title') score += 2;
    if (/(^|[\s/-])(?:no|without)\s*grip\b|(?:no|without)\s*griptape|deck\s*only/.test(text)) score += 30;
    if (/\b(add|with)\s*grip\b|\bgrip\s*tape\b|\bgriptape\b/.test(text)) score -= 20;
    return score;
  }
  getDeckSizeChoices(item = {}) {
    const originalVariants = Array.isArray(item.deckVariants) ? item.deckVariants : [];
    const variants = originalVariants.filter(variant => this.isCatalogVariantAvailable(variant.variantId));
    if (originalVariants.length && !variants.length) return [];
    const byWidth = new Map();
    variants.forEach(variant => {
      const width = this.getDeckVariantWidth(variant, item);
      if (!width) return;
      const score = this.getDeckVariantPreferenceScore(variant);
      const existing = byWidth.get(width);
      if (!existing || score > existing.score) {
        byWidth.set(width, { width, variant, score });
      }
    });
    if (!byWidth.size) {
      const width = this.normalizeWidthValue(item?.specs?.width) || this.extractWidthFromName(item?.name, false);
      if (width) byWidth.set(width, { width, variant: null, score: 0 });
    }
    if (!byWidth.size && variants.length) {
      const fallbackVariant = variants.reduce((best, variant) => {
        return this.getDeckVariantPreferenceScore(variant) > this.getDeckVariantPreferenceScore(best) ? variant : best;
      }, variants[0]);
      byWidth.set('default', { width: '', variant: fallbackVariant, score: this.getDeckVariantPreferenceScore(fallbackVariant) });
    }
    return Array.from(byWidth.values()).sort((a, b) => {
      const aNum = Number.parseFloat(a.width);
      const bNum = Number.parseFloat(b.width);
      if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
      if (Number.isFinite(aNum)) return -1;
      if (Number.isFinite(bNum)) return 1;
      return 0;
    });
  }
  getActiveDeckFilterWidth() {
    const value = this.activeDrawerFilters?.deck?.width;
    return value && value !== 'all' ? this.normalizeWidthValue(value) : '';
  }
  getDeckItemForVariant(item, choice) {
    if (!choice) return item;
    const variant = choice.variant || {};
    const width = choice.width || this.getDeckVariantWidth(variant, item);
    const productTitle = item.productTitle || item.name || '';
    const variantTitle = width ? this.formatSpecValue('width', width) : '';
    const displayName = this.getItemDisplayName({ ...item, productTitle, variantTitle }) || productTitle;
    return this.enrichFilterItem({
      ...item,
      uid: variant.variantId ? `${item.uid}-${variant.variantId}` : item.uid,
      productUid: item.productUid || item.uid,
      name: displayName,
      displayName,
      productTitle,
      variantTitle,
      selectedSize: width,
      variantId: variant.variantId ?? item.variantId,
      price: variant.price ?? item.price,
      displayPriceCents: variant.displayPriceCents ?? item.displayPriceCents,
      variantPriceCents: variant.variantPriceCents ?? item.variantPriceCents,
      texture: variant.texture || item.texture,
      specs: { ...(item.specs || {}), width },
      imageUrl: variant.imageUrl || item.imageUrl,
      fullImageUrl: variant.fullImageUrl || item.fullImageUrl,
      deckVariants: item.deckVariants || []
    }, 'deck');
  }
  resolveDeckVariantSelection(item, preferredVariantId = null, shouldPrompt = true) {
    const choices = this.getDeckSizeChoices(item);
    if (!choices.length) return !item.deckVariants?.length && this.isCatalogVariantAvailable(item.variantId) ? item : null;
    let choice = null;
    if (preferredVariantId) {
      choice = choices.find(option => String(option.variant?.variantId || '') === String(preferredVariantId));
      if (!choice) return null; // A saved exact size must never switch to another variant.
    }
    const filterWidth = this.getActiveDeckFilterWidth();
    if (!choice && filterWidth) {
      choice = choices.find(option => option.width === filterWidth);
    }
    if (!choice && (choices.length === 1 || !shouldPrompt)) {
      choice = choices[0];
    }
    if (!choice) {
      const lines = choices.map((option, index) => `${index + 1}. ${this.formatSpecValue('width', option.width)}`);
      const answer = window.prompt(`Choose deck size:\n${lines.join('\n')}`, '1');
      if (answer === null) return null;
      const normalizedAnswer = this.normalizeWidthValue(answer);
      const answerIndex = Number.parseInt(answer, 10) - 1;
      choice = Number.isInteger(answerIndex) && choices[answerIndex]
        ? choices[answerIndex]
        : choices.find(option => option.width === normalizedAnswer);
      if (!choice) {
        alert('Please choose one of the listed deck sizes.');
        return null;
      }
    }
    return this.getDeckItemForVariant(item, choice);
  }
  resolveDeckFromParam(deckParam) {
    if (!deckParam) return null;
    const exact = this.decks.find(deck => deck.uid === deckParam);
    if (exact) return this.resolveDeckVariantSelection(exact, null, false);
    const product = this.decks
      .filter(deck => String(deckParam).startsWith(`${deck.uid}-`))
      .sort((a, b) => String(b.uid).length - String(a.uid).length)[0];
    if (!product) return null;
    const preferredVariantId = String(deckParam).slice(String(product.uid).length + 1);
    return this.resolveDeckVariantSelection(product, preferredVariantId, false);
  }
  getItemFilterValues(item, field, type) {
    if (type === 'deck' && field === 'width') {
      const values = this.getDeckSizeChoices(item).map(choice => choice.width).filter(Boolean);
      return [...new Set(values)];
    }
    const value = item?.specs?.[field];
    return value ? [value] : [];
  }
  connectedCallback() {
    this.bindBoardAddLifecycle();
    this.textureLifecycleGeneration += 1;
    this.updateDesktopScale();
    this.initCatalogAvailability();
    this.initState();
    this.renderUI();
    this.initCollectionDrawers();
    this.initDrawerFilters();
    this.initTouchPressFeedback();
    this.initBuilderMenu();
    this.initSummaryControls();
    this.initPersistentScrollbars();
    this.initCartDrawer();
    this.initThree();
    this.preloadTexture(this.selectedDeck.texture);
    this.preloadTexture(this.selectedGrip.texture);
    this.preloadTexture(this.selectedWheel.texture);
    this.loadAssets();
    const addToCartBtn = this.querySelector('#bc-add-to-cart-btn');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleAddToCart();
      });
    }
    this.initAssemblyChoice();
    this.updateCatalogAvailabilityUI();
    void this.refreshCatalogAvailability({ reason: 'initial' });
    window.HalfpipedBoardIntegrity?.replayRemovals?.(this.handleBoardsRemoved);
    const resetCameraBtn = this.querySelector('#bc-reset-camera-btn');
    if (resetCameraBtn) {
      resetCameraBtn.addEventListener('click', () => {
        this.resetCamera();
        resetCameraBtn.classList.add('is-pressed');
        window.setTimeout(() => {
          resetCameraBtn.classList.remove('is-pressed');
          resetCameraBtn.blur?.();
        }, 140);
      });
    }
    const resetBuildBtn = this.querySelector('#bc-reset-build-btn');
    if (resetBuildBtn) {
      resetBuildBtn.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.hasChosenParts() && !window.confirm('Reset your board and clear all selected parts?')) return;
        this.resetBuild();
      });
    }
    this.querySelectorAll('[data-close-modal]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.closeImageModal();
      });
    });
    this.querySelectorAll('[data-image-modal-view]').forEach(button => {
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.setImageModalView(button.getAttribute('data-image-modal-view') || 'image');
      });
    });
    const runImageModalNav = nav => {
      if (nav.disabled) return;
      if (nav.matches('[data-image-modal-prev]')) {
        this.showImageModalImage(this.imageModalIndex - 1);
      } else if (nav.matches('[data-image-modal-next]')) {
        this.showImageModalImage(this.imageModalIndex + 1);
      } else {
        this.showImageModalImage(Number.parseInt(nav.getAttribute('data-image-modal-index'), 10));
      }
      nav.classList.add('is-pressed');
      window.setTimeout(() => {
        nav.classList.remove('is-pressed');
      }, 180);
    };
    this.querySelector('#bc-image-modal')?.addEventListener('click', event => {
      const nav = event.target.closest('[data-image-modal-prev], [data-image-modal-next], [data-image-modal-index]');
      if (!nav) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      runImageModalNav(nav);
    }, true);
    this.initImageModalSwipe();
    window.addEventListener('resize', this.handleWindowResize, { passive: true });
    const canvasContainer = this.querySelector('#bc-canvas-container');
    if (canvasContainer && 'ResizeObserver' in window) {
      this.canvasResizeObserver = new ResizeObserver(() => this.scheduleResize());
      this.canvasResizeObserver.observe(canvasContainer);
    }
    this.scheduleResize();
  }
  initAssemblyChoice() {
    const assemblyControl = this.querySelector('#bc-assembly');
    const buttons = Array.from(this.querySelectorAll('[data-assembly-choice-option]'));
    if (!assemblyControl || buttons.length === 0) return;
    buttons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.setAssemblyChoiceValue(button.getAttribute('data-assembly-choice-option') || '', true);
      });
    });
    assemblyControl.addEventListener('change', () => this.setAssemblyChoiceValue(assemblyControl.value || '', true));
    this.setAssemblyChoiceValue(this.initialAssemblyChoice || assemblyControl.value || '');
  }
  setAssemblyChoiceValue(value = '', shouldResetCamera = false) {
    const assemblyControl = this.querySelector('#bc-assembly');
    const buttons = Array.from(this.querySelectorAll('[data-assembly-choice-option]'));
    if (!assemblyControl) return;
    const optionValues = Array.from(assemblyControl.options).map(option => option.value);
    const nextValue = optionValues.includes(value) && (value !== 'service' || this.isCatalogVariantAvailable(assemblyControl.getAttribute('data-variant-id'))) ? value : '';
    assemblyControl.value = nextValue;
    buttons.forEach(button => {
      const isActive = Boolean(nextValue) && button.getAttribute('data-assembly-choice-option') === nextValue;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-checked', isActive ? 'true' : 'false');
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    this.querySelector('[data-assembly-choice]')?.classList.toggle('has-selection', Boolean(nextValue));
    if (nextValue) {
      this.updateUrlParam('assembly', nextValue);
    } else {
      this.removeUrlParam('assembly');
    }
    if (shouldResetCamera && nextValue) this.resetCamera();
  }
  disconnectedCallback() {
    this.suspendDrawerCatalog();
    this.suspendCatalogAvailability();
    window.removeEventListener('pagehide', this.handleCatalogPageHide);
    window.removeEventListener('pageshow', this.handleCatalogPageShow);
    document.removeEventListener('visibilitychange', this.handleCatalogVisibility);
    this.catalogLifecycleBound = false;
    this.suspendBoardAdd();
    window.removeEventListener('pagehide', this.handleBoardAddPageHide);
    window.removeEventListener('pageshow', this.handleBoardAddPageShow);
    document.removeEventListener?.('hp:boards:removed', this.handleBoardsRemoved);
    document.removeEventListener?.('hp:boards:cleanup-start', this.handleBoardCleanupStart);
    document.removeEventListener?.('hp:cart:priced', this.handleCatalogCartVerified);
    this.boardAddLifecycleBound = false;
    this.textureLifecycleGeneration += 1;
    this.imageModalSwipeCleanup?.();
    this.imageModalSwipeCleanup = null;
    this.cancelInitialDrawerImages();
    document.removeEventListener('visibilitychange', this.handleSceneVisibility);
    this.drawerTextureWarmSchedules?.forEach(cancel => cancel());
    this.drawerTextureWarmSchedules?.clear();
    window.removeEventListener('resize', this.handleWindowResize);
    document.removeEventListener('keydown', this.onCollectionDrawerKeydown);
    if (this.cartViewportListenersBound) {
      window.removeEventListener('resize', this.handleCartViewportResize);
      window.visualViewport?.removeEventListener('resize', this.handleCartViewportResize);
      window.visualViewport?.removeEventListener('scroll', this.handleCartViewportResize);
      this.cartViewportListenersBound = false;
    }
    this.canvasResizeObserver?.disconnect();
    this.canvasResizeObserver = null;
    if (this.resizeFrameId) {
      cancelAnimationFrame(this.resizeFrameId);
      this.resizeFrameId = 0;
    }
    this.persistentScrollbarCleanups.forEach(cleanup => cleanup());
    this.persistentScrollbarCleanups = [];
    this.persistentScrollbarSyncs = [];
    this.drawerImageObservers.forEach(observer => observer.disconnect());
    this.drawerImageObservers.clear();
    Object.keys(this.drawerTextureGenerations).forEach(type => this.cancelDrawerTextureWarmup(type));
    this.drawerTextureObservers.forEach(observer => observer.disconnect());
    this.drawerTextureObservers.clear();
    this.textureWarmQueue = [];
    this.textureWarmQueuedUrls.clear();
    this.cancelCameraTransition();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    while (this.scrollLockCount > 0) this.unlockPageScroll();
    this.controls?.dispose?.();
    this.controls = null;
    Object.entries(this.textureCache).forEach(([url, texture]) => {
      texture?.dispose?.();
      THREE.Cache.remove(url);
    });
    this.textureCache = {};
    this.texturePromises = {};
    this.textureUsage.clear();
    this.renderer?.domElement?.remove();
    this.renderer?.dispose?.();
    this.renderer = null;
    this.scene = null;
    this.camera = null;
  }
  bindBuilderTap(node, handler, options = {}) {
    if (!node) return;
    let pointerTap = null;
    let suppressClickUntil = 0;
    const maxTapMove = 24;
    const triggerOn = options.triggerOn === 'press' ? 'press' : 'release';
    const runHandler = (event) => {
      if (event?.cancelable) event.preventDefault();
      if (typeof event?.stopPropagation === 'function') event.stopPropagation();
      handler(event);
    };
    node.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      pointerTap = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY
      };
      try {
        node.setPointerCapture?.(event.pointerId);
      } catch (error) {
        // Click fallback covers browsers that cannot capture this pointer.
      }
      if (triggerOn === 'press') {
        suppressClickUntil = performance.now() + 650;
        runHandler(event);
      }
    }, { passive: false });
    node.addEventListener('pointerup', (event) => {
      if (!pointerTap || pointerTap.id !== event.pointerId) return;
      const deltaX = event.clientX - pointerTap.x;
      const deltaY = event.clientY - pointerTap.y;
      const moved = Math.hypot(deltaX, deltaY);
      pointerTap = null;
      try {
        node.releasePointerCapture?.(event.pointerId);
      } catch (error) {
        // Ignore stale pointer capture releases.
      }
      if (triggerOn === 'press') {
        if (event?.cancelable) event.preventDefault();
        if (typeof event?.stopPropagation === 'function') event.stopPropagation();
        return;
      }
      suppressClickUntil = performance.now() + 650;
      if (moved > maxTapMove) {
        if (event?.cancelable) event.preventDefault();
        if (typeof event?.stopPropagation === 'function') event.stopPropagation();
        return;
      }
      runHandler(event);
    }, { passive: false });
    node.addEventListener('pointercancel', (event) => {
      pointerTap = null;
      suppressClickUntil = performance.now() + 650;
      try {
        node.releasePointerCapture?.(event.pointerId);
      } catch (error) {
        // Ignore stale pointer capture releases.
      }
    });
    node.addEventListener('click', (event) => {
      if (performance.now() < suppressClickUntil) {
        if (event?.cancelable) event.preventDefault();
        if (typeof event?.stopPropagation === 'function') event.stopPropagation();
        return;
      }
      runHandler(event);
    });
  }
  initTouchPressFeedback() {
    const controls = '.bc-builder-part, .bc-product-card, .bc-menu-action, .bc-sidebar .hp-btn, .bc-summary__header, .bc-summary__reset';
    this.addEventListener('click', event => {
      if (event.detail === 0 || !window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
      const button = event.target.closest?.(controls);
      if (!button || !this.contains(button) || button.disabled) return;
      window.clearTimeout(button._bcPressTimer);
      button.classList.add('is-pressed');
      button._bcPressTimer = window.setTimeout(() => {
        button.classList.remove('is-pressed');
        button.blur?.();
      }, 140);
    }, true);
  }
  syncCartDrawerViewportHeight() {
    const height = Math.ceil(
      window.visualViewport?.height ||
      window.innerHeight ||
      document.documentElement.clientHeight ||
      0
    );
    if (height > 0) {
      this.style.setProperty('--cart-drawer-viewport-height', `${height}px`);
    }
  }
  lockPageScroll({ fixedBody = true } = {}) {
    if (this.scrollLockCount > 0) {
      this.scrollLockCount += 1;
      return;
    }
    this.scrollLockY = window.scrollY || document.documentElement.scrollTop || 0;
    this.scrollLockUsesFixedBody = fixedBody;
    const scrollbarComp = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--scrollbar-comp', `${scrollbarComp}px`);
    document.documentElement.classList.add('bc-builder-menu-lock');
    document.body.classList.add('bc-builder-menu-lock');
    document.body.classList.toggle('bc-builder-menu-lock-fixed', fixedBody);
    if (fixedBody) {
      document.body.style.setProperty('--scroll-lock-top', `-${this.scrollLockY}px`);
    } else {
      document.body.style.removeProperty('--scroll-lock-top');
    }
    this.scrollLockCount = 1;
  }
  unlockPageScroll() {
    if (this.scrollLockCount === 0) return;
    this.scrollLockCount -= 1;
    if (this.scrollLockCount > 0) return;
    const shouldRestoreFixedBodyScroll = this.scrollLockUsesFixedBody;
    document.documentElement.classList.remove('bc-builder-menu-lock');
    document.body.classList.remove('bc-builder-menu-lock');
    document.body.classList.remove('bc-builder-menu-lock-fixed');
    document.body.style.removeProperty('--scroll-lock-top');
    document.body.style.removeProperty('--scrollbar-comp');
    if (shouldRestoreFixedBodyScroll) {
      window.scrollTo(0, this.scrollLockY);
    }
    this.scrollLockUsesFixedBody = false;
  }
  initState() {
    const urlParams = new URLSearchParams(window.location.search);
    const deckParam = urlParams.get('deck');
    this.selectedDeck = this.resolveDeckFromParam(deckParam) || this.createEmptySelection('deck');
    const gripParam = urlParams.get('grip');
    this.selectedGrip = this.grips.find(g => g.uid === gripParam && this.isCatalogItemAvailable('grip', g)) || this.createEmptySelection('grip');
    const wheelParam = urlParams.get('wheel');
    this.selectedWheel = this.wheels.find(w => w.uid === wheelParam && this.isCatalogVariantAvailable(w.variantId)) || this.createEmptySelection('wheel');
    const bearingParam = urlParams.get('bearing');
    this.selectedBearing = this.bearings.find(b => b.uid === bearingParam && this.isCatalogVariantAvailable(b.variantId)) || this.createEmptySelection('bearing');
    const truckParam = urlParams.get('truck');
    this.selectedTruck = this.trucks.find(t => t.uid === truckParam && this.isCatalogVariantAvailable(t.variantId)) || this.trucks.find(t => truckParam && t.uid.startsWith(`${truckParam}-`) && this.isCatalogVariantAvailable(t.variantId)) || this.createEmptySelection('truck');
    const hardwareParam = urlParams.get('hardware') || urlParams.get('bolt');
    this.selectedHardware = this.hardware.find(h => h.uid === hardwareParam && this.isCatalogVariantAvailable(h.variantId)) || this.bolts.find(b => b.uid === hardwareParam && this.isCatalogVariantAvailable(b.variantId)) || this.createEmptySelection('hardware');
    this.selectedSkateTool = this.findProductCardSelection('skate_tool', urlParams.get('skate_tool')) || this.createEmptySelection('skate_tool');
    this.selectedAssembly = this.findProductCardSelection('assembly', urlParams.get('assembly_item')) || this.createEmptySelection('assembly');
    this.initialAssemblyChoice = urlParams.get('assembly') || '';
    for (const [param, selection] of [['deck', this.selectedDeck], ['grip', this.selectedGrip], ['wheel', this.selectedWheel], ['bearing', this.selectedBearing], ['truck', this.selectedTruck], ['hardware', this.selectedHardware], ['bolt', this.selectedHardware], ['skate_tool', this.selectedSkateTool], ['assembly_item', this.selectedAssembly]]) {
      if (urlParams.has(param) && selection?.isPlaceholder) this.removeUrlParam(param);
    }
  }
  createEmptySelection(type) {
    const labels = {
      deck: 'Choose a deck',
      grip: 'Choose grip',
      wheel: 'Choose wheels',
      bearing: 'Choose bearings',
      truck: 'Choose trucks',
      hardware: 'Choose hardware',
      skate_tool: 'Choose a skate tool',
      assembly: 'Choose assembly'
    };
    const defaults = {
      deck: { texture: this.defaultDeckUrl },
      grip: { texture: this.defaultGripUrl, color: this.defaultGripColor },
      wheel: { texture: this.defaultWheelUrl },
      bearing: { color: this.defaultBearingColor },
      truck: {
        color: this.defaultTruckColor,
        baseplateColor: this.defaultTruckColor,
        bushingTopColor: '#111111',
        bushingBottomColor: '#111111',
        washerTopColor: this.defaultTruckColor,
        washerBottomColor: this.defaultTruckColor
      },
      hardware: {
        color: this.defaultHardwareColor || this.defaultBoltColor,
        axleNutColor: '#050505',
        kingpinNutColor: '#050505'
      },
      skate_tool: {},
      assembly: {}
    };
    return {
      uid: `__empty_${type}`,
      name: labels[type],
      isPlaceholder: true,
      ...defaults[type]
    };
  }
  renderModalDescription(target, html = '', fallbackText = '') {
    if (!target) return;
    const fallback = String(fallbackText || '').trim() || 'No product description is available yet. Use the product image for the closest visual reference.';
    const rawHtml = String(html || '').trim();
    const allowedTags = new Set(['A', 'B', 'BR', 'DIV', 'EM', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'I', 'LI', 'OL', 'P', 'SPAN', 'STRONG', 'TABLE', 'TBODY', 'TD', 'TH', 'THEAD', 'TR', 'U', 'UL']);
    const appendFallback = () => {
      const paragraph = document.createElement('p');
      paragraph.textContent = fallback;
      target.replaceChildren(paragraph);
    };
    if (!rawHtml) {
      appendFallback();
      return;
    }
    const sanitizeNode = node => {
      if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent || '');
      if (node.nodeType !== Node.ELEMENT_NODE) return document.createDocumentFragment();
      const tagName = node.tagName.toUpperCase();
      const children = Array.from(node.childNodes).map(sanitizeNode);
      if (!allowedTags.has(tagName)) {
        const fragment = document.createDocumentFragment();
        children.forEach(child => fragment.appendChild(child));
        return fragment;
      }
      const cleanElement = document.createElement(tagName.toLowerCase());
      if (tagName === 'A') {
        const href = node.getAttribute('href') || '';
        if (/^(https?:|mailto:|tel:|\/)/i.test(href)) {
          cleanElement.setAttribute('href', href);
          cleanElement.setAttribute('target', '_blank');
          cleanElement.setAttribute('rel', 'noopener noreferrer');
        }
      } else if (['TD', 'TH'].includes(tagName)) {
        ['colspan', 'rowspan'].forEach(attr => {
          const value = node.getAttribute(attr);
          if (value && /^\d{1,2}$/.test(value)) cleanElement.setAttribute(attr, value);
        });
      }
      children.forEach(child => cleanElement.appendChild(child));
      return cleanElement;
    };
    const template = document.createElement('template');
    template.innerHTML = rawHtml;
    const fragment = document.createDocumentFragment();
    Array.from(template.content.childNodes).map(sanitizeNode).forEach(node => fragment.appendChild(node));
    if (!fragment.textContent.trim()) {
      appendFallback();
      return;
    }
    target.replaceChildren(fragment);
  }
  parseImageList(value) {
    if (!value || value === 'null') return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }
  normalizeDrawerImageSrc(src) {
    const value = String(src || '').trim();
    if (!value || value === 'null') return '';
    try {
      return new URL(value, document.baseURI).href;
    } catch (_error) {
      return value;
    }
  }
  setDrawerImageSource(img, src) {
    const nextSrc = this.normalizeDrawerImageSrc(src);
    if (nextSrc && this.normalizeDrawerImageSrc(img.getAttribute('src') || img.src) !== nextSrc) {
      img.src = nextSrc;
    }
  }
  normalizeModalImages(src, previewSrc = '', images = [], alt = 'Part Image') {
    const gallery = [];
    const seen = new Set();
    const entries = [
      { src, previewSrc, alt },
      ...this.parseImageList(images)
    ];
    entries.forEach(entry => {
      const item = typeof entry === 'string' ? { src: entry } : (entry || {});
      const fullSrc = this.normalizeDrawerImageSrc(item.src || item.fullImageUrl || item.url);
      if (!fullSrc || fullSrc === 'null' || seen.has(fullSrc)) return;
      seen.add(fullSrc);
      gallery.push({
        src: fullSrc,
        previewSrc: this.normalizeDrawerImageSrc(item.previewSrc || item.imageUrl || item.thumbnail || previewSrc || fullSrc),
        alt: String(item.alt || alt || 'Product image').trim()
      });
    });
    return gallery;
  }
  renderImageModalGalleryControls() {
    const modal = this.querySelector('#bc-image-modal');
    const prevButton = this.querySelector('[data-image-modal-prev]');
    const nextButton = this.querySelector('[data-image-modal-next]');
    const dots = this.querySelector('[data-image-modal-dots]');
    const hasMultipleImages = this.imageModalImages.length > 1;
    modal?.classList.toggle('has-gallery', hasMultipleImages);
    [prevButton, nextButton].forEach(button => {
      if (!button) return;
      button.hidden = !hasMultipleImages;
    });
    if (prevButton) prevButton.disabled = !hasMultipleImages || this.imageModalIndex <= 0;
    if (nextButton) nextButton.disabled = !hasMultipleImages || this.imageModalIndex >= this.imageModalImages.length - 1;
    if (!dots) return;
    dots.hidden = !hasMultipleImages;
    // Retain the buttons while browsing so the active pill can animate and
    // keyboard focus stays on the control that selected the image.
    if (dots.children.length !== this.imageModalImages.length) {
      dots.replaceChildren(...this.imageModalImages.map((_image, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bc-image-modal__dot';
        dot.setAttribute('data-image-modal-index', String(index));
        dot.setAttribute('aria-label', `Show product image ${index + 1}`);
        return dot;
      }));
    }
    Array.from(dots.children).forEach((dot, index) => {
      const isActive = index === this.imageModalIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
  renderImageModalGallery() {
    const gallery = this.querySelector('[data-image-modal-gallery]');
    const firstImage = this.querySelector('#bc-image-modal-img');
    if (!gallery || !firstImage) return;
    this.resetImageModalGalleryNavigation();
    const sources = this.imageModalImages.map(image => image.src);
    const reuseSlides = gallery.children.length === sources.length &&
      this.imageModalGallerySources?.length === sources.length &&
      sources.every((src, index) => src === this.imageModalGallerySources[index]);
    if (reuseSlides) {
      this.imageModalImages.forEach((image, index) => {
        gallery.children[index].querySelector('img').alt = image.alt || 'Product image';
      });
      gallery.scrollTo({ left: 0, behavior: 'auto' });
      return;
    }
    const slides = this.imageModalImages.map((image, index) => {
      const slide = document.createElement('div');
      slide.className = 'bc-image-modal__slide';
      const img = index === 0 ? firstImage : firstImage.cloneNode(false);
      if (index !== 0) img.removeAttribute('id');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.alt = image.alt || 'Product image';
      img.draggable = false;
      img.loading = 'eager';
      // Decode upgrades off-screen; keep the visible image in the same paint.
      img.decoding = 'sync';
      this.setDrawerImageSource(img, this.loadedDrawerImages.has(image.src) ? image.src : image.previewSrc || image.src);
      slide.appendChild(img);
      return slide;
    });
    gallery.replaceChildren(...slides);
    this.imageModalGallerySources = sources;
    gallery.scrollTo({ left: 0, behavior: 'auto' });
  }
  showImageModalImage(index, options = {}) {
    const modal = this.querySelector('#bc-image-modal');
    if (!modal || !this.imageModalImages.length) return;
    const total = this.imageModalImages.length;
    this.imageModalIndex = Math.max(0, Math.min(total - 1, index));
    const gallery = this.querySelector('[data-image-modal-gallery]');
    const modalImg = gallery?.children[this.imageModalIndex]?.querySelector('img') || this.querySelector('#bc-image-modal-img');
    if (!modalImg) return;
    const image = this.imageModalImages[this.imageModalIndex];
    const requestId = ++this.imageModalRequestId;
    modalImg.alt = image.alt || 'Product image';
    this.renderImageModalGalleryControls();
    if (options.scroll !== false) this.scrollImageModalGallery(this.imageModalIndex, options);
    const fullImageIsVisible = modalImg.complete && modalImg.naturalWidth > 0 &&
      this.normalizeDrawerImageSrc(modalImg.currentSrc || modalImg.src) === image.src;
    if (this.loadedDrawerImages.has(image.src) || fullImageIsVisible) {
      this.setDrawerImageSource(modalImg, image.src);
      modal.classList.remove('is-loading');
      this.preloadImageModalNeighbors();
      return;
    }
    if (image.previewSrc && image.previewSrc !== 'null') {
      this.setDrawerImageSource(modalImg, image.previewSrc);
      modal.classList.remove('is-loading');
    } else {
      modalImg.removeAttribute('src');
      modal.classList.add('is-loading');
    }
    this.preloadDrawerImage(image.src).then(loadedSrc => {
      if (requestId !== this.imageModalRequestId) return;
      // A failed load/decode must not replace a working preview.
      if (loadedSrc) this.setDrawerImageSource(modalImg, loadedSrc);
      modal.classList.remove('is-loading');
      this.preloadImageModalNeighbors();
    }).catch(() => {
      if (requestId !== this.imageModalRequestId) return;
      modal.classList.remove('is-loading');
      this.preloadImageModalNeighbors();
    });
    this.preloadImageModalNeighbors();
  }
  preloadImageModalNeighbors() {
    const total = this.imageModalImages.length;
    if (total < 2) return;
    [this.imageModalIndex - 1, this.imageModalIndex + 1].forEach(index => {
      const image = this.imageModalImages[((index % total) + total) % total];
      if (image?.previewSrc && image.previewSrc !== 'null') this.preloadDrawerImage(image.previewSrc);
      if (image?.src && image.src !== 'null') this.preloadDrawerImage(image.src);
    });
  }
  preloadImageModalGallery() {
    this.imageModalImages.forEach(image => {
      if (image?.previewSrc && image.previewSrc !== 'null') this.preloadDrawerImage(image.previewSrc);
      if (image?.src && image.src !== 'null') this.preloadDrawerImage(image.src);
    });
  }
  resetImageModalGalleryNavigation() {
    this.imageModalScrollTarget = null;
    window.clearTimeout(this.imageModalScrollTimer);
    this.imageModalScrollTimer = 0;
    const gallery = this.querySelector('[data-image-modal-gallery]');
    const swipe = this.imageModalSwipe;
    this.imageModalSwipe = null;
    if (!gallery) return;
    gallery.style.scrollSnapType = '';
    gallery.classList.remove('is-dragging');
    if (swipe && gallery.hasPointerCapture?.(swipe.pointerId)) gallery.releasePointerCapture(swipe.pointerId);
  }
  scrollImageModalGallery(index, options = {}) {
    const gallery = this.querySelector('[data-image-modal-gallery]');
    if (!gallery || !gallery.clientWidth) return;
    window.clearTimeout(this.imageModalScrollTimer);
    const left = index * gallery.clientWidth;
    const instant = options.instant === true || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.imageModalScrollTarget = instant || Math.abs(gallery.scrollLeft - left) < 1 ? null : index;
    gallery.scrollTo({ left, behavior: this.imageModalScrollTarget === null ? 'auto' : 'smooth' });
  }
  syncImageModalGallery(settled = false) {
    const modal = this.querySelector('#bc-image-modal');
    const gallery = this.querySelector('[data-image-modal-gallery]');
    if (!modal?.classList.contains('is-open') || !gallery?.clientWidth || !this.imageModalImages.length || this.imageModalSwipe) return;
    // Arrow/dot navigation may cross several slides. Keep its destination
    // selected until it settles; native swipes update as each slide passes.
    if (this.imageModalScrollTarget !== null && this.imageModalScrollTarget !== undefined) {
      if (!settled) return;
      this.imageModalScrollTarget = null;
    }
    const index = Math.max(0, Math.min(this.imageModalImages.length - 1, Math.round(gallery.scrollLeft / gallery.clientWidth)));
    if (index !== this.imageModalIndex) this.showImageModalImage(index, { scroll: false });
  }
  initImageModalSwipe() {
    this.imageModalSwipeCleanup?.();
    const gallery = this.querySelector('[data-image-modal-gallery]');
    if (!gallery) return;
    const onScroll = () => {
      this.syncImageModalGallery();
      window.clearTimeout(this.imageModalScrollTimer);
      this.imageModalScrollTimer = window.setTimeout(() => this.syncImageModalGallery(true), 160);
    };
    const onScrollEnd = () => {
      window.clearTimeout(this.imageModalScrollTimer);
      this.syncImageModalGallery(true);
    };
    const interruptNavigation = () => {
      if (this.imageModalScrollTarget !== null && this.imageModalScrollTarget !== undefined) {
        gallery.scrollTo({ left: gallery.scrollLeft, behavior: 'auto' });
      }
      this.imageModalScrollTarget = null;
      this.syncImageModalGallery(true);
    };
    const onPointerDown = event => {
      if (event.isPrimary === false) return;
      interruptNavigation();
      // Touch, pen and trackpads use the browser's native scrolling and snap.
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.imageModalImages.length < 2) return;
      this.imageModalSwipe = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        startScrollLeft: gallery.scrollLeft,
        startIndex: this.imageModalIndex,
        swiping: false
      };
      gallery.setPointerCapture?.(event.pointerId);
    };
    const onPointerMove = event => {
      const swipe = this.imageModalSwipe;
      if (!swipe || swipe.pointerId !== event.pointerId) return;
      swipe.lastX = event.clientX;
      const deltaX = swipe.lastX - swipe.startX;
      const deltaY = event.clientY - swipe.startY;
      if (!swipe.swiping && Math.abs(deltaX) > 6 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
        swipe.swiping = true;
        gallery.style.scrollSnapType = 'none';
        gallery.classList.add('is-dragging');
      }
      if (!swipe.swiping) return;
      event.preventDefault();
      gallery.scrollLeft = swipe.startScrollLeft - deltaX;
    };
    const finishSwipe = (event, cancelled = false) => {
      const swipe = this.imageModalSwipe;
      if (!swipe || swipe.pointerId !== event.pointerId) return;
      const deltaX = swipe.lastX - swipe.startX;
      this.resetImageModalGalleryNavigation();
      if (!swipe.swiping) return;
      const moved = Math.abs(deltaX) >= Math.min(48, gallery.clientWidth * .15);
      const nextIndex = swipe.startIndex + (!cancelled && moved ? (deltaX < 0 ? 1 : -1) : 0);
      this.showImageModalImage(Math.max(0, Math.min(this.imageModalImages.length - 1, nextIndex)), { instant: cancelled });
    };
    const onPointerUp = event => finishSwipe(event);
    const onPointerCancel = event => finishSwipe(event, true);
    const preventImageDrag = event => event.preventDefault();
    const onResize = () => {
      if (this.querySelector('#bc-image-modal')?.classList.contains('is-open') && !this.imageModalSwipe) {
        this.scrollImageModalGallery(this.imageModalIndex, { instant: true });
      }
    };
    gallery.addEventListener('scroll', onScroll, { passive: true });
    gallery.addEventListener('scrollend', onScrollEnd);
    gallery.addEventListener('wheel', interruptNavigation, { passive: true });
    gallery.addEventListener('pointerdown', onPointerDown);
    gallery.addEventListener('pointermove', onPointerMove, { passive: false });
    gallery.addEventListener('pointerup', onPointerUp);
    gallery.addEventListener('pointercancel', onPointerCancel);
    gallery.addEventListener('dragstart', preventImageDrag);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(onResize) : null;
    observer?.observe(gallery);
    if (!observer) window.addEventListener('resize', onResize, { passive: true });
    this.imageModalSwipeCleanup = () => {
      this.resetImageModalGalleryNavigation();
      observer?.disconnect();
      if (!observer) window.removeEventListener('resize', onResize);
      gallery.removeEventListener('scroll', onScroll);
      gallery.removeEventListener('scrollend', onScrollEnd);
      gallery.removeEventListener('wheel', interruptNavigation);
      gallery.removeEventListener('pointerdown', onPointerDown);
      gallery.removeEventListener('pointermove', onPointerMove);
      gallery.removeEventListener('pointerup', onPointerUp);
      gallery.removeEventListener('pointercancel', onPointerCancel);
      gallery.removeEventListener('dragstart', preventImageDrag);
    };
  }
  openImageModal(src, alt = 'Part Image', previewSrc = '', description = '', descriptionHtml = '', images = []) {
    if (!src || src === 'null') return;
    this.clearHoveredProductCard();
    const modal = this.querySelector('#bc-image-modal');
    const modalImg = this.querySelector('#bc-image-modal-img');
    const modalTitle = this.querySelector('#bc-image-modal-title');
    const modalDescription = this.querySelector('#bc-image-modal-description');
    const modalViewport = this.querySelector('[data-image-modal-viewport]');
    if (!modal || !modalImg) return;
    this.imageModalLastFocusedElement = document.activeElement;
    this.imageModalBackgroundDrawer = this.getOpenCollectionDrawer();
    const modalName = alt || 'Part Image';
    if (modalTitle) modalTitle.textContent = modalName;
    this.renderModalDescription(modalDescription, descriptionHtml, description);
    this.imageModalImages = this.normalizeModalImages(src, previewSrc, images, modalName);
    this.imageModalIndex = 0;
    this.renderImageModalGallery();
    this.setImageModalView('image');
    if (modalViewport) {
      modalViewport.scrollTop = 0;
      modalViewport.scrollLeft = 0;
    }
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    modal.removeAttribute('inert');
    this.showImageModalImage(0);
    modal.querySelector('.bc-image-modal__close[data-close-modal]')?.focus({ preventScroll: true });
    if (this.imageModalBackgroundDrawer) {
      this.imageModalBackgroundDrawer.setAttribute('aria-hidden', 'true');
      this.imageModalBackgroundDrawer.setAttribute('inert', '');
    }
    window.requestAnimationFrame(() => this.syncPersistentScrollbars());
    this.preloadImageModalGallery();
  }
  preloadDrawerImage(src) {
    src = this.normalizeDrawerImageSrc(src);
    if (!src) return Promise.resolve(null);
    if (this.loadedDrawerImages.has(src)) return Promise.resolve(src);
    if (this.imagePreloadPromises.has(src)) return this.imagePreloadPromises.get(src);
    const preloadPromise = new Promise(resolve => {
      const image = new Image();
      let settled = false;
      const finish = loaded => {
        if (settled) return;
        settled = true;
        if (loaded) this.loadedDrawerImages.add(src);
        resolve(loaded ? src : null);
      };
      image.decoding = 'async';
      image.onload = () => {
        if (typeof image.decode !== 'function') {
          finish(true);
          return;
        }
        image.decode().then(() => finish(true), () => finish(false));
      };
      image.onerror = () => finish(false);
      image.src = src;
    });
    this.imagePreloadPromises.set(src, preloadPromise);
    preloadPromise.then(() => this.imagePreloadPromises.delete(src));
    return preloadPromise;
  }
  primeProductCardImage(card, priority = 'auto', allowClosed = false) {
    const drawer = card?.closest?.('.bc-drawer');
    const drawerCanLoad = drawer?.classList.contains('is-open') || drawer?.classList.contains('is-opening') || allowClosed;
    if (!drawerCanLoad || card.classList.contains('is-filtered-out')) return;
    const img = card?.querySelector?.('.bc-product-card__image-wrapper img');
    if (!img) return;
    const imageWrapper = img.closest('.bc-product-card__image-wrapper');
    const deferredSrc = img.getAttribute('data-bc-src');
    const deferredSrcset = img.getAttribute('data-bc-srcset');
    const src = img.currentSrc || img.getAttribute('src') || deferredSrc;
    if (!src || src === 'null') return;
    // The observer already limits us to nearby cards. Native lazy loading would
    // defer these requests a second time and defeat the drawer's lookahead.
    img.loading = 'eager';
    img.decoding = 'async';
    if ('fetchPriority' in img && (priority === 'high' || img.fetchPriority !== 'high')) {
      img.fetchPriority = priority;
    }
    if (img.dataset.bcPreviewPrimed !== 'true') {
      img.dataset.bcPreviewPrimed = 'true';
      imageWrapper?.classList.add('is-image-loading');
      const revealImage = () => {
        const loadedSrc = img.currentSrc || img.src || src;
        if (loadedSrc) this.loadedDrawerImages.add(this.normalizeDrawerImageSrc(loadedSrc));
        imageWrapper?.classList.remove('is-image-loading');
        imageWrapper?.classList.add('is-image-ready');
      };
      img.addEventListener('load', revealImage, { once: true });
      img.addEventListener('error', () => {
        imageWrapper?.classList.remove('is-image-loading');
        imageWrapper?.classList.add('is-image-unavailable');
      }, { once: true });
    }
    // Set hints and responsive candidates before the fallback URL so one image,
    // at the appropriate size, is requested. Closed drawers retain deferred URLs.
    if (deferredSrcset) {
      img.setAttribute('srcset', deferredSrcset);
      img.removeAttribute('data-bc-srcset');
    }
    if (deferredSrc) {
      img.setAttribute('src', deferredSrc);
      img.removeAttribute('data-bc-src');
    }
    if (img.complete && img.naturalWidth > 0) {
      this.loadedDrawerImages.add(this.normalizeDrawerImageSrc(img.currentSrc || img.src || src));
      imageWrapper?.classList.remove('is-image-loading');
      imageWrapper?.classList.add('is-image-ready');
    }
    return img;
  }
  getPrimeableDrawerCards(type, allowClosed = false) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const drawerCanLoad = drawer?.classList.contains('is-open') || drawer?.classList.contains('is-opening') || allowClosed;
    if (!drawerCanLoad) return [];
    return Array.from(drawer.querySelectorAll(`.bc-product-card[data-type="${type}"]`))
      .filter(card => !card.classList.contains('is-filtered-out'));
  }
  prewarmDrawerFirstImages(type) {
    // A customer's chosen drawer takes priority over background deck pictures.
    this.cancelInitialDrawerImages();
    this.getPrimeableDrawerCards(type, true).slice(0, 6).forEach((card, index) => {
      this.primeProductCardImage(card, index < 2 ? 'high' : 'auto', true);
    });
  }
  scheduleInitialDrawerImages() {
    this.cancelInitialDrawerImages();
    if (!this.isConnected || this.querySelector('.bc-drawer.is-open, .bc-drawer.is-opening')) return;
    // Called after the board is visible. Only the first deck row is speculative;
    // prepare one responsive image per idle task, with a bounded wait between them.
    const cards = this.getPrimeableDrawerCards('deck', true).slice(0, 4);
    if (!cards.length) return;
    const generation = this.initialDrawerImageWarmGeneration;
    this.initialDrawerImageWarmInteraction = () => this.cancelInitialDrawerImages();
    this.addEventListener('pointerdown', this.initialDrawerImageWarmInteraction, { passive: true, capture: true });
    this.addEventListener('keydown', this.initialDrawerImageWarmInteraction, true);
    const scheduleNext = () => {
      if (generation !== this.initialDrawerImageWarmGeneration) return;
      if (!this.isConnected || !cards.length || this.querySelector('.bc-drawer.is-open, .bc-drawer.is-opening')) {
        this.cancelInitialDrawerImages();
        return;
      }
      const warmNext = () => {
        this.initialDrawerImageWarmHandle = null;
        if (generation !== this.initialDrawerImageWarmGeneration) return;
        if (!this.isConnected || this.querySelector('.bc-drawer.is-open, .bc-drawer.is-opening')) {
          this.cancelInitialDrawerImages();
          return;
        }
        const img = this.primeProductCardImage(cards.shift(), 'low', true);
        this.decodeInitialDrawerImage(img).then(scheduleNext);
      };
      if (typeof window.requestIdleCallback === 'function') {
        this.initialDrawerImageWarmKind = 'idle';
        this.initialDrawerImageWarmHandle = window.requestIdleCallback(warmNext, { timeout: 1200 });
      } else {
        this.initialDrawerImageWarmKind = 'timer';
        this.initialDrawerImageWarmHandle = window.setTimeout(warmNext, 160);
      }
    };
    scheduleNext();
  }
  decodeInitialDrawerImage(img) {
    if (!img) return Promise.resolve(null);
    return new Promise(resolve => {
      let settled = false;
      const finish = result => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        img.removeEventListener('load', loaded);
        img.removeEventListener('error', cancel);
        if (this.initialDrawerImageDecodeCancel === cancel) this.initialDrawerImageDecodeCancel = null;
        resolve(result);
      };
      const loaded = () => finish(img);
      const cancel = () => finish(null);
      const timeout = window.setTimeout(cancel, 1500);
      this.initialDrawerImageDecodeCancel = cancel;
      img.addEventListener('error', cancel, { once: true });
      // Decode the actual responsive candidate, never a separate full-size Image.
      // This is preparation only: the normal load/error handlers reveal the card
      // independently, including if decode rejects or takes too long.
      if (typeof img.decode === 'function') {
        try {
          Promise.resolve(img.decode()).then(loaded, cancel);
        } catch (error) {
          cancel();
        }
      } else if (img.complete && img.naturalWidth > 0) {
        loaded();
      } else {
        img.addEventListener('load', loaded, { once: true });
      }
    });
  }
  cancelInitialDrawerImages() {
    this.initialDrawerImageWarmGeneration = (this.initialDrawerImageWarmGeneration || 0) + 1;
    if (this.initialDrawerImageWarmHandle != null) {
      if (this.initialDrawerImageWarmKind === 'idle') {
        window.cancelIdleCallback?.(this.initialDrawerImageWarmHandle);
      } else {
        window.clearTimeout(this.initialDrawerImageWarmHandle);
      }
      this.initialDrawerImageWarmHandle = null;
    }
    this.initialDrawerImageDecodeCancel?.();
    if (this.initialDrawerImageWarmInteraction) {
      this.removeEventListener('pointerdown', this.initialDrawerImageWarmInteraction, true);
      this.removeEventListener('keydown', this.initialDrawerImageWarmInteraction, true);
      this.initialDrawerImageWarmInteraction = null;
    }
  }
  primeDrawerPreviewImages(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const body = drawer?.querySelector('.bc-drawer__body');
    if (!drawer || !body || !drawer.classList.contains('is-open')) return;
    const cards = this.getPrimeableDrawerCards(type);
    if (!cards.length) return;
    const rootRect = body.getBoundingClientRect();
    const lead = Math.max((body.clientHeight || window.innerHeight || 720) * 0.75, 420);
    let primed = 0;
    cards.forEach(card => {
      const rect = card.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      if (rect.bottom < rootRect.top - lead || rect.top > rootRect.bottom + lead) return;
      this.primeProductCardImage(card, primed < 2 ? 'high' : 'auto');
      primed += 1;
    });
    if (primed > 0) return;
    cards.slice(0, 6).forEach((card, index) => {
      this.primeProductCardImage(card, index < 2 ? 'high' : 'auto');
    });
  }
  primeDrawerImages(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const body = drawer?.querySelector('.bc-drawer__body');
    if (!drawer || !body || (!drawer.classList.contains('is-open') && !drawer.classList.contains('is-opening'))) return;
    const existingObserver = this.drawerImageObservers.get(type);
    if (existingObserver) existingObserver.disconnect();
    this.drawerImageObservers.delete(type);
    requestAnimationFrame(() => this.primeDrawerPreviewImages(type));
    if (!('IntersectionObserver' in window)) {
      let frameId = null;
      const onScroll = () => {
        if (frameId !== null) return;
        frameId = requestAnimationFrame(() => {
          frameId = null;
          this.primeDrawerPreviewImages(type);
        });
      };
      body.addEventListener('scroll', onScroll, { passive: true });
      this.drawerImageObservers.set(type, {
        disconnect: () => {
          body.removeEventListener('scroll', onScroll);
          if (frameId !== null) cancelAnimationFrame(frameId);
        }
      });
      return;
    }
    const lead = Math.round(Math.max((body.clientHeight || window.innerHeight || 720) * 0.9, 520));
    const observer = new IntersectionObserver(entries => {
      if (!drawer.classList.contains('is-open')) return;
      entries.forEach(entry => {
        if (!entry.isIntersecting && entry.intersectionRatio <= 0) return;
        if (entry.target.classList.contains('is-filtered-out')) return;
        this.primeProductCardImage(entry.target);
        observer.unobserve(entry.target);
      });
    }, { root: body, rootMargin: `${lead}px 0px`, threshold: 0 });
    this.getPrimeableDrawerCards(type).forEach(card => observer.observe(card));
    this.drawerImageObservers.set(type, observer);
  }
  closeImageModal() {
    const modal = this.querySelector('#bc-image-modal');
    if (!modal) return;
    this.imageModalRequestId += 1;
    this.resetImageModalGalleryNavigation();
    if (this.imageModalBackgroundDrawer?.classList.contains('is-open')) {
      this.imageModalBackgroundDrawer.setAttribute('aria-hidden', 'false');
      this.imageModalBackgroundDrawer.removeAttribute('inert');
    }
    if (modal.contains(document.activeElement)) {
      let fallback = this.imageModalLastFocusedElement && !modal.contains(this.imageModalLastFocusedElement)
        ? this.imageModalLastFocusedElement
        : this.querySelector('#bc-summary-toggle');
      if (fallback?.closest?.('.bc-product-card')) {
        fallback = this.querySelector('.bc-drawer.is-open .bc-drawer__close') || this.querySelector('#bc-summary-toggle');
      }
      fallback?.focus?.({ preventScroll: true });
    }
    modal.classList.remove('is-open');
    modal.classList.remove('is-loading');
    modal.classList.remove('has-gallery');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');
    this.imageModalLastFocusedElement = null;
    this.imageModalBackgroundDrawer = null;
    this.imageModalImages = [];
    this.imageModalIndex = 0;
    this.imageModalSwipe = null;
    this.clearHoveredProductCard();
  }
  setImageModalView(view = 'image') {
    const modal = this.querySelector('#bc-image-modal');
    if (!modal) return;
    const activeView = view === 'details' ? 'details' : 'image';
    modal.setAttribute('data-mobile-view', activeView);
    this.querySelectorAll('[data-image-modal-view]').forEach(button => {
      const isActive = button.getAttribute('data-image-modal-view') === activeView;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    const modalViewport = this.querySelector('[data-image-modal-viewport]');
    if (modalViewport) {
      modalViewport.scrollTop = 0;
      modalViewport.scrollLeft = 0;
    }
    window.requestAnimationFrame(() => {
      if (activeView === 'image') this.scrollImageModalGallery(this.imageModalIndex, { instant: true });
      this.syncPersistentScrollbars();
    });
  }
  syncPersistentScrollbars() {
    this.persistentScrollbarSyncs.forEach(sync => sync());
  }
  initPersistentScrollbars() {
    this.persistentScrollbarCleanups.forEach(cleanup => cleanup());
    this.persistentScrollbarCleanups = [];
    this.persistentScrollbarSyncs = [];

    const scrollbars = Array.from(this.querySelectorAll('[data-bc-persistent-scroll-shell]'), shell => {
      const area = shell.querySelector('[data-bc-persistent-scroll-area]');
      const track = shell.querySelector('[data-bc-persistent-scrollbar]');
      const thumb = track?.querySelector('[data-bc-persistent-scroll-thumb]');
      return { shell, area, track, thumb };
    });

    // Keep the native product scroller and its desktop gutter. Overlay-scrollbar
    // platforms get a persistent visual rail without changing the fixed toolbar.
    this.querySelectorAll('.bc-drawer[data-catalog-profile="revamp"] .bc-drawer__content').forEach(shell => {
      const area = shell.querySelector('.bc-drawer__body');
      if (!area) return;
      let track = shell.querySelector('[data-bc-drawer-scrollbar]');
      if (!track) {
        track = document.createElement('span');
        track.className = 'bc-persistent-scrollbar bc-drawer-scrollbar';
        track.setAttribute('data-bc-drawer-scrollbar', '');
        track.setAttribute('aria-hidden', 'true');
        const thumb = document.createElement('span');
        thumb.className = 'bc-persistent-scrollbar__thumb';
        track.appendChild(thumb);
        shell.appendChild(track);
      }
      scrollbars.push({ shell, area, track, thumb: track.firstElementChild, isDrawer: true });
    });

    scrollbars.forEach(({ shell, area, track, thumb, isDrawer = false }) => {
      if (!area || !track || !thumb) return;

      let drag = null;
      const sync = () => {
        const viewportHeight = area.clientHeight;
        const contentHeight = area.scrollHeight;
        if (viewportHeight <= 0 || contentHeight <= 0) {
          shell.classList.toggle('has-scroll-overflow', false);
          if (isDrawer) shell.classList.toggle('has-drawer-scrollbar-fallback', false);
          return;
        }

        const scrollRange = Math.max(0, contentHeight - viewportHeight);
        const hasOverflow = scrollRange > 1;
        shell.classList.toggle('has-scroll-overflow', hasOverflow);
        if (isDrawer) {
          const style = window.getComputedStyle(area);
          const borders = (Number.parseFloat(style.borderLeftWidth) || 0) + (Number.parseFloat(style.borderRightWidth) || 0);
          const nativeGutter = Math.max(0, area.offsetWidth - area.clientWidth - borders);
          // Once active, keep the custom rail for this open overflowing area.
          // Hiding its native overlay must not change which rail is selected.
          const needsFallback = hasOverflow && (shell.classList.contains('has-drawer-scrollbar-fallback') || nativeGutter < 1);
          shell.classList.toggle('has-drawer-scrollbar-fallback', needsFallback);
          if (!needsFallback) return;
          // The rail spans the complete scrolling viewport, below the fixed
          // toolbar. Content padding must not create gaps at either end.
          track.style.top = `${area.offsetTop}px`;
          track.style.height = `${viewportHeight}px`;
        }
        if (!hasOverflow) {
          shell.style.setProperty('--bc-persistent-thumb-size', '100%');
          shell.style.setProperty('--bc-persistent-thumb-offset', '0px');
          return;
        }

        const trackHeight = track.clientHeight;
        if (trackHeight <= 0) return;
        const pixelRatio = window.devicePixelRatio || 1;
        const snapToPixel = value => Math.round(value * pixelRatio) / pixelRatio;
        const minimumThumbHeight = trackHeight * 0.08;
        const thumbHeight = Math.min(trackHeight, Math.max(1 / pixelRatio, snapToPixel(Math.max(minimumThumbHeight, trackHeight * (viewportHeight / contentHeight)))));
        const thumbRange = Math.max(0, trackHeight - thumbHeight);
        const thumbOffset = Math.min(thumbRange, Math.max(0, snapToPixel((area.scrollTop / scrollRange) * thumbRange)));

        shell.style.setProperty('--bc-persistent-thumb-size', `${thumbHeight}px`);
        shell.style.setProperty('--bc-persistent-thumb-offset', `${thumbOffset}px`);
      };

      // Custom rails remain usable with a mouse or touch pointer. The body is
      // still the scroll container, so wheel, keyboard and image observers keep
      // their native behavior. Classic drawer scrollbars retain native dragging.
      const onPointerDown = event => {
        if (event.button !== 0 || event.isPrimary === false) return;
        if (isDrawer && !shell.classList.contains('has-drawer-scrollbar-fallback')) return;
        sync();
        const scrollRange = Math.max(0, area.scrollHeight - area.clientHeight);
        const thumbHeight = Number.parseFloat(shell.style.getPropertyValue('--bc-persistent-thumb-size'));
        const thumbRange = track.clientHeight - thumbHeight;
        if (scrollRange <= 1 || thumbRange <= 0 || !Number.isFinite(thumbRange)) return;
        event.preventDefault();
        if (event.target !== thumb) {
          const offset = event.clientY - track.getBoundingClientRect().top - thumbHeight / 2;
          area.scrollTop = Math.min(scrollRange, Math.max(0, offset / thumbRange * scrollRange));
          sync();
        }
        drag = { pointerId: event.pointerId, startY: event.clientY, startScroll: area.scrollTop, ratio: scrollRange / thumbRange };
        track.classList.toggle('is-dragging', true);
        track.setPointerCapture(event.pointerId);
      };
      const onPointerMove = event => {
        if (!drag || event.pointerId !== drag.pointerId) return;
        const scrollRange = Math.max(0, area.scrollHeight - area.clientHeight);
        area.scrollTop = Math.min(scrollRange, Math.max(0, drag.startScroll + (event.clientY - drag.startY) * drag.ratio));
        sync();
      };
      const endDrag = event => {
        if (!drag || (event && event.pointerId !== drag.pointerId)) return;
        const pointerId = drag.pointerId;
        drag = null;
        track.classList.toggle('is-dragging', false);
        if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
      };

      area.addEventListener('scroll', sync, { passive: true });
      track.addEventListener('pointerdown', onPointerDown);
      track.addEventListener('pointermove', onPointerMove);
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('lostpointercapture', endDrag);
      const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(sync) : null;
      resizeObserver?.observe(area);
      resizeObserver?.observe(track);
      const mutationObserver = typeof MutationObserver === 'function'
        ? new MutationObserver(() => window.requestAnimationFrame(sync))
        : null;
      mutationObserver?.observe(area, { childList: true, subtree: true, characterData: true, attributes: true });

      this.persistentScrollbarSyncs.push(sync);
      this.persistentScrollbarCleanups.push(() => {
        endDrag();
        area.removeEventListener('scroll', sync);
        track.removeEventListener('pointerdown', onPointerDown);
        track.removeEventListener('pointermove', onPointerMove);
        track.removeEventListener('pointerup', endDrag);
        track.removeEventListener('pointercancel', endDrag);
        track.removeEventListener('lostpointercapture', endDrag);
        resizeObserver?.disconnect();
        mutationObserver?.disconnect();
      });
    });

    window.requestAnimationFrame(() => this.syncPersistentScrollbars());
  }
  setHoveredProductCard(card) {
    if (this.hoveredProductCard === card) return;
    if (this.hoveredProductCard) this.hoveredProductCard.classList.remove('is-hovering');
    this.hoveredProductCard = card && this.contains(card) ? card : null;
    if (this.hoveredProductCard) this.hoveredProductCard.classList.add('is-hovering');
  }
  clearHoveredProductCard() {
    this.hoveredProductCard = null;
    this.querySelectorAll('.bc-product-card.is-hovering').forEach(card => card.classList.remove('is-hovering'));
  }
  hasChosenParts() {
    return [this.selectedDeck, this.selectedGrip, this.selectedWheel, this.selectedBearing, this.selectedTruck, this.selectedHardware, this.selectedSkateTool, this.selectedAssembly]
      .some(part => part && !part.isPlaceholder);
  }
  resetBuild() {
    this.catalogSelectionMessage = '';
    this.selectedDeck = this.createEmptySelection('deck');
    this.selectedGrip = this.createEmptySelection('grip');
    this.selectedWheel = this.createEmptySelection('wheel');
    this.selectedBearing = this.createEmptySelection('bearing');
    this.selectedTruck = this.createEmptySelection('truck');
    this.selectedHardware = this.createEmptySelection('hardware');
    this.selectedSkateTool = this.createEmptySelection('skate_tool');
    this.selectedAssembly = this.createEmptySelection('assembly');
    ['deck', 'grip', 'wheel', 'bearing', 'truck', 'hardware', 'bolt', 'skate_tool', 'assembly_item', 'assembly'].forEach(key => this.removeUrlParam(key));
    this.updateDeckTexture(this.selectedDeck.texture);
    this.updateGripTexture(this.selectedGrip.texture);
    this.updateGripColor(this.selectedGrip.color);
    this.updateWheelTexture(this.selectedWheel.texture);
    this.updateBearingColor(this.selectedBearing.color);
    this.updateTruckColor(this.selectedTruck.color, this.selectedTruck.baseplateColor, this.selectedTruck);
    this.updateHardwareColors(this.selectedHardware);
    this.setAssemblyChoiceValue('');
    this.renderUI();
    this.resetCamera();
    this.querySelector('#bc-summary')?.classList.remove('is-expanded');
    this.querySelector('#bc-summary-toggle')?.setAttribute('aria-expanded', 'false');
    this.querySelector('#bc-summary-scrim')?.classList.remove('is-visible');
  }
  renderUI(changedType = null) {
    const partText = (key, fallbackLabel, fallbackCopy) => {
      const row = this.querySelector(`[data-open-drawer="${key}"]`);
      return {
        label: row?.dataset.partLabel || fallbackLabel,
        copy: row?.dataset.defaultCopy || fallbackCopy
      };
    };
    const deckText = partText('deck', 'Deck', 'The flat wooden board you stand on.');
    const gripText = partText('grip', 'Griptape', "The sandpaper sheet on top so your shoes don't slip.");
    const truckText = partText('truck', 'Trucks', 'The metal part that holds the wood and wheel.');
    const wheelText = partText('wheel', 'Wheels', 'The round parts that roll on the ground.');
    const bearingText = partText('bearing', 'Bearings', 'Tiny metal rings inside the wheels that help them spin fast.');
    const hardwareText = partText('hardware', 'Hardware', 'The nuts and bolts that hold everything together.');
    const skateToolText = partText('skate_tool', 'Skate Tool', 'Adjust trucks and maintain your board.');
    const assemblyText = partText('assembly', 'Assembly', 'How should this board arrive?');
    const defaultPartCopy = {
      deck: deckText.copy,
      grip: gripText.copy,
      truck: truckText.copy,
      wheel: wheelText.copy,
      bearing: bearingText.copy,
      hardware: hardwareText.copy,
      skate_tool: skateToolText.copy,
      assembly: assemblyText.copy
    };
    const parts = [
      { key: 'deck', label: deckText.label, sel: this.selectedDeck },
      { key: 'grip', label: gripText.label, sel: this.selectedGrip },
      { key: 'truck', label: truckText.label, sel: this.selectedTruck },
      { key: 'wheel', label: wheelText.label, sel: this.selectedWheel },
      { key: 'bearing', label: bearingText.label, sel: this.selectedBearing },
      { key: 'hardware', label: hardwareText.label, sel: this.selectedHardware },
      { key: 'skate_tool', label: skateToolText.label, sel: this.selectedSkateTool },
      { key: 'assembly', label: assemblyText.label, sel: this.selectedAssembly }
    ];
    const activeParts = parts.filter(p => this.querySelector(`[data-open-drawer="${p.key}"]`) || this.querySelector(`#bc-drawer-${p.key}`));
    const summaryParts = activeParts.length ? activeParts : parts;
    const listEl = this.querySelector('#bc-summary-list');
    const totalsEl = this.querySelector('#bc-summary-totals');
    const countEl = this.querySelector('#bc-summary-count');
    const builderProgressEl = this.querySelector('#bc-builder-progress');
    const priceState = this.getSummaryPriceState(summaryParts);
    const count = priceState.selectedCount;
    let summaryHtml = '';
    summaryParts.forEach(p => {
      const isChosen = p.sel && !p.sel.isPlaceholder;
      const valName = isChosen ? this.getItemDisplayName(p.sel) : 'Not selected';
      const valClass = isChosen ? 'bc-summary__val is-chosen' : 'bc-summary__val';
      const itemPriceState = isChosen
        ? this.getBoardPromotionPriceState(p.key, p.sel.variantPriceCents)
        : null;
      const discountedPrice = itemPriceState?.finalCents === null || !itemPriceState
        ? null
        : this.formatSummaryMoney(itemPriceState.finalCents);
      const priceHtml = !isChosen
        ? ''
        : discountedPrice === null
          ? '<span class="bc-summary__price is-unavailable">Unavailable</span>'
          : `<span class="bc-summary__price">${this.escapeHtml(discountedPrice)}</span>`;
      const previewImage = isChosen ? (p.sel.imageUrl || '') : '';
      const fullImage = isChosen ? (p.sel.fullImageUrl || p.sel.imageUrl || '') : '';
      const description = isChosen ? (p.sel.description || '') : '';
      const descriptionHtml = isChosen ? (p.sel.descriptionHtml || '') : '';
      const modalImages = isChosen
        ? this.normalizeModalImages(fullImage, previewImage, p.sel.images || [], valName)
        : [];
      const imgSrcAttr = previewImage
        ? `data-img="${this.escapeHtml(previewImage)}" data-full-img="${this.escapeHtml(fullImage)}" data-description="${this.escapeHtml(description)}" data-description-html="${this.escapeHtml(descriptionHtml)}" data-images="${this.escapeHtml(JSON.stringify(modalImages))}"`
        : '';
      summaryHtml += `
        <li class="bc-summary__item" data-drawer="${p.key}" ${imgSrcAttr} role="button" tabindex="0">
          <span class="bc-summary__type">${this.escapeHtml(p.label)}</span>
          <span class="${valClass}">${this.escapeHtml(valName.replace(/-/g, ' '))}</span>
          ${priceHtml}
        </li>
      `;
      const rowBtn = this.querySelector(`[data-open-drawer="${p.key}"] .bc-builder-status`);
      const rowCopy = this.querySelector(`[data-open-drawer="${p.key}"] .bc-builder-copy`);
      const row = this.querySelector(`[data-open-drawer="${p.key}"]`);
      if (rowBtn) {
        rowBtn.textContent = isChosen ? 'Chosen' : 'Choose';
        rowBtn.title = isChosen ? valName : '';
      }
      if (rowCopy) {
        rowCopy.textContent = isChosen ? valName.replace(/-/g, ' ') : defaultPartCopy[p.key];
        rowCopy.title = rowCopy.textContent;
      }
      if (row) {
        row.classList.toggle('is-chosen', isChosen);
        const rowPriceLabel = isChosen ? `, ${discountedPrice || 'price unavailable'}` : '';
        row.setAttribute('aria-label', `${p.label}: ${valName}${rowPriceLabel}`);
      }
    });
    const discountedSubtotalText = priceState.discountedSubtotalCents === null
      ? 'Unavailable'
      : this.formatSummaryMoney(priceState.discountedSubtotalCents);
    const totalsRows = [];
    if (!priceState.hasUnknownPrice) {
      totalsRows.push({ kind: 'original', label: 'Before discounts', cents: priceState.subtotalCents });
      if (priceState.boardDiscountCents > 0) {
        const promotionPercent = this.parsePriceCents(this.boardPromotionPercent) || 20;
        totalsRows.push({
          kind: 'promotion',
          label: `Board Customiser Promotion · ${promotionPercent}%`,
          cents: priceState.boardDiscountCents,
          isSaving: true
        });
      }
      if (priceState.memberDiscountCents > 0) {
        totalsRows.push({
          kind: 'loyalty',
          label: `Loyalty Program · ${this.parsePriceCents(this.memberDiscountPercent)}%`,
          cents: priceState.memberDiscountCents,
          isSaving: true
        });
      }
    }
    totalsRows.push({ kind: 'discounted', label: 'Subtotal', cents: priceState.discountedSubtotalCents });
    const totalsRowsHtml = totalsRows.map(row => {
      const amount = row.cents === null ? 'Unavailable' : this.formatSummaryMoney(row.cents);
      const valueClass = row.cents === null
        ? 'bc-summary__subtotal-value is-unavailable'
        : 'bc-summary__subtotal-value';
      return `
        <div class="bc-summary__subtotal bc-summary__subtotal--${row.kind}">
          <dt class="bc-summary__subtotal-label">${this.escapeHtml(row.label)}</dt>
          <dd class="${valueClass}">${row.isSaving ? '−' : ''}${this.escapeHtml(amount || 'Unavailable')}</dd>
        </div>
      `;
    }).join('');
    const totalsTag = totalsEl ? 'div' : 'li';
    const totalsHtml = `
      <${totalsTag} class="bc-summary__totals">
        <dl class="bc-summary__breakdown">${totalsRowsHtml}</dl>
      </${totalsTag}>
    `;
    if (totalsEl) totalsEl.innerHTML = totalsHtml;
    else summaryHtml += totalsHtml;
    if (listEl) listEl.innerHTML = summaryHtml;
    if (countEl) {
      countEl.textContent = priceState.hasUnknownPrice
        ? '—'
        : discountedSubtotalText;
      countEl.classList.toggle('is-unavailable', priceState.hasUnknownPrice);
      countEl.setAttribute(
        'aria-label',
        priceState.hasUnknownPrice
          ? 'Selected parts subtotal unavailable'
          : `Selected parts discounted subtotal ${discountedSubtotalText}`
      );
    }
    if (builderProgressEl) builderProgressEl.textContent = `${count}/${summaryParts.length} selected`;
    if (listEl) {
      listEl.querySelectorAll('.bc-summary__item').forEach(el => {
        const openReceiptImage = () => {
          const previewSrc = el.getAttribute('data-img');
          const fullSrc = el.getAttribute('data-full-img') || previewSrc;
          if (previewSrc && previewSrc !== 'null' && previewSrc !== '') {
            this.openImageModal(
              fullSrc,
              el.querySelector('.bc-summary__val')?.textContent || 'Part Image',
              previewSrc,
              el.getAttribute('data-description') || '',
              el.getAttribute('data-description-html') || '',
              this.parseImageList(el.getAttribute('data-images'))
            );
          }
        };
        el.addEventListener('click', openReceiptImage);
        el.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openReceiptImage();
          }
        });
      });
    }
    window.requestAnimationFrame(() => this.syncPersistentScrollbars());
    this.updateActiveProductCards(changedType);
    if (this.catalogLifecycleBound) this.updateCatalogAvailabilityUI();
  }
  initBuilderMenu() {
    const menu = this.querySelector('[data-bc-menu]');
    const openBtn = this.querySelector('[data-bc-menu-open]');
    const closeBtn = this.querySelector('[data-bc-menu-close]');
    if (!menu || !openBtn || !closeBtn) return;
    this.upgradeLegacyBuilderMenu(menu);
    const mainMenuPanel = menu?.querySelector('[data-main-menu-panel]');
    const shopMenuPanel = menu?.querySelector('[data-shop-menu-panel]');
    const menuCloseTriggers = menu?.querySelectorAll('[data-bc-menu-close]') || [];
    const shopTriggers = menu?.querySelectorAll('[data-bc-shop-drill]') || [];
    const shopBackTriggers = menu?.querySelectorAll('[data-bc-shop-back]') || [];
    const menuMainTriggers = menu?.querySelectorAll('[data-bc-menu-main]') || [];
    const menuShopShortcuts = menu?.querySelectorAll('[data-bc-shop-shortcut]') || [];
    const isMenuOpen = () => menu.getAttribute('aria-hidden') === 'false';
    const updateMenuMode = (isNested) => {
      menu.classList.toggle('is-nested-open', isNested);
      shopTriggers.forEach(trigger => trigger.setAttribute('aria-expanded', isNested ? 'true' : 'false'));
      menuMainTriggers.forEach(trigger => trigger.setAttribute('aria-pressed', isNested ? 'false' : 'true'));
      menuShopShortcuts.forEach(trigger => trigger.setAttribute('aria-pressed', isNested ? 'true' : 'false'));
    };
    const closeNested = () => {
      updateMenuMode(false);
      if (shopMenuPanel) shopMenuPanel.scrollTop = 0;
    };
    const openNested = () => {
      updateMenuMode(true);
      if (mainMenuPanel) mainMenuPanel.scrollTop = 0;
    };
    const openMenu = () => {
      if (isMenuOpen()) return;
      this.menuLastFocusedElement = document.activeElement;
      menu.hidden = false;
      menu.removeAttribute('inert');
      this.lockPageScroll();
      closeNested();
      menu.setAttribute('aria-hidden', 'false');
      menu.classList.add('is-open');
      closeBtn.focus?.({ preventScroll: true });
    };
    const closeMenu = ({ restoreFocus = true } = {}) => {
      if (!isMenuOpen()) return;
      const focusTarget = this.menuLastFocusedElement && !menu.contains(this.menuLastFocusedElement)
        ? this.menuLastFocusedElement
        : openBtn;
      this.menuLastFocusedElement = null;
      menu.setAttribute('aria-hidden', 'true');
      menu.classList.remove('is-open');
      closeNested();
      this.unlockPageScroll();
      menu.setAttribute('inert', '');
      menu.hidden = true;
      if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    };
    this.bindBuilderTap(openBtn, () => {
      if (isMenuOpen()) return;
      openMenu();
    });
    menuCloseTriggers.forEach(trigger => trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isMenuOpen()) return;
      closeMenu();
    }));
    shopTriggers.forEach(trigger => this.bindBuilderTap(trigger, () => {
      openNested();
    }));
    shopBackTriggers.forEach(trigger => this.bindBuilderTap(trigger, () => {
      closeNested();
    }));
    menuMainTriggers.forEach(trigger => this.bindBuilderTap(trigger, () => {
      closeNested();
    }));
    menuShopShortcuts.forEach(trigger => this.bindBuilderTap(trigger, () => {
      openNested();
    }));
    menu.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', closeMenu);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isMenuOpen()) closeMenu();
    });
    menu.addEventListener('click', (event) => {
      if (event.target?.closest?.('.mobile-menu-inner')) return;
      closeMenu();
    });
  }
  initSummaryControls() {
    const summaryToggle = this.querySelector('#bc-summary-toggle');
    const summary = this.querySelector('#bc-summary');
    const summaryScrim = this.querySelector('#bc-summary-scrim');
    const summaryClose = this.querySelector('#bc-summary-close');
    if (!summaryToggle || !summary) return;

    summaryToggle.setAttribute('aria-expanded', summary.classList.contains('is-expanded') ? 'true' : 'false');
    const isDesktopSummaryMode = () => window.matchMedia('(min-width: 75rem) and (hover: hover) and (pointer: fine), (min-width: 64rem) and (orientation: landscape) and (hover: hover) and (pointer: fine)').matches;
    const syncSummary = () => {
      const isOpen = summary.classList.contains('is-expanded');
      summaryToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      summaryScrim?.classList.toggle('is-visible', isOpen && !isDesktopSummaryMode());
      if (isOpen) window.requestAnimationFrame(() => this.syncPersistentScrollbars());
    };
    this.syncSummaryLayout = syncSummary;
    const closeSummary = () => {
      summary.classList.remove('is-expanded');
      syncSummary();
    };
    summaryToggle.addEventListener('click', (event) => {
      event.stopPropagation();
      summary.classList.toggle('is-expanded');
      syncSummary();
    });
    summaryClose?.addEventListener('click', (event) => {
      event.stopPropagation();
      closeSummary();
    });
    summary.addEventListener('click', (event) => {
      event.stopPropagation();
    });
    summaryScrim?.addEventListener('click', (event) => {
      if (isDesktopSummaryMode()) return;
      if (event.target === summaryScrim) closeSummary();
    });
    document.addEventListener('click', (event) => {
      if (!summary.classList.contains('is-expanded')) return;
      if (isDesktopSummaryMode()) return;
      if (event.target.closest?.('.bc-summary')) return;
      if (event.target.closest?.('.bc-image-modal')) return;
      closeSummary();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && summary.classList.contains('is-expanded')) closeSummary();
    });
  }
  upgradeLegacyBuilderMenu(menu) {
    if (!menu) return;
    let didUpgrade = false;

    menu.querySelectorAll('.mobile-menu-heading, .mobile-menu-thumbbar').forEach(node => {
      node.remove();
      didUpgrade = true;
    });

    const navList = menu.querySelector('.mobile-main-panel .mobile-nav-list');
    if (navList) {
      const links = Array.from(navList.querySelectorAll('a'));
      const hasHomeLink = links.some(link => (link.textContent || '').trim().toLowerCase() === 'home');
      let homeItem = links.find(link => (link.textContent || '').trim().toLowerCase() === 'home')?.closest('.mobile-nav-item') || null;
      if (!hasHomeLink) {
        homeItem = document.createElement('li');
        homeItem.className = 'mobile-nav-item';
        homeItem.innerHTML = `<a href="${this.escapeHtml(this.rootUrl || '/')}" class="mobile-nav-link">Home</a>`;
        navList.insertBefore(homeItem, navList.firstChild);
        didUpgrade = true;
      }

      if (!navList.querySelector('[data-bc-shop-drill]')) {
        const shopItem = document.createElement('li');
        shopItem.className = 'mobile-nav-item';
        shopItem.innerHTML = `
          <button type="button" class="mobile-root-button" data-bc-shop-drill aria-expanded="false">
            <span class="mobile-nav-link">Shop</span>
            <span class="mobile-root-icon" aria-hidden="true">&rsaquo;</span>
          </button>
        `;
        const insertAfter = homeItem || navList.firstElementChild;
        if (insertAfter?.nextSibling) {
          navList.insertBefore(shopItem, insertAfter.nextSibling);
        } else {
          navList.appendChild(shopItem);
        }
        didUpgrade = true;
      }
    }

    const shopPanel = menu.querySelector('[data-shop-menu-panel]');
    if (shopPanel && !shopPanel.querySelector('[data-bc-shop-back]')) {
      const backButton = document.createElement('button');
      backButton.className = 'mobile-shop-back-trigger';
      backButton.type = 'button';
      backButton.setAttribute('data-bc-shop-back', '');
      backButton.setAttribute('aria-label', 'Back to main menu');
      backButton.innerHTML = `
        <span class="mobile-shop-back-icon" aria-hidden="true">&lsaquo;</span>
        <span class="mobile-shop-back-heading">Back</span>
      `;
      shopPanel.insertBefore(backButton, shopPanel.firstChild);
      didUpgrade = true;
    }

    menu.classList.toggle('is-legacy-menu-upgraded', didUpgrade);
  }
  initCartDrawer() {
    this.cartDrawer = this.querySelector('[data-bc-cart-drawer]') || this.querySelector('#CartDrawer');
    this.cartDrawerPanel = this.querySelector('.cart-drawer-panel');
    this.cartDrawerBody = this.querySelector('[data-cart-drawer-body]');
    this.cartDrawerFooter = this.querySelector('[data-cart-drawer-footer]');
    this.cartDrawerTotal = this.querySelector('[data-cart-total]');
    this.cartClearButton = this.querySelector('[data-cart-clear]');
    this.cartClearConfirm = this.querySelector('[data-cart-clear-confirm]');
    this.cartClearConfirmAction = this.querySelector('[data-cart-clear-confirm-action]');
    this.cartTriggers = this.querySelectorAll('[data-bc-cart-drawer-trigger], [data-cart-drawer-trigger]');
    const cartCloseButtons = this.querySelectorAll('[data-bc-cart-drawer-close], [data-cart-drawer-close]');
    this.cartBadges = this.querySelectorAll('[data-bc-cart-count-badge], [data-cart-count-badge]');
    this.syncCartDrawerViewportHeight();
    if (!this.cartViewportListenersBound) {
      window.addEventListener('resize', this.handleCartViewportResize, { passive: true });
      window.visualViewport?.addEventListener('resize', this.handleCartViewportResize, { passive: true });
      window.visualViewport?.addEventListener('scroll', this.handleCartViewportResize, { passive: true });
      this.cartViewportListenersBound = true;
    }
    this.cartTriggers.forEach(trigger => {
      this.bindBuilderTap(trigger, () => {
        this.openCartDrawer({ userInitiated: true });
      });
    });
    cartCloseButtons.forEach(btn => {
      const isScrimClose = btn.classList.contains('cart-drawer-scrim');
      this.bindBuilderTap(btn, (event) => {
        if (isScrimClose && performance.now() < this.cartDrawerSuppressCloseUntil) {
          if (event?.cancelable) event.preventDefault();
          if (typeof event?.stopPropagation === 'function') event.stopPropagation();
          return;
        }
        const isTouchClose = event?.pointerType === 'touch' || event?.pointerType === 'pen';
        this.closeCartDrawer({ restoreFocus: !isTouchClose });
      }, {});
    });
    this.cartClearButton?.addEventListener('click', async (e) => {
      const clearButton = e.target.closest('[data-cart-clear]');
      if (!clearButton) return;
      e.preventDefault();
      if (clearButton.disabled) return;
      const itemCount = Number(this.latestCart ? this.getOptimisticCart(this.latestCart).item_count : 0) || 0;
      if (itemCount <= 0) return;
      if (this.openCartClearConfirm(clearButton)) return;
      clearButton.disabled = true;
      clearButton.style.opacity = '0.45';
      try {
        await this.clearCart();
      } catch (error) {
        this.showCartDrawerNotice('Could not clear cart. Please try again.');
        clearButton.disabled = false;
        clearButton.style.opacity = '';
      }
    });
    this.cartClearConfirm?.addEventListener('click', async (e) => {
      const cancelButton = e.target.closest('[data-cart-clear-cancel]');
      const confirmButton = e.target.closest('[data-cart-clear-confirm-action]');
      if (e.target === this.cartClearConfirm || cancelButton) {
        e.preventDefault();
        this.closeCartClearConfirm();
        return;
      }
      if (!confirmButton) return;
      e.preventDefault();
      confirmButton.disabled = true;
      confirmButton.style.opacity = '0.45';
      this.closeCartClearConfirm({ restoreFocus: false, resetAction: false });
      try {
        await this.clearCart();
      } catch (error) {
        this.showCartDrawerNotice('Could not clear cart. Please try again.');
        confirmButton.disabled = false;
        confirmButton.style.opacity = '';
        if (this.cartClearButton) {
          this.cartClearButton.disabled = false;
          this.cartClearButton.style.opacity = '';
        }
      }
    });
    this.cartDrawerBody?.addEventListener('click', async (e) => {
      const qtyButton = e.target.closest('[data-cart-qty]');
      const removeButton = e.target.closest('[data-cart-remove]');
      const item = e.target.closest('[data-cart-item]');
      if (!item || (!qtyButton && !removeButton) || item.dataset.updating === 'true') return;
      e.preventDefault();
      const key = item.getAttribute('data-line-key');
      const variantId = item.getAttribute('data-variant-id') || '';
      const isOptimisticPending = item.getAttribute('data-optimistic') === 'true';
      const currentQty = Number(item.getAttribute('data-quantity')) || 0;
      const delta = qtyButton ? Number(qtyButton.getAttribute('data-cart-qty')) || 0 : 0;
      const maxQuantity = this.parseCartNumber(item.getAttribute('data-max-quantity'));
      if (!key || (qtyButton && qtyButton.disabled) || (removeButton && removeButton.disabled)) return;
      const totalVariantQuantity = window.HalfpipedBoardCartQuantity?.variantQuantity(this.latestCart, variantId) ?? currentQty;
      if (qtyButton && delta > 0 && maxQuantity !== null && totalVariantQuantity >= maxQuantity) {
        const quantities = window.HalfpipedBoardCartQuantity;
        if (quantities?.notifyQuantityLimit && quantities.hasBoard(this.latestCart)) quantities.notifyQuantityLimit(quantities.quantityLimitError(maxQuantity, {
          stockLimited: item.getAttribute('data-stock-limited') === 'true', cartQuantity: totalVariantQuantity,
          item: this.latestCart?.items?.find(line => this.getCartLineKey(line) === key),
        }));
        else {
          qtyButton.disabled = true;
          this.showCartDrawerNotice(maxQuantity === 1 ? 'Only 1 available.' : `Only ${maxQuantity} available.`);
        }
        return;
      }
      if (removeButton && isOptimisticPending && variantId) {
        this.pendingCartRemovals.add(key);
        item.style.display = 'none';
        if (this.latestCart) this.renderCart(this.latestCart);
        return;
      }
      const removesLine = removeButton || (qtyButton && currentQty + delta <= 0);
      const removesBoard = this.latestCart?.items?.some(line => line.key === key && line.properties?._hp_board_bundle_id);
      if (removesLine && removesBoard && !window.HalfpipedBoardCartRemoval) {
        this.showCartDrawerNotice('Please reload the page before removing this board.');
        return;
      }
      if (removesLine && window.HalfpipedBoardCartRemoval) {
        let removingBoard = false;
        try {
          const result = await window.HalfpipedBoardCartRemoval.remove({
            key,
            cart: this.latestCart,
            onPending: () => {
              removingBoard = true;
              this.cartActiveChanges += 1;
              this.cartLoadRequestId += 1;
              this.cartDrawer?.classList.add('is-updating');
            },
            onCart: (cart, snapshot) => {
              this.pendingCartRemovals.clear();
              this.cartLastFetchedAt = Date.now();
              this.renderCart(cart, { snapshot });
            }
          });
          if (result.handled) return;
        } catch (error) {
          this.showCartDrawerNotice(error?.message || 'Could not remove this board. Please try again.');
          return;
        } finally {
          if (removingBoard) {
            this.cartActiveChanges = Math.max(0, this.cartActiveChanges - 1);
            if (this.cartActiveChanges === 0) this.cartDrawer?.classList.remove('is-updating');
          }
        }
      }
      item.dataset.updating = 'true';
      item.style.opacity = '0.4';
      item.style.pointerEvents = 'none';
      try {
        if (qtyButton) {
          const nextQty = Math.max(0, currentQty + delta);
          if (nextQty === 0) {
            this.pendingCartRemovals.add(key);
            item.style.display = 'none';
            if (this.latestCart) this.renderCart(this.latestCart);
          } else if (!this.latestCart?.items?.find(line => line.key === key)?.properties?._hp_board_bundle_id) {
            item.setAttribute('data-quantity', String(nextQty));
            const qtyText = item.querySelector('.cart-drawer-qty span');
            if (qtyText) qtyText.textContent = String(nextQty);
          }
          await this.changeCartItem(key, nextQty, { revealExtra: delta > 0 });
        } else if (removeButton) {
          this.pendingCartRemovals.add(key);
          item.style.display = 'none';
          if (this.latestCart) this.renderCart(this.latestCart);
          await this.changeCartItem(key, 0);
        }
      } catch (error) {
        if (!window.HalfpipedBoardCartQuantity?.notifyQuantityLimit?.(error)) {
          this.showCartDrawerNotice(error?.message || 'Could not update your cart. Please try again.');
        }
        item.dataset.updating = 'false';
        item.style.display = '';
        item.style.opacity = '';
        item.style.pointerEvents = '';
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.cartClearConfirm && !this.cartClearConfirm.hidden) {
        this.closeCartClearConfirm();
        return;
      }
      if (e.key === 'Escape' && this.cartDrawer?.classList.contains('is-open')) {
        this.closeCartDrawer();
      }
    });
  }
  getCartEndpoint(path) {
    const root = this.rootUrl.endsWith('/') ? this.rootUrl : `${this.rootUrl}/`;
    return `${root}cart${path}`;
  }
  escapeHtml(value = '') {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  formatMoney(cents = 0) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.fallbackCurrency,
        currencyDisplay: 'code'
      }).format(cents / 100);
    } catch (error) {
      return `${this.fallbackCurrency} ${(cents / 100).toFixed(2)}`;
    }
  }
  parseCartNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }
  normalizeCartMaxQuantity(value) {
    const number = this.parseCartNumber(value);
    return number === null ? null : Math.max(0, number);
  }
  getCartLineVariantId(item = {}) {
    const variantId = item.variant_id ?? item.variantId;
    return variantId === null || variantId === undefined || variantId === '' ? '' : String(variantId);
  }
  getPendingCartKeyForVariant(variantId) {
    const normalizedVariantId = variantId === null || variantId === undefined || variantId === '' ? '' : String(variantId);
    return normalizedVariantId ? `pending-${normalizedVariantId}` : '';
  }
  getCartLineKey(item = {}) {
    if (item.key !== null && item.key !== undefined && item.key !== '') return String(item.key);
    return this.getPendingCartKeyForVariant(this.getCartLineVariantId(item));
  }
  getCartItemOrderKeys(item = {}) {
    const keys = [];
    const itemKey = this.getCartLineKey(item);
    const variantId = this.getCartLineVariantId(item);
    if (itemKey) keys.push(`key:${itemKey}`);
    if (variantId) keys.push(`identity:${JSON.stringify([variantId, Object.entries(item.properties || {}).sort(), item.selling_plan_allocation?.selling_plan?.id || item.selling_plan_id || null])}`);
    return keys;
  }
  rememberCartDrawerItemOrder(items = []) {
    const nextOrder = [];
    const seenKeys = new Set();
    items.forEach(item => {
      this.getCartItemOrderKeys(item).forEach(key => {
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        nextOrder.push(key);
      });
    });
    this.cartDrawerItemOrder = nextOrder;
  }
  getCartItemPreviousOrder(item = {}) {
    let previousOrder = Infinity;
    this.getCartItemOrderKeys(item).forEach(key => {
      const index = this.cartDrawerItemOrder.indexOf(key);
      if (index >= 0) previousOrder = Math.min(previousOrder, index);
    });
    return previousOrder;
  }
  stabilizeCartDrawerItemOrder(cart = {}) {
    if (window.HalfpipedBoardIntegrity) return window.HalfpipedBoardIntegrity.orderCart(cart, window.HalfpipedBoardCheckout?.getCartPresentation?.(cart));
    if (!this.cartDrawerItemOrder.length || !Array.isArray(cart.items) || cart.items.some(item => item?.pending === true)) {
      return cart;
    }
    const sortedItems = cart.items
      .map((item, index) => ({
        item,
        index,
        previousOrder: this.getCartItemPreviousOrder(item)
      }))
      .sort((a, b) => {
        if (a.previousOrder !== b.previousOrder) return a.previousOrder - b.previousOrder;
        return a.index - b.index;
      })
      .map(entry => entry.item);
    const didReorder = sortedItems.some((item, index) => item !== cart.items[index]);
    return didReorder ? { ...cart, items: sortedItems } : cart;
  }
  getCartItemMaxQuantity(item = {}) {
    const inventoryManagement = item.inventory_management ?? item.inventoryManagement;
    const inventoryPolicy = item.inventory_policy ?? item.inventoryPolicy;
    const inventoryQuantity = this.parseCartNumber(item.inventory_quantity ?? item.inventoryQuantity);
    const quantityRuleMax = this.parseCartNumber(item.quantity_rule_max ?? item.quantityRuleMax ?? item.quantity_rule?.max ?? item.quantityRule?.max);
    let maxQuantity = quantityRuleMax;
    if (inventoryManagement && inventoryPolicy === 'deny' && inventoryQuantity !== null) {
      maxQuantity = maxQuantity === null ? inventoryQuantity : Math.min(maxQuantity, inventoryQuantity);
    }
    return this.normalizeCartMaxQuantity(maxQuantity);
  }
  isCartItemStockLimited(item = {}) {
    const inventoryQuantity = this.parseCartNumber(item.inventory_quantity ?? item.inventoryQuantity);
    const quantityRuleMax = this.parseCartNumber(item.quantity_rule_max ?? item.quantityRuleMax ?? item.quantity_rule?.max ?? item.quantityRule?.max);
    return Boolean((item.inventory_management ?? item.inventoryManagement)
      && (item.inventory_policy ?? item.inventoryPolicy) === 'deny' && inventoryQuantity !== null
      && (quantityRuleMax === null || inventoryQuantity <= quantityRuleMax));
  }
  renderCartItemMeta(item = {}) {
    if (item.options_with_values?.length) {
      return item.options_with_values
        .filter(option => option.value && option.value !== 'Default Title')
        .map(option => `${this.escapeHtml(option.name)}: ${this.escapeHtml(option.value)}`)
        .join('<br>');
    }
    if (item.variant_title && item.variant_title !== 'Default Title') {
      return this.escapeHtml(item.variant_title);
    }
    return '';
  }
  resetCartBodyScroll() {
    if (!this.cartDrawerBody) return;
    this.cartDrawerBody.scrollTop = 0;
    this.cartDrawerBody.scrollLeft = 0;
  }
  restoreCartBodyScroll(top = 0, left = 0) {
    if (!this.cartDrawerBody) return;
    // Even an unchanged assignment interrupts a native smooth extra-item reveal.
    if (this.cartDrawerBody.scrollTop !== top) this.cartDrawerBody.scrollTop = top;
    if (this.cartDrawerBody.scrollLeft !== left) this.cartDrawerBody.scrollLeft = left;
  }
  showCartDrawerNotice(message) {
    if (!this.cartDrawerBody || !message) return;
    const scrollTop = this.cartDrawerBody.scrollTop;
    const scrollLeft = this.cartDrawerBody.scrollLeft;
    this.cartDrawerBody.querySelector('[data-cart-drawer-notice]')?.remove();
    const notice = document.createElement('p');
    notice.className = 'cart-drawer-notice';
    notice.setAttribute('data-cart-drawer-notice', '');
    notice.setAttribute('role', 'alert');
    notice.textContent = message;
    this.cartDrawerBody.prepend(notice);
    this.restoreCartBodyScroll(scrollTop, scrollLeft);
  }
  openCartClearConfirm(trigger) {
    if (!this.cartClearConfirm) return false;
    this.cartClearReturnFocus = trigger || document.activeElement;
    if (this.cartClearConfirmAction) {
      this.cartClearConfirmAction.disabled = false;
      this.cartClearConfirmAction.style.opacity = '';
    }
    this.cartClearConfirm.hidden = false;
    window.requestAnimationFrame(() => {
      this.cartClearConfirmAction?.focus({ preventScroll: true });
    });
    return true;
  }
  closeCartClearConfirm({ restoreFocus = true, resetAction = true } = {}) {
    if (!this.cartClearConfirm || this.cartClearConfirm.hidden) return;
    this.cartClearConfirm.hidden = true;
    if (resetAction && this.cartClearConfirmAction) {
      this.cartClearConfirmAction.disabled = false;
      this.cartClearConfirmAction.style.opacity = '';
    }
    const focusTarget = this.cartClearReturnFocus;
    this.cartClearReturnFocus = null;
    if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  }
  broadcastCartUpdate(cart, source = 'board-customizer', confirmation = false) {
    if (!cart || typeof cart !== 'object') return;
    const snapshot = confirmation === true ? this.confirmedCartReads?.get(cart) : confirmation;
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'board-customizer',
        data: {
          source,
          cartConfirmed: snapshot?.confirmed === true,
          cartRevision: snapshot?.cartRevision,
          itemCount: Number(cart.item_count) || 0
        }
      }
    }));
  }
  openLiveHeaderCartDrawer({ load = true, showLoading = true, force = false, revealBundleId = null } = {}) {
    const headerCartDrawer = document.querySelector('.site-header #CartDrawer') || document.getElementById('CartDrawer');
    if (!headerCartDrawer) return false;

    const cart = !load && showLoading ? null : this.latestCart ? this.getOptimisticCart(this.latestCart) : null;
    document.dispatchEvent(new CustomEvent('cart:open', {
      bubbles: true,
      detail: {
        cart,
        load,
        showLoading,
        force,
        revealBundleId,
        source: 'board-customizer'
      }
    }));
    return true;
  }
  updateCartChrome(cart) {
    if (window.HalfpipedBoardIntegrity?.isCleanupPending?.()) return;
    const count = cart?.item_count || 0;
    const label = count === 1 ? 'item' : 'items';
    const badgeText = count > 99 ? '99+' : String(count);
    this.cartTriggers = this.querySelectorAll('[data-bc-cart-drawer-trigger], [data-cart-drawer-trigger]');
    this.cartBadges = this.querySelectorAll('[data-bc-cart-count-badge], [data-cart-count-badge]');
    this.cartTriggers.forEach(trigger => {
      trigger.setAttribute('aria-label', `Open cart, ${count} ${label}`);
    });
    this.cartBadges.forEach(badge => {
      badge.textContent = badgeText;
      badge.classList.toggle('is-wide', badgeText.length > 1);
      badge.classList.toggle('is-hidden', count <= 0);
      badge.hidden = count <= 0;
      if (count > 0) {
        badge.style.display = 'inline-flex';
        badge.style.visibility = 'visible';
        badge.style.opacity = '1';
      } else {
        badge.style.removeProperty('display');
        badge.style.removeProperty('visibility');
        badge.style.removeProperty('opacity');
      }
    });
  }
  renderCart(cart, options = {}) {
    if (window.HalfpipedBoardIntegrity?.isCleanupPending?.()) return;
    const cartSnapshot = options.snapshot || (options.cartConfirmed ? this.confirmedCartReads?.get(cart) : null);
    cart = this.stabilizeCartDrawerItemOrder(cart || {});
    this.latestCart = cart;
    const confirmedDisplayCart = this.getOptimisticCart(cart);
    const preview = this.boardAddWork?.presentationItems;
    const previewBundle = preview?.[0]?.properties?._hp_board_bundle_id;
    const displayCart = preview?.length ? { ...confirmedDisplayCart, items: [
      ...(confirmedDisplayCart.items || []).filter(item => item.properties?._hp_board_bundle_id !== previewBundle), ...preview
    ] } : confirmedDisplayCart;
    this.updateCartChrome(displayCart);
    const publishRenderedCart = () => {
      if (options.presentationOnly) return;
      this.broadcastCartUpdate(confirmedDisplayCart, options.source || 'board-customizer', cartSnapshot);
      if (cartSnapshot?.confirmed) window.HalfpipedCartMutations?.acknowledgeCart?.(cartSnapshot.cartRevision);
    };
    if (!this.cartDrawerBody || !this.cartDrawerFooter || !this.cartDrawerTotal) {
      publishRenderedCart();
      return;
    }

    const visibleItems = displayCart.items || [];
    const boardNumbers = new Map([...new Set(visibleItems.map(item => item.properties?._hp_board_bundle_id).filter(Boolean))].map((id, index) => [id, index + 1]));
    const shouldResetScroll = Boolean(options.resetScroll);
    const previousScrollTop = this.cartDrawerBody.scrollTop || 0;
    const previousScrollLeft = this.cartDrawerBody.scrollLeft || 0;
    this.cartDrawerTotal.textContent = this.formatMoney(displayCart.total_price);
    if (this.cartClearButton) {
      this.cartClearButton.disabled = Boolean(preview?.length) || !visibleItems.some(item => item.pending !== true);
      this.cartClearButton.style.opacity = '';
    }
    if (visibleItems.length === 0) {
      this.rememberCartDrawerItemOrder([]);
      this.cartDrawerBody.innerHTML = '<p class="cart-drawer-empty">Your cart is empty. Add a deck, wheels, or fresh gear and it will show up here.</p>';
      this.cartDrawerFooter.hidden = false;
      if (shouldResetScroll) this.resetCartBodyScroll();
      else this.restoreCartBodyScroll(previousScrollTop, previousScrollLeft);
      publishRenderedCart();
      return;
    }
    this.cartDrawerFooter.hidden = false;
    const cartHtml = `
      <ul class="cart-drawer-items">
        ${visibleItems.map((item, itemIndex) => {
          const meta = this.renderCartItemMeta(item);
          const isPending = item.pending === true;
          const isOptimisticPending = isPending && item.optimistic === true;
          const quantity = Number(item.quantity) || 0;
          const itemKey = this.getCartLineKey(item);
          const variantId = this.getCartLineVariantId(item);
          const maxQuantity = this.getCartItemMaxQuantity(item);
          const maxQuantityAttr = maxQuantity !== null ? ` data-max-quantity="${maxQuantity}" data-stock-limited="${this.isCartItemStockLimited(item)}"` : '';
          const bundleId = item.properties?._hp_board_bundle_id;
          const isBoardPart = Boolean(bundleId);
          const boardNumber = boardNumbers.get(bundleId);
          const boardComponent = isBoardPart ? item.properties._hp_board_bundle_component || '' : '';
          const isBoardEnd = isBoardPart && visibleItems[itemIndex + 1]?.properties?._hp_board_bundle_id !== bundleId;
          const boardGroupClass = isBoardPart ? ` cart-drawer-item--board-${boardComponent === 'deck' ? 'deck' : 'part'}${isBoardEnd ? ' cart-drawer-item--board-end' : ''}` : '';
          const fixedBoardChoice = isBoardPart && (quantity !== 1 || !window.HalfpipedBoardCartQuantity?.canAddExtra(item));
          const variantQuantity = window.HalfpipedBoardCartQuantity?.variantQuantity(displayCart, variantId) ?? quantity;
          const minusDisabled = quantity <= 1 || isPending || isBoardPart ? ' disabled' : '';
          const isAtMaxQuantity = maxQuantity !== null && variantQuantity >= maxQuantity;
          const hasBoardCart = window.HalfpipedBoardCartQuantity?.notifyQuantityLimit && window.HalfpipedBoardCartQuantity.hasBoard(displayCart);
          const plusDisabled = (isAtMaxQuantity && !hasBoardCart) || isPending || fixedBoardChoice ? ' disabled' : '';
          const plusTitle = fixedBoardChoice ? ' title="This choice is included once with your board"' : isAtMaxQuantity
            ? ` title="${this.escapeHtml(maxQuantity === 1 ? 'Only 1 available' : `Only ${maxQuantity} available`)}"`
            : isBoardPart ? ' title="Add an extra item outside this board"' : '';
          const removeDisabled = isPending && !isOptimisticPending ? ' disabled aria-disabled="true"' : '';
          if (item.image) this.preloadDrawerImage(item.image);
          const image = item.image
            ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title || item.title)}" loading="eager" decoding="async">`
            : '';
          return `
            <li class="cart-drawer-item${boardGroupClass}${isPending ? ' is-pending' : ''}${isOptimisticPending ? ' is-optimistic' : ''}" data-cart-item${isBoardPart ? ` data-board-group-tier="${this.escapeHtml(this.memberTier || 'Member')}" data-board-bundle-id="${this.escapeHtml(bundleId)}" data-board-bundle-component="${this.escapeHtml(boardComponent)}"` : ''} data-line-key="${this.escapeHtml(itemKey)}" data-cart-line-identity="${this.escapeHtml(window.HalfpipedCartDrawerDom?.identity(item) || '')}" data-variant-id="${this.escapeHtml(variantId)}" data-pending="${isPending ? 'true' : 'false'}" data-optimistic="${isOptimisticPending ? 'true' : 'false'}" data-quantity="${quantity}"${maxQuantityAttr}>
              ${boardComponent === 'deck' ? `<span class="cart-drawer-board-label">Custom complete bundle${boardNumbers.size > 1 ? ` ${boardNumber}` : ''}</span>` : ''}
              <a class="cart-drawer-item-media" href="${this.escapeHtml(item.url || '')}" tabindex="-1">${image}</a>
              <div>
                <h3 class="cart-drawer-item-title">
                  <a href="${this.escapeHtml(item.url || '')}">${this.escapeHtml(item.product_title || item.title)}</a>
                </h3>
                ${meta ? `<p class="cart-drawer-item-meta">${meta}</p>` : ''}
                ${isBoardPart ? `<p class="cart-drawer-item-meta cart-drawer-item-bundle-meta">Part of custom complete${boardNumbers.size > 1 ? ` ${boardNumber}` : ''}</p>` : ''}
                ${isPending && !isOptimisticPending ? `
                  <div class="cart-drawer-item-bottom">
                    <span class="cart-drawer-pending">Adding...</span>
                  </div>
                ` : `
                  <div class="cart-drawer-item-bottom">
                    <div class="cart-drawer-qty" aria-label="Quantity controls">
                      <button type="button" data-cart-qty="-1" aria-label="Decrease quantity"${minusDisabled}>-</button>
                      <span>${quantity}</span>
                      <button type="button" data-cart-qty="1" aria-label="${isBoardPart ? 'Add an extra item outside this board' : 'Increase quantity'}"${plusDisabled}${plusTitle}>+</button>
                    </div>
                    <span class="cart-drawer-price">${this.formatMoney(item.final_line_price)}</span>
                  </div>
                  <button class="cart-drawer-remove" type="button" data-cart-remove${removeDisabled}>Remove</button>
                `}
              </div>
            </li>
          `;
        }).join('')}
      </ul>
    `;
    if (window.HalfpipedCartDrawerDom) window.HalfpipedCartDrawerDom.render(this.cartDrawerBody, cartHtml);
    else this.cartDrawerBody.innerHTML = cartHtml;
    this.rememberCartDrawerItemOrder(visibleItems);
    if (shouldResetScroll) this.resetCartBodyScroll();
    else this.restoreCartBodyScroll(previousScrollTop, previousScrollLeft);
    publishRenderedCart();
  }
  async fetchCart(signal) {
    const mutations = window.HalfpipedCartMutations;
    const cartRevision = mutations?.getRevision();
    const exclusive = mutations?.getPendingCount?.() === 0;
    const response = await fetch(this.getCartEndpoint('.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      signal
    });
    if (!response.ok) throw new Error('Could not load cart');
    const cart = await response.json();
    if (signal?.aborted) throw this.boardAddAbortError();
    this.validateBoardAddCart(cart);
    this.confirmedCartReads ||= new WeakMap();
    const snapshot = { cartRevision, confirmed: exclusive && cartRevision === mutations?.getRevision() };
    this.confirmedCartReads.set(cart, snapshot);
    window.HalfpipedBoardCartRemoval?.rememberCart?.(cart, snapshot);
    return cart;
  }
  async clearCart() {
    if (!this.cartDrawerBody || !this.cartDrawerFooter) return null;
    const finishCartMutation = window.HalfpipedCartMutations?.begin();
    const clearRenderToken = ++this.cartClearRenderToken;
    let resolveCartClearWait = null;
    const cartClearWait = new Promise(resolve => {
      resolveCartClearWait = resolve;
    });
    const previousCart = this.latestCart ? {
      ...this.latestCart,
      items: Array.isArray(this.latestCart.items) ? this.latestCart.items.slice() : []
    } : null;
    const emptyCart = {
      ...(previousCart || {}),
      currency: previousCart?.currency || this.fallbackCurrency,
      item_count: 0,
      total_price: 0,
      items: []
    };
    this.cartActiveChanges += 1;
    this.cartLoadRequestId += 1;
    this.cartRefreshAfterChangeId += 1;
    window.HalfpipedCartClearPromise = cartClearWait;
    document.dispatchEvent(new CustomEvent('cart:clear:pending', { detail: { promise: cartClearWait } }));
    this.cartDrawer?.classList.add('is-updating');
    this.pendingCartRemovals.clear();
    this.renderCart(emptyCart, { resetScroll: true });
    try {
      const response = await fetch(this.getCartEndpoint('/clear.js'), {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Could not clear cart');
      const cart = await response.json();
      this.pendingCartRemovals.clear();
      if (clearRenderToken === this.cartClearRenderToken) {
        this.renderCart(cart, { resetScroll: true });
      }
      return cart;
    } catch (error) {
      if (previousCart && clearRenderToken === this.cartClearRenderToken) {
        this.renderCart(previousCart, { resetScroll: true });
      }
      this.showCartDrawerNotice('Could not clear cart. Please try again.');
      finishCartMutation?.(error);
      throw error;
    } finally {
      finishCartMutation?.();
      this.cartActiveChanges = Math.max(0, this.cartActiveChanges - 1);
      if (this.cartActiveChanges === 0) {
        this.cartDrawer?.classList.remove('is-updating');
      }
      if (window.HalfpipedCartClearPromise === cartClearWait) {
        window.HalfpipedCartClearPromise = null;
      }
      resolveCartClearWait?.();
      document.dispatchEvent(new CustomEvent('cart:clear:complete', { detail: { cart: this.latestCart } }));
      if (this.cartClearButton) {
        this.cartClearButton.disabled = !(this.latestCart?.items || []).some(item => item.pending !== true);
        this.cartClearButton.style.opacity = '';
        this.cartClearButton.blur?.();
      }
    }
  }
  getOptimisticCart(cart = {}) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    if (!items.length || this.pendingCartRemovals.size === 0) {
      return {
        ...cart,
        items,
        item_count: Number(cart.item_count) || 0,
        total_price: Number(cart.total_price) || 0
      };
    }
    let removedItemCount = 0;
    let removedTotal = 0;
    const visibleItems = items.filter(item => {
      if (!this.pendingCartRemovals.has(this.getCartLineKey(item))) return true;
      removedItemCount += Number(item.quantity) || 0;
      removedTotal += Number(item.final_line_price) || 0;
      return false;
    });
    return {
      ...cart,
      items: visibleItems,
      item_count: Math.max(0, (Number(cart.item_count) || 0) - removedItemCount),
      total_price: Math.max(0, (Number(cart.total_price) || 0) - removedTotal)
    };
  }
  async changeCartItem(id, quantity, { revealExtra = false } = {}) {
    if (window.HalfpipedBoardQuantityRepair?.hasPending?.(this.latestCart)) {
      throw new Error('Select Checkout to separate your saved extra items safely.');
    }
    if (quantity > 0 && (this.latestCart?.items || []).some(item => item.properties?._hp_board_bundle_id)) {
      if (!window.HalfpipedBoardCartQuantity) throw new Error('Please reload the page before updating board parts.');
      this.cartActiveChanges += 1;
      this.cartLoadRequestId += 1;
      this.cartDrawer?.classList.add('is-updating');
      try {
        const lifecycleGeneration = this.textureLifecycleGeneration;
        const item = this.latestCart.items.find(line => line.key === id);
        const result = await window.HalfpipedBoardCartQuantity.change({ key: id, quantity, cart: this.latestCart,
          maxQuantity: item ? this.getCartItemMaxQuantity(item) : null,
          maxStockLimited: item ? this.isCartItemStockLimited(item) : false,
          onCart: (cart, snapshot) => {
            this.pendingCartRemovals.delete(id);
            this.cartLastFetchedAt = Date.now();
            this.renderCart(cart, { snapshot });
          },
        });
        if (revealExtra && this.isConnected && lifecycleGeneration === this.textureLifecycleGeneration) {
          window.HalfpipedBoardCartQuantity.revealExtra({ result, cart: this.latestCart, drawer: this.cartDrawer, body: this.cartDrawerBody });
        }
        return result.cart;
      } finally {
        this.cartActiveChanges = Math.max(0, this.cartActiveChanges - 1);
        if (!this.cartActiveChanges) this.cartDrawer?.classList.remove('is-updating');
      }
    }
    const finishCartMutation = window.HalfpipedCartMutations?.begin();
    this.cartActiveChanges += 1;
    this.cartLoadRequestId += 1;
    this.cartDrawer?.classList.add('is-updating');
    const runChange = async () => {
      const response = await fetch(this.getCartEndpoint('/change.js'), {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
      if (!response.ok) throw new Error('Could not update cart');
      const newCart = await response.json();
      this.pendingCartRemovals.delete(id);
      this.cartLastFetchedAt = Date.now();
      this.renderCart(newCart, { cartConfirmed: true });
      return newCart;
    };
    const queuedChange = this.cartChangeQueue.then(runChange, runChange);
    this.cartChangeQueue = queuedChange.catch(() => {});
    try {
      return await queuedChange;
    } catch (error) {
      finishCartMutation?.(error);
      this.pendingCartRemovals.delete(id);
      if (this.latestCart) this.renderCart(this.latestCart);
      if (this.cartDrawerBody) {
        this.cartDrawerBody.insertAdjacentHTML('afterbegin', '<p class="cart-drawer-empty">Could not update cart. Please try again.</p>');
      }
      throw error;
    } finally {
      finishCartMutation?.();
      this.cartActiveChanges = Math.max(0, this.cartActiveChanges - 1);
      if (this.cartActiveChanges === 0) {
        this.cartDrawer?.classList.remove('is-updating');
        // The mutation response already contains the confirmed full cart.
      }
    }
  }
  async loadAndRenderCart() {
    if (!this.cartDrawerBody) return;
    const requestId = ++this.cartLoadRequestId;
    let mutationRevision;
    // Keep existing rows available while refreshing so the shopper can keep
    // scrolling. Only an initial empty surface needs a loading placeholder.
    if (!this.cartDrawerBody.querySelector('[data-cart-item]')) {
      this.cartDrawerBody.innerHTML = '<p class="cart-drawer-loading">Loading cart...</p>';
    }
    if (this.cartDrawerFooter) this.cartDrawerFooter.hidden = false;
    try {
      await window.HalfpipedCartMutations?.whenIdle();
      mutationRevision = window.HalfpipedCartMutations?.getRevision();
      const cart = await this.fetchCart();
      if (requestId !== this.cartLoadRequestId || mutationRevision !== window.HalfpipedCartMutations?.getRevision()) return;
      this.cartLastFetchedAt = Date.now();
      this.renderCart(cart, { cartConfirmed: true });
    } catch (error) {
      if (requestId !== this.cartLoadRequestId || mutationRevision !== window.HalfpipedCartMutations?.getRevision()) return;
      this.cartDrawerBody.querySelector('.cart-drawer-loading')?.remove();
      this.showCartDrawerNotice('Could not load cart. You can still view your cart page.');
      if (this.cartDrawerFooter) this.cartDrawerFooter.hidden = false;
    }
  }
  lockCartDrawerScroll() {
    if (this.cartDrawerScrollLocked) return;
    this.lockPageScroll({ fixedBody: true });
    this.cartDrawerScrollLocked = true;
  }
  unlockCartDrawerScroll() {
    if (!this.cartDrawerScrollLocked) return;
    this.cartDrawerScrollLocked = false;
    this.unlockPageScroll();
  }
  async refreshCartAfterAdd({ signal, work, recoverFailureThroughRevision } = {}) {
    // Refresh without reopening a drawer the customer dismissed.
    try {
      const mutations = window.HalfpipedCartMutations;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        await mutations?.whenIdle({ signal, recoverFailureThroughRevision });
        const revision = mutations?.getRevision();
        const cart = await this.fetchCart(signal);
        if (signal?.aborted || (work && !this.isCurrentBoardAdd(work))) throw this.boardAddAbortError();
        if (revision !== mutations?.getRevision() || mutations?.isPending()) continue;
        if (!this.publishBoardAddCart(cart)) continue;
        return work ? { cart, revision } : cart;
      }
      throw new Error('The cart is still changing. Please try again.');
    } catch (error) {
      if (work || signal?.aborted) throw error;
      if (this.cartDrawer) {
        if (this.latestCart) this.renderCart(this.latestCart);
        else if (this.cartDrawerBody) this.cartDrawerBody.replaceChildren();
        this.showCartDrawerNotice('Could not refresh your cart. Please reopen it to try again.');
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { notice: 'Could not refresh your cart. Please reopen it to try again.' } }));
      }
    }
  }
  async openCartDrawer({ load = true, userInitiated = false, force = false, showLoading = true, revealBundleId = null } = {}) {
    if (!this.cartDrawer) {
      // Show the drawer immediately. Only this owner fetches the post-add cart;
      // the header and checkout controller share the confirmed result.
      this.openLiveHeaderCartDrawer({ load: false, showLoading, force, revealBundleId });
      if (load) await this.refreshCartAfterAdd();
      return;
    }
    if (!force && performance.now() < this.cartDrawerSuppressOpenUntil) return;
    const revealAddedBundle = () => {
      if (revealBundleId) window.HalfpipedCartDrawerDom?.reveal?.({
        drawer: this.cartDrawer, body: this.cartDrawerBody, bundleId: revealBundleId
      });
    };
    if (this.cartDrawerIsOpenOrOpening || this.cartDrawer.classList.contains('is-open')) {
      revealAddedBundle();
      if (load) await this.loadAndRenderCart();
      return;
    }
    this.cartDrawerIsOpenOrOpening = true;
    this.lastFocusedElement = document.activeElement;
    this.cartDrawerSuppressCloseUntil = performance.now() + 650;
    window.clearTimeout(this.cartDrawerCloseTimer);
    this.cartDrawer.classList.remove('is-closing');
    this.cartDrawer.classList.add('is-open');
    document.body.classList.add('hp-cart-drawer-open');
    this.cartDrawer.setAttribute('aria-hidden', 'false');
    this.lockCartDrawerScroll();
    this.resetCartBodyScroll();
    this.cartDrawerPanel?.focus({ preventScroll: true });
    if (this.cartDrawerFooter) this.cartDrawerFooter.hidden = false;
    if (!load && showLoading && this.cartDrawerBody && !this.boardAddWork?.presentationItems?.length
      && !this.cartDrawerBody.querySelector('[data-cart-item]')) {
      this.cartDrawerBody.innerHTML = '<p class="cart-drawer-loading">Adding to cart...</p>';
    }
    revealAddedBundle();
    if (load) await this.loadAndRenderCart();
  }
  closeCartDrawer({ restoreFocus = true } = {}) {
    if (!this.cartDrawer) return;
    if (!this.cartDrawerIsOpenOrOpening && !this.cartDrawer.classList.contains('is-open')) return;
    this.cartDrawerIsOpenOrOpening = false;
    this.cartDrawerSuppressOpenUntil = performance.now() + 450;
    this.cartLoadRequestId += 1;
    this.cartRefreshAfterChangeId += 1;
    this.closeCartClearConfirm({ restoreFocus: false });
    const focusTarget = this.lastFocusedElement;
    this.lastFocusedElement = null;
    window.clearTimeout(this.cartDrawerCloseTimer);
    this.cartDrawer.classList.remove('is-open');
    this.cartDrawer.classList.add('is-closing');
    document.body.classList.remove('hp-cart-drawer-open');
    this.cartDrawer.setAttribute('aria-hidden', 'true');
    if (this.latestCart) this.updateCartChrome(this.getOptimisticCart(this.latestCart));
    this.cartDrawerItemOrder = [];
    this.unlockCartDrawerScroll();
    this.cartDrawerCloseTimer = window.setTimeout(() => {
      this.cartDrawer?.classList.remove('is-closing');
    }, 180);
    if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  }
  renderProductCardPrice(card, type, item = null) {
    const priceEl = card?.querySelector?.('.bc-product-card__price');
    if (!priceEl) return;
    const cardPriceData = this.getProductCardPriceData(card);
    const rawCents = cardPriceData.variantPriceCents ?? this.parsePriceCents(item?.variantPriceCents);
    const priceState = this.getBoardPromotionPriceState(type, rawCents);
    if (priceState.rawCents === null) {
      card.classList.remove('has-discount-price');
      priceEl.classList.add('is-unavailable');
      priceEl.textContent = 'Unavailable';
      priceEl.setAttribute('aria-label', 'Price unavailable');
      return;
    }
    const originalPrice = this.formatSummaryMoney(priceState.rawCents);
    const discountedPrice = this.formatSummaryMoney(priceState.finalCents);
    const hasDiscount = priceState.finalCents < priceState.rawCents;
    card.classList.toggle('has-discount-price', hasDiscount);
    priceEl.classList.remove('is-unavailable');
    if (!hasDiscount) {
      priceEl.innerHTML = `<span class="bc-product-card__price-current">${this.escapeHtml(originalPrice || '')}</span>`;
      priceEl.setAttribute('aria-label', `Price ${originalPrice}`);
      return;
    }
    priceEl.innerHTML = `<s class="bc-product-card__price-original">${this.escapeHtml(originalPrice || '')}</s><span class="bc-product-card__price-discounted">${this.escapeHtml(discountedPrice || '')}</span>`;
    priceEl.setAttribute('aria-label', `Original price ${originalPrice}, discounted price ${discountedPrice}`);
  }
  hydrateProductCardDisplayNames(grid) {
    if (!grid) return;
    grid.querySelectorAll('.bc-product-card').forEach(card => {
      const type = card.getAttribute('data-type');
      const item = this.getProductCardItem(card);
      const displayName = this.getItemDisplayName(item);
      if (displayName) {
        card.setAttribute('data-display-title', displayName);
        const nameEl = card.querySelector('.bc-product-card__name');
        if (nameEl) nameEl.textContent = displayName;
      }
      this.renderProductCardPrice(card, type, item);
      const sizeLabel = this.getProductCardSizeLabel(item, type);
      let sizeEl = card.querySelector('.bc-product-card__size');
      card.classList.toggle('has-size-label', Boolean(sizeLabel));
      if (!sizeLabel) {
        if (sizeEl) sizeEl.remove();
        return;
      }
      if (!sizeEl) {
        sizeEl = document.createElement('span');
        sizeEl.className = 'bc-product-card__size';
        const meta = card.querySelector('.bc-product-card__meta');
        const price = card.querySelector('.bc-product-card__price');
        if (meta) {
          meta.insertBefore(sizeEl, price || null);
        }
      }
      sizeEl.textContent = sizeLabel;
    });
  }
  getProductCardSizeLabel(item, type) {
    if (!item) return '';
    if (type === 'deck') {
      const width =
        this.normalizeWidthValue(item?.specs?.width) ||
        this.normalizeWidthValue(item?.selectedSize) ||
        this.extractWidthFromName(item?.variantTitle, false) ||
        this.extractWidthFromName(item?.name, false);
      return width ? this.formatSpecValue('width', width) : '';
    }
    if (type === 'truck') {
      const width =
        this.normalizeWidthValue(item?.specs?.width) ||
        this.normalizeWidthValue(item?.selectedSize) ||
        this.extractTruckWidthFromName(item?.variantTitle) ||
        this.extractTruckWidthFromName(item?.name);
      return width ? this.formatSpecValue('width', width) : '';
    }
    if (type === 'wheel') {
      const diameter =
        this.normalizeWheelDiameter(item?.specs?.diameter) ||
        this.normalizeWheelDiameter(item?.variantTitle) ||
        this.normalizeWheelDiameter(item?.name);
      const hardness =
        this.normalizeWheelHardness(item?.specs?.hardness) ||
        this.normalizeWheelHardness(item?.variantTitle) ||
        this.normalizeWheelHardness(item?.name);
      return [diameter, hardness].filter(Boolean).join(' / ');
    }
    return '';
  }
  initDrawerCatalog() {
    this.drawerCatalogStates ||= new Map();
    this.catalogUnverifiedIds ||= new Set();
    this.catalogMembershipRevision ||= 0;
    this.drawerCatalogGeneration ||= 0;
    for (const drawer of this.querySelectorAll('.bc-drawer[data-catalog-type]')) {
      const type = drawer.getAttribute('data-catalog-type');
      if (this.drawerCatalogStates.has(type)) continue;
      try {
        const products = JSON.parse(drawer.getAttribute('data-catalog-products') || '[]');
        const directProducts = JSON.parse(drawer.getAttribute('data-catalog-direct-products') || '[]');
        this.validateCatalogProducts(products); this.validateCatalogProducts(directProducts);
        const page = Number(drawer.getAttribute('data-catalog-page'));
        const pages = Number(drawer.getAttribute('data-catalog-pages'));
        const total = Number(drawer.getAttribute('data-catalog-total-products'));
        const complete = page === 1 && pages === 1 && total === products.length;
        this.drawerCatalogStates.set(type, { products, directProducts, complete, scope: this.getDrawerCatalogScope(type), checkedAt: complete ? Date.now() : 0, error: '', promise: null, controller: null });
        drawer.querySelectorAll('.bc-product-card').forEach(card => { card._bcCatalogSource = this.catalogCardSignature(card); });
      } catch { /* Missing or malformed initial metadata cannot authorize removal. */ }
    }
  }
  validateCatalogProducts(products) {
    if (!Array.isArray(products) || products.length > 5000) throw new Error('Invalid catalog membership.');
    const ids = new Set(), handles = new Set(), variants = new Set();
    for (const product of products) {
      if (!product || !/^[1-9]\d*$/.test(product.id) || typeof product.id !== 'string' || ids.has(product.id)
        || typeof product.handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(product.handle) || handles.has(product.handle)
        || typeof product.title !== 'string' || typeof product.eligible !== 'boolean'
        || !Array.isArray(product.variantIds) || !Number.isInteger(product.variantCount) || product.variantCount !== product.variantIds.length || product.variantIds.length > 2000
        || product.variantIds.some(id => typeof id !== 'string' || !/^[1-9]\d*$/.test(id) || variants.has(id))) throw new Error('Invalid catalog membership.');
      ids.add(product.id); handles.add(product.handle);
      for (const id of product.variantIds) { if (variants.has(id)) throw new Error('Duplicate catalog variant.'); variants.add(id); }
    }
  }
  suspendDrawerCatalog() {
    this.drawerCatalogGeneration = (this.drawerCatalogGeneration || 0) + 1;
    this.drawerCatalogStates?.forEach(state => { if (state.controller) state.checkedAt = 0; state.controller?.abort(); state.controller = null; state.promise = null; });
  }
  getDrawerCatalogScope(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    return JSON.stringify([window.location.origin, ...['data-catalog-url', 'data-catalog-profile', 'data-catalog-handle', 'data-catalog-currency'].map(key => drawer?.getAttribute(key) || '')]);
  }
  getDrawerCatalogState(type) {
    return this.drawerCatalogStates?.get(type);
  }
  isBuilderVariantEligible(type, variantId) {
    const state = this.getDrawerCatalogState(type);
    if (!state?.complete || state.scope !== this.getDrawerCatalogScope(type)) return true;
    return [...state.products, ...state.directProducts.filter(product => !state.products.some(source => source.id === product.id && !source.eligible))].some(product => product.eligible
      && !(type === 'grip' && /transparent/i.test(product.title)) && product.variantIds.includes(String(variantId)));
  }
  isBuilderItemEligible(type, item) {
    if (!item || item.isPlaceholder) return false;
    if (type === 'grip' && /transparent/i.test(String(item.productTitle || item.name || item.title || ''))) return false;
    return this.isBuilderVariantEligible(type, item.variantId);
  }
  formatIneligibleBuilderParts(parts) {
    return `These selections are no longer offered in the board customiser. Please choose replacements for:\n\n${parts.map(part => `• ${part.label}: ${part.name}${part.size ? ` — ${this.formatSpecValue('width', part.size)}` : ''}`).join('\n')}`;
  }
  clearIneligibleBuilderSelections(type = null) {
    const details = this.getSelectedPartDetails().filter(part => (!type || part.component === type)
      && (!this.isBuilderVariantEligible(part.component, part.variantId) || (part.component === 'grip' && /transparent/i.test(part.name))));
    const attempt = this.boardAddAttempt;
    if (attempt?.status === 'new') {
      const parts = (attempt.selectionNames || []).filter(part => (!type || part.component === type)
        && (!this.isBuilderVariantEligible(part.component, part.variantId) || (part.component === 'grip' && /transparent/i.test(part.name))));
      if (parts.length) { attempt.catalogInvalidated = true; attempt.unavailableMessage = this.formatIneligibleBuilderParts(parts); }
    }
    if (!details.length) return false;
    this.clearSelectedBuilderParts(details);
    this.catalogSelectionMessage = this.formatIneligibleBuilderParts(details);
    this.showCatalogSelectionPopup(this.catalogSelectionMessage);
    return true;
  }
  clearSelectedBuilderParts(details) {
    const fields = { deck: 'selectedDeck', grip: 'selectedGrip', wheel: 'selectedWheel', bearing: 'selectedBearing', truck: 'selectedTruck', hardware: 'selectedHardware', skate_tool: 'selectedSkateTool', assembly: 'selectedAssembly' };
    for (const part of details) {
      this[fields[part.component]] = this.createEmptySelection(part.component);
      this.removeUrlParam(part.component === 'assembly' ? 'assembly_item' : part.component);
      if (part.component === 'hardware') this.removeUrlParam('bolt');
      if (part.component === 'assembly') { this.setAssemblyChoiceValue(''); this.removeUrlParam('assembly'); }
    }
    this.updateDeckTexture?.(this.selectedDeck?.texture);
    this.updateGripTexture?.(this.selectedGrip?.texture); this.updateGripColor?.(this.selectedGrip?.color);
    this.updateWheelTexture?.(this.selectedWheel?.texture); this.updateBearingColor?.(this.selectedBearing?.color);
    this.updateTruckColor?.(this.selectedTruck?.color, this.selectedTruck?.baseplateColor, this.selectedTruck);
    this.updateHardwareColors?.(this.selectedHardware);
    this.renderUI();
  }
  getDrawerCatalogUrl(type, page = 1) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const raw = drawer?.getAttribute('data-catalog-url');
    if (!raw) return null;
    const profile = drawer.getAttribute('data-catalog-profile'), handle = drawer.getAttribute('data-catalog-handle');
    const url = new URL(raw, window.location.origin);
    if (!['original', 'revamp'].includes(profile) || !['deck','grip','wheel','bearing','truck','hardware','skate_tool','assembly'].includes(type)
      || url.origin !== window.location.origin || url.username || url.password || url.hash || !url.pathname.endsWith(`/collections/${handle}`)) throw new Error('Invalid collection URL.');
    url.search = '';
    url.searchParams.set('view', `hp-board-catalog-${profile}-${type}`);
    url.searchParams.set('page', String(page));
    return url;
  }
  resolveCatalogFeedDefaults(value, html = false) {
    const tokens = { __HP_BC_DEFAULT_DECK__: this.defaultDeckUrl, __HP_BC_DEFAULT_GRIP__: this.defaultGripUrl, __HP_BC_DEFAULT_WHEEL__: this.defaultWheelUrl,
      __HP_BC_DEFAULT_GRIP_COLOR__: this.defaultGripColor, __HP_BC_DEFAULT_TRUCK_COLOR__: this.defaultTruckColor,
      __HP_BC_DEFAULT_BEARING_COLOR__: this.defaultBearingColor, __HP_BC_DEFAULT_HARDWARE_COLOR__: this.defaultHardwareColor,
      __HP_BC_DEFAULT_BOLT_COLOR__: this.defaultBoltColor };
    if (typeof value === 'string') {
      if (html) return value.replace(/__HP_BC_DEFAULT_[A-Z_]+__/g, token => {
        if (!Object.prototype.hasOwnProperty.call(tokens, token)) throw new Error('Unknown catalog default.');
        return this.escapeHtml(String(tokens[token] || ''));
      });
      return Object.prototype.hasOwnProperty.call(tokens, value) ? tokens[value] || '' : value;
    }
    if (Array.isArray(value)) return value.map(item => this.resolveCatalogFeedDefaults(item));
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, this.resolveCatalogFeedDefaults(item)]));
    return value;
  }
  validateDrawerCatalogItems(type, profile, items, cards, products) {
    const uids = new Set();
    const validPrice = item => {
      if (typeof item.price !== 'string') return false;
      for (const key of ['displayPriceCents', 'variantPriceCents']) {
        if ((profile === 'revamp' || Object.prototype.hasOwnProperty.call(item, key)) && (!Number.isSafeInteger(item[key]) || item[key] < 0)) return false;
      }
      return true;
    };
    for (const item of items) {
      if (uids.has(item.uid) || !validPrice(item) || typeof item.available !== 'boolean'
        || (item.specs != null && (typeof item.specs !== 'object' || Array.isArray(item.specs)))) throw new Error('Invalid catalog metadata.');
      uids.add(item.uid);
    }
    if (['skate_tool', 'assembly'].includes(type) && !items.length) return;
    for (const product of products.filter(product => product.eligible && product.variantIds.length && !(type === 'grip' && /transparent/i.test(product.title)))) {
      const productCards = cards.filter(card => card.getAttribute('data-product-id') === product.id);
      if (['deck', 'truck', 'hardware'].includes(type)
        && (productCards.length !== product.variantIds.length || product.variantIds.some(id => !productCards.some(card => card.getAttribute('data-variant-id') === id)))) throw new Error('Incomplete variant cards.');
      if (type === 'deck') {
        const item = items.find(item => item.uid === product.handle);
        if (!item || !Array.isArray(item.deckVariants) || item.deckVariants.length !== product.variantIds.length) throw new Error('Incomplete deck metadata.');
        const ids = new Set();
        for (const variant of item.deckVariants) {
          const id = String(variant?.variantId || '');
          if (!variant || ids.has(id) || !product.variantIds.includes(id) || typeof variant.available !== 'boolean' || !validPrice(variant)
            || (variant.options != null && (!Array.isArray(variant.options) || variant.options.some(option => typeof option !== 'string')))) throw new Error('Invalid deck variant.');
          ids.add(id);
        }
      } else if (productCards.some(card => !items.some(item => item.uid === card.getAttribute('data-uid') && String(item.variantId) === card.getAttribute('data-variant-id')))) {
        throw new Error('Incomplete catalog metadata.');
      }
    }
  }
  parseDrawerCatalogPage(type, result, expectedPage) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    if (!result || result.schema !== 1 || result.type !== type || result.profile !== drawer.getAttribute('data-catalog-profile')
      || result.handle !== drawer.getAttribute('data-catalog-handle') || result.currency !== drawer.getAttribute('data-catalog-currency')
      || result.page !== expectedPage || !Number.isInteger(result.pages) || result.pages < 1 || result.pages > 20
      || !Number.isInteger(result.totalProducts) || result.totalProducts < 0 || result.totalProducts > 5000
      || result.pages !== Math.max(1, Math.ceil(result.totalProducts / 250))
      || !Array.isArray(result.productIds) || !Array.isArray(result.items) || typeof result.html !== 'string'
      || result.items.length > 5000) throw new Error('Invalid collection response.');
    this.validateCatalogProducts(result.products);
    if (result.products.length !== Math.min(250, Math.max(0, result.totalProducts - (expectedPage - 1) * 250))
      || JSON.stringify(result.productIds) !== JSON.stringify(result.products.map(product => product.id))) throw new Error('Incomplete collection response.');
    const items = this.resolveCatalogFeedDefaults(result.items);
    if (items.some(item => !item || typeof item.uid !== 'string' || !/^[1-9]\d*$/.test(String(item.variantId))
      || !result.products.some(product => product.eligible && product.variantIds.includes(String(item.variantId))))) throw new Error('Invalid catalog item.');
    const template = document.createElement('template');
    template.innerHTML = this.resolveCatalogFeedDefaults(result.html, true);
    const cards = [...template.content.children];
    const keys = new Set();
    for (const card of cards) {
      const key = `${card.getAttribute('data-uid')}:${card.getAttribute('data-variant-id')}`;
      const product = result.products.find(row => row.id === card.getAttribute('data-product-id'));
      if (card.tagName !== 'BUTTON' || !card.classList.contains('bc-product-card') || card.getAttribute('data-type') !== type
        || !card.getAttribute('data-uid') || keys.has(key) || !product?.eligible
        || !product.variantIds.includes(card.getAttribute('data-variant-id')) || card.getAttribute('data-product-handle') !== product.handle
        || card.querySelector('script,iframe,object,embed,link,style')
        || [card, ...card.querySelectorAll('*')].some(node => [...node.attributes].some(attr => /^on/i.test(attr.name)))) throw new Error('Invalid catalog card.');
      keys.add(key);
    }
    // A valid empty collection is distinct from truncated or missing markup.
    const eligible = result.products.filter(product => product.eligible && !(type === 'grip' && /transparent/i.test(product.title)) && product.variantIds.length);
    if (eligible.some(product => !cards.some(card => card.getAttribute('data-product-id') === product.id))) throw new Error('Incomplete catalog cards.');
    this.validateDrawerCatalogItems(type, result.profile, items, cards, result.products);
    return { ...result, items, cards: cards.filter(card => !(type === 'grip' && /transparent/i.test(result.products.find(product => product.id === card.getAttribute('data-product-id'))?.title || ''))) };
  }
  async refreshDrawerCatalog(type, { force = false } = {}) {
    const state = this.getDrawerCatalogState(type);
    if (!state || this.catalogSuspended || this.isConnected === false) return false;
    if (state.promise) return state.promise;
    const scope = this.getDrawerCatalogScope(type);
    if (!force && state.complete && state.scope === scope && Date.now() - state.checkedAt < 30000) return true;
    let firstUrl;
    try { firstUrl = this.getDrawerCatalogUrl(type); } catch { return false; }
    if (!firstUrl) return true;
    const generation = this.drawerCatalogGeneration || 0;
    const controller = new AbortController(); state.controller = controller; state.error = '';
    const current = () => scope === this.getDrawerCatalogScope(type) && !controller.signal.aborted && generation === this.drawerCatalogGeneration && state.controller === controller && !this.catalogSuspended && this.isConnected !== false;
    const cancelled = new Promise((resolve, reject) => controller.signal.addEventListener('abort', () => reject(new Error('Collection refresh cancelled.')), { once: true }));
    const task = async () => {
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      try {
        const pages = []; let totalBytes = 0;
        for (let page = 1; page <= (pages[0]?.pages || 1); page += 1) {
          const response = await Promise.race([window.fetch(this.getDrawerCatalogUrl(type, page).toString(), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }, signal: controller.signal }), cancelled]);
          if (!response.ok) throw new Error('Collection unavailable.');
          const text = await Promise.race([response.text(), cancelled]);
          totalBytes += text.length;
          if (text.length > 3000000 || totalBytes > 8000000) throw new Error('Collection response too large.');
          const parsed = this.parseDrawerCatalogPage(type, JSON.parse(text), page);
          if (pages.length && (parsed.pages !== pages[0].pages || parsed.totalProducts !== pages[0].totalProducts)) throw new Error('Collection changed while loading.');
          pages.push(parsed);
        }
        const snapshot = { ...pages[0], products: pages.flatMap(page => page.products), items: pages.flatMap(page => page.items), cards: pages.flatMap(page => page.cards) };
        this.validateCatalogProducts(snapshot.products);
        if (snapshot.products.length !== snapshot.totalProducts) throw new Error('Incomplete collection.');
        if (!current()) return false;
        this.applyDrawerCatalog(type, snapshot);
        state.scope = scope; state.checkedAt = Date.now(); state.error = '';
        return true;
      } catch {
        if (current()) { state.checkedAt = 0; state.error = 'Options could not be refreshed. Your choices are unchanged. Please retry.'; }
        controller.abort();
        return false;
      } finally {
        window.clearTimeout(timeout);
        if (state.controller === controller) { state.controller = null; this.updateCatalogAvailabilityFeedback(); }
      }
    };
    const promise = task(); state.promise = promise;
    try { return await promise; } finally { if (state.promise === promise) state.promise = null; }
  }
  async refreshDrawerOptions(type, { reason = 'drawer-open', force = false } = {}) {
    const revision = this.catalogMembershipRevision || 0;
    const generation = this.drawerCatalogGeneration || 0;
    const stock = this.refreshCatalogAvailability({ type, reason });
    const membership = await this.refreshDrawerCatalog(type, { force });
    if (generation !== this.drawerCatalogGeneration || this.catalogSuspended || this.isConnected === false) return false;
    const available = revision !== (this.catalogMembershipRevision || 0)
      ? await this.refreshCatalogAvailability({ type, reason: 'catalog-membership' }) : await stock;
    return membership && available;
  }
  catalogCardSignature(card) {
    const ignored = new Set(['disabled', 'aria-disabled', 'aria-pressed', 'data-catalog-unavailable', 'data-catalog-available', 'data-direct-product', 'data-sort-index']);
    const describe = node => node.nodeType === 3 ? node.textContent.replace(/\s+/g, ' ').trim() : node.nodeType !== 1 ? '' : [node.tagName,
      [...node.attributes].filter(attr => !ignored.has(attr.name)).map(attr => [attr.name, attr.value]).sort(([a],[b]) => a.localeCompare(b)),
      [...node.childNodes].map(describe).filter(value => value !== '')];
    return JSON.stringify(describe(card));
  }
  applyDrawerCatalog(type, snapshot) {
    const drawer = this.querySelector(`#bc-drawer-${type}`), grid = drawer?.querySelector('.bc-drawer__grid');
    const state = this.getDrawerCatalogState(type);
    if (!state || !grid) throw new Error('Missing catalog drawer.');
    const products = snapshot.products.map(product => ({ ...product, eligible: product.eligible && !(type === 'grip' && /transparent/i.test(product.title)) }));
    const knownIds = new Set(this.getCatalogVariantIds());
    const existing = [...grid.querySelectorAll('.bc-product-card')];
    const keyOf = card => `${card.getAttribute('data-uid')}:${card.getAttribute('data-variant-id')}`;
    const byKey = new Map(existing.map(card => [keyOf(card), card]));
    const directIds = new Set(state.directProducts.filter(product => product.eligible && !products.some(source => source.id === product.id && !source.eligible)).map(product => product.id));
    const desired = snapshot.cards.map(card => {
      if (directIds.has(card.getAttribute('data-product-id'))) card.setAttribute('data-direct-product', 'true');
      return card;
    });
    for (const card of existing) if (card.hasAttribute('data-direct-product') && directIds.has(card.getAttribute('data-product-id'))
      && !desired.some(candidate => candidate.getAttribute('data-product-id') === card.getAttribute('data-product-id'))) desired.push(card);
    const normalized = this.enrichFilterItems(snapshot.items.filter(item => type !== 'grip' || !/transparent/i.test(String(item.productTitle || item.name || item.title || ''))), type);
    const field = { deck: 'decks', grip: 'grips', wheel: 'wheels', bearing: 'bearings', truck: 'trucks', hardware: 'hardware' }[type];
    const currentItems = this.getCatalogItems(type);
    const itemsChanged = field && JSON.stringify(currentItems) !== JSON.stringify(normalized);
    const membershipChanged = !state.complete || JSON.stringify(state.products) !== JSON.stringify(products);
    let cardsChanged = existing.length !== desired.length;
    const order = desired.map((card, index) => {
      const old = byKey.get(keyOf(card));
      const signature = card === old ? old._bcCatalogSource : this.catalogCardSignature(card);
      if (old && old._bcCatalogSource === signature) {
        if (Number(old.getAttribute('data-sort-index')) !== index) cardsChanged = true;
        return { card: old, index, changed: false };
      }
      card._bcCatalogSource = signature; cardsChanged = true;
      return { card, old, index, changed: true };
    });
    state.products = products; state.complete = true; state.scope = this.getDrawerCatalogScope(type);
    if (!membershipChanged && !itemsChanged && !cardsChanged) return false;
    this.catalogMembershipRevision = (this.catalogMembershipRevision || 0) + 1;
    // Membership can supersede an earlier healthy stock response. New variants
    // remain unselectable until a later authoritative stock check succeeds.
    this.suspendCatalogAvailability({ preservePending: true }); this.catalogSuspended = false;
    this.catalogUnverifiedIds ||= new Set();
    desired.forEach(card => { const id = card.getAttribute('data-variant-id'); if (!knownIds.has(id)) this.catalogUnverifiedIds.add(id); });
    normalized.forEach(item => [item, ...(item.deckVariants || [])].forEach(variant => { const id = String(variant.variantId); if (!knownIds.has(id)) this.catalogUnverifiedIds.add(id); }));
    if (itemsChanged) this[field] = normalized;
    const scroll = drawer.querySelector('.bc-drawer__body'); const scrollTop = scroll?.scrollTop || 0;
    const viewport = scroll?.getBoundingClientRect();
    const anchor = viewport && order.find(entry => !entry.changed && entry.card.isConnected && (() => { const rect = entry.card.getBoundingClientRect(); return rect.height > 0 && rect.bottom > viewport.top && rect.top < viewport.bottom; })())?.card;
    const anchorOffset = anchor ? anchor.getBoundingClientRect().top - viewport.top : null;
    const active = document.activeElement; const focusKey = active?.closest?.('.bc-product-card') && keyOf(active.closest('.bc-product-card'));
    if (cardsChanged) {
      this.clearHoveredProductCard(); this.cancelDrawerTextureWarmup(type);
      const keep = new Set(order.map(entry => entry.card));
      existing.filter(card => !keep.has(card)).forEach(card => card.remove());
      let cursor = grid.firstElementChild;
      for (const entry of order) {
        const card = entry.card;
        if (card.getAttribute('data-sort-index') !== String(entry.index)) card.setAttribute('data-sort-index', String(entry.index));
        if (card !== cursor) grid.insertBefore(card, cursor); else cursor = cursor.nextElementSibling;
      }
      this.hydrateProductCardDisplayNames(grid);
    }
    this.refreshDrawerFilterChoices(type);
    this.catalogAvailabilityRevision = (this.catalogAvailabilityRevision || 0) + 1;
    const cleared = this.clearIneligibleBuilderSelections(type);
    if (!cleared && this.refreshSelectedCatalogPresentation(type)) this.renderUI(type);
    this.updateActiveProductCards(type);
    this.updateCatalogAvailabilityUI(type);
    if (cardsChanged && drawer.classList.contains('is-open')) this.primeDrawerImages(type);
    if (scroll) {
      if (anchor?.isConnected && anchor.getBoundingClientRect().height > 0) scroll.scrollTop += anchor.getBoundingClientRect().top - scroll.getBoundingClientRect().top - anchorOffset;
      else scroll.scrollTop = scrollTop;
    }
    if (focusKey && active?.isConnected === false) {
      const replacement = [...grid.querySelectorAll('.bc-product-card')].find(card => keyOf(card) === focusKey);
      if (replacement && !replacement.disabled) replacement.focus({ preventScroll: true });
      else drawer.querySelector('[data-close-drawer]')?.focus({ preventScroll: true });
    }
    return true;
  }
  refreshSelectedCatalogPresentation(type) {
    const field = { deck: 'selectedDeck', grip: 'selectedGrip', wheel: 'selectedWheel', bearing: 'selectedBearing', truck: 'selectedTruck', hardware: 'selectedHardware', skate_tool: 'selectedSkateTool', assembly: 'selectedAssembly' }[type];
    const selected = this[field];
    if (!selected || selected.isPlaceholder || !this.isBuilderItemEligible(type, selected)) return false;
    const variantId = String(selected.variantId);
    let fresh = null;
    if (type === 'deck') {
      const parent = this.decks.find(item => (item.deckVariants || []).some(variant => String(variant.variantId) === variantId));
      const variant = parent?.deckVariants.find(variant => String(variant.variantId) === variantId);
      if (variant) fresh = this.getDeckItemForVariant(parent, { variant, width: this.getDeckVariantWidth(variant, parent) });
    }
    if (!fresh) {
      const card = [...this.querySelectorAll(`.bc-product-card[data-type="${type}"]`)].find(card => card.getAttribute('data-variant-id') === variantId);
      if (card) fresh = this.getProductCardItem(card);
    }
    // A generic product may now display a different first variant. Keep the
    // customer's exact selection until that same variant has fresh metadata.
    if (!fresh || String(fresh.variantId) !== variantId) return false;
    const updated = { ...selected, ...fresh, variantId: selected.variantId };
    if (JSON.stringify(updated) === JSON.stringify(selected)) return false;
    this[field] = updated;
    this.syncSelectedCatalogVisual(type, selected, updated);
    if (updated.uid !== selected.uid) this.updateUrlParam(type === 'assembly' ? 'assembly_item' : type, updated.uid);
    return true;
  }
  syncSelectedCatalogVisual(type, previous, updated) {
    const changed = (...fields) => fields.some(field => String(previous[field] || '') !== String(updated[field] || ''));
    if (type === 'deck' && changed('texture')) this.updateDeckTexture(updated.texture);
    if (type === 'grip') {
      if (changed('texture')) this.updateGripTexture(updated.texture);
      if (changed('color', 'texture')) this.updateGripColor(updated.color);
    }
    if (type === 'wheel' && changed('texture')) this.updateWheelTexture(updated.texture);
    if (type === 'bearing' && changed('color')) this.updateBearingColor(updated.color);
    if (type === 'truck' && changed('color', 'baseplateColor', 'bushingTopColor', 'bushingBottomColor', 'washerTopColor', 'washerBottomColor')) this.updateTruckColor(updated.color, updated.baseplateColor, updated);
    if (type === 'hardware' && changed('color', 'axleNutColor', 'kingpinNutColor')) this.updateHardwareColors(updated);
  }
  captureDrawerViewport(type, excludedIds = new Set()) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const scroll = drawer?.querySelector('.bc-drawer__body');
    if (!scroll || (!drawer.classList.contains('is-open') && !drawer.classList.contains('is-opening'))) return null;
    const viewport = scroll.getBoundingClientRect();
    const anchor = [...drawer.querySelectorAll('.bc-product-card')].find(card => {
      if (excludedIds.has(card.getAttribute('data-variant-id'))) return false;
      const rect = card.getBoundingClientRect();
      return rect.height > 0 && rect.bottom > viewport.top && rect.top < viewport.bottom;
    });
    return { scroll, scrollTop: scroll.scrollTop, anchor, offset: anchor ? anchor.getBoundingClientRect().top - viewport.top : null };
  }
  restoreDrawerViewport(snapshot) {
    if (!snapshot?.scroll?.isConnected) return;
    const { scroll, anchor, offset, scrollTop } = snapshot;
    if (anchor?.isConnected && anchor.getBoundingClientRect().height > 0) scroll.scrollTop += anchor.getBoundingClientRect().top - scroll.getBoundingClientRect().top - offset;
    else scroll.scrollTop = scrollTop;
  }
  refreshDrawerFilterChoices(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`), panel = drawer?.querySelector('[data-filter-panel]');
    if (!panel) return;
    for (const group of this.getFilterConfig(type)) {
      const values = this.sortSpecValues(group.field, [...new Set(this.getCatalogItems(type).flatMap(item => this.getItemFilterValues(item, group.field, type)).filter(Boolean))]);
      let select = panel.querySelector(`[data-filter-field="${group.field}"]`);
      if (!select && values.length > 1) {
        const container = document.createElement('div'); container.className = 'bc-filter-group'; container.setAttribute('data-filter-group', group.field);
        const label = document.createElement('label'); label.className = 'bc-filter-label'; label.textContent = group.label;
        const wrap = document.createElement('span'); wrap.className = 'bc-filter-select-wrap'; select = document.createElement('select'); select.className = 'bc-filter-select';
        select.id = `bc-filter-${type}-${group.field}`; label.htmlFor = select.id; select.setAttribute('data-filter-field', group.field); select.setAttribute('aria-label', `${group.label} filter`);
        wrap.append(select); container.append(label, wrap); panel.prepend(container); this.activeDrawerFilters[type][group.field] ||= 'all';
      }
      if (select) {
        const options = [{ value: 'all', label: `All ${group.label}` }, ...values.map(value => ({ value, label: this.formatSpecValue(group.field, value) }))];
        if (JSON.stringify(select._bcFilterOptions) !== JSON.stringify(options)) { select._bcFilterOptions = options; select._bcFilterAvailabilitySignature = null; }
      }
    }
  }
  initCatalogAvailability() {
    this.initDrawerCatalog();
    this.catalogAvailability ||= new Map();
    this.catalogInitialAvailability ||= new Map();
    this.catalogRequestGeneration ||= 0;
    this.catalogAvailabilityRevision ||= 0;
    this.catalogPendingIds ||= new Set();
    this.catalogSuspended = false;
    const remember = item => {
      const id = String(item?.variantId || '');
      if (/^[1-9]\d*$/.test(id) && typeof item.available === 'boolean') this.catalogInitialAvailability.set(id, item.available);
    };
    for (const type of ['deck', 'grip', 'wheel', 'bearing', 'truck', 'hardware']) {
      for (const item of this.getCatalogItems(type)) {
        remember(item);
        (item.deckVariants || []).forEach(remember);
      }
    }
    this.querySelectorAll('.bc-product-card[data-variant-id]').forEach(card => {
      const value = card.getAttribute('data-catalog-available');
      if (value === 'true' || value === 'false') this.catalogInitialAvailability.set(String(card.getAttribute('data-variant-id')), value === 'true');
    });
    const assembly = this.querySelector('#bc-assembly');
    if (assembly?.getAttribute('data-catalog-available')) this.catalogInitialAvailability.set(String(assembly.getAttribute('data-variant-id')), assembly.getAttribute('data-catalog-available') === 'true');
    if (this.catalogLifecycleBound) return;
    this.catalogLifecycleBound = true;
    this.handleCatalogPageHide = () => { this.suspendDrawerCatalog(); this.suspendCatalogAvailability(); };
    this.handleCatalogPageShow = event => {
      if (!event?.persisted && !this.catalogSuspended) return;
      this.catalogSuspended = false;
      void this.refreshCatalogAvailability({ reason: 'return' });
      const type = this.getOpenCollectionDrawer()?.id.replace('bc-drawer-', '');
      if (type) void this.refreshDrawerOptions(type, { reason: 'return' });
    };
    this.handleCatalogVisibility = () => {
      if (document.visibilityState === 'hidden') { this.suspendDrawerCatalog(); this.suspendCatalogAvailability(); }
      else if (document.visibilityState === 'visible') {
        this.catalogSuspended = false;
        void this.refreshCatalogAvailability({ reason: 'visible' });
        const type = this.getOpenCollectionDrawer()?.id.replace('bc-drawer-', '');
        if (type) void this.refreshDrawerOptions(type, { reason: 'visible' });
      }
    };
    window.addEventListener('pagehide', this.handleCatalogPageHide);
    window.addEventListener('pageshow', this.handleCatalogPageShow);
    document.addEventListener('visibilitychange', this.handleCatalogVisibility);
  }
  suspendCatalogAvailability({ preservePending = false } = {}) {
    const pending = preservePending ? new Set([...(this.catalogPendingIds || []), ...(this.catalogCheckingIds || []), ...(this.catalogUnverifiedIds || [])]) : new Set();
    this.catalogSuspended = true;
    if (!preservePending) {
      this.catalogSelectionSyncGeneration = (this.catalogSelectionSyncGeneration || 0) + 1;
      this.catalogSelectionSyncPromise = null;
      this.pendingCatalogSelectionCleanup?.clear();
    }
    this.catalogRequestGeneration = (this.catalogRequestGeneration || 0) + 1;
    this.catalogRequestController?.abort();
    this.catalogRequestController = null;
    this.catalogRefreshPromise = null;
    this.catalogPendingIds = pending;
    this.catalogCheckingIds = null;
    this.catalogAvailabilityChecking = false;
    if (this.catalogLifecycleBound) this.updateCatalogAvailabilityFeedback();
  }
  getCatalogVariantIds(type = null) {
    const ids = new Set();
    const add = value => { if (/^[1-9]\d*$/.test(String(value || ''))) ids.add(String(value)); };
    for (const component of type ? [type] : ['deck', 'grip', 'wheel', 'bearing', 'truck', 'hardware']) {
      this.getCatalogItems(component).forEach(item => { add(item.variantId); (item.deckVariants || []).forEach(variant => add(variant.variantId)); });
    }
    this.querySelectorAll(type ? `.bc-product-card[data-type="${type}"]` : '.bc-product-card').forEach(card => add(card.getAttribute('data-variant-id')));
    this.getSelectedPartDetails().forEach(part => add(part.variantId));
    if (!type || type === 'assembly') add(this.querySelector('#bc-assembly')?.getAttribute('data-variant-id'));
    return [...ids];
  }
  isCatalogVariantAvailable(variantId) {
    const id = String(variantId || '');
    if (!/^[1-9]\d*$/.test(id)) return false;
    if (this.catalogUnverifiedIds?.has(id)) return false;
    return this.catalogAvailability?.get(id)?.available ?? this.catalogInitialAvailability?.get(id) ?? true;
  }
  isCatalogItemAvailable(type, item) {
    if (!this.isBuilderItemEligible(type, item)) return false;
    if (type === 'deck' && Array.isArray(item.deckVariants) && item.deckVariants.length && !item.productUid) {
      return item.deckVariants.some(variant => this.isCatalogVariantAvailable(variant.variantId));
    }
    return this.isCatalogVariantAvailable(item.variantId);
  }
  isCatalogCardAvailable(card, type) {
    if (!this.isBuilderVariantEligible(type, card?.getAttribute('data-variant-id'))) return false;
    if (type === 'grip' && /transparent/i.test(card?.getAttribute('data-product-title') || card?.getAttribute('data-title') || '')) return false;
    if (type === 'deck') {
      const item = this.findCatalogItem(type, card?.getAttribute('data-uid'));
      if (item?.deckVariants?.length && !item.productUid) return this.isCatalogItemAvailable(type, item);
    }
    const id = card?.getAttribute('data-variant-id');
    return id ? this.isCatalogVariantAvailable(id) : this.isCatalogItemAvailable(type, this.getProductCardItem(card));
  }
  getCatalogAvailabilityEndpoint() {
    let configured = window.HalfpipedBoardCheckout?.config?.endpoint;
    if (!configured) {
      try { configured = JSON.parse(document.getElementById('hp-board-checkout-config')?.textContent || '{}').endpoint; } catch { /* The retry feedback handles missing configuration. */ }
    }
    const url = new URL(String(configured || ''), window.location.origin);
    if (!configured || url.origin !== window.location.origin || !url.pathname.startsWith('/apps/') || url.search || url.hash || url.username || url.password) throw new Error('Availability could not be checked. Please retry.');
    return url.toString();
  }
  refreshCatalogAvailability({ type = null, reason = 'drawer-open' } = {}) {
    if (this.catalogRefreshPromise && ['cart-cleanup', 'add-unavailable', 'return', 'visible', 'catalog-membership'].includes(reason)) {
      this.suspendCatalogAvailability({ preservePending: true });
      this.catalogSuspended = false;
    }
    if (this.catalogSuspended || this.isConnected === false) return Promise.resolve(false);
    this.catalogAvailability ||= new Map();
    this.catalogPendingIds ||= new Set();
    const currentIds = new Set(this.getCatalogVariantIds());
    for (const id of this.catalogPendingIds) if (!currentIds.has(id)) this.catalogPendingIds.delete(id);
    for (const id of this.catalogUnverifiedIds || []) if (!currentIds.has(id)) this.catalogUnverifiedIds.delete(id);
    for (const id of [...this.getCatalogVariantIds(type), ...(this.catalogUnverifiedIds || [])]) if (!this.catalogCheckingIds?.has(id)) this.catalogPendingIds.add(id);
    if (this.catalogRefreshPromise) return this.catalogRefreshPromise;
    if (!this.catalogPendingIds.size) {
      this.catalogAvailabilityChecking = false; this.catalogCheckingIds = null; this.catalogAvailabilityError = '';
      this.updateCatalogAvailabilityFeedback();
      return Promise.resolve(true);
    }
    const generation = this.catalogRequestGeneration = (this.catalogRequestGeneration || 0) + 1;
    const controller = new AbortController();
    this.catalogRequestController = controller;
    const membershipRevision = this.catalogMembershipRevision || 0;
    const current = () => membershipRevision === (this.catalogMembershipRevision || 0) && !controller.signal.aborted && generation === this.catalogRequestGeneration && !this.catalogSuspended && this.isConnected !== false;
    this.catalogAvailabilityError = '';
    this.catalogAvailabilityChecking = true;
    this.updateCatalogAvailabilityFeedback();
    const operation = async () => {
      const timeout = window.setTimeout(() => controller.abort(), 12000);
      const cancelled = new Promise((resolve, reject) => controller.signal.addEventListener('abort', () => reject(Object.assign(new Error('Availability could not be checked. Please retry.'), { name: 'AbortError' })), { once: true }));
      try {
        const endpoint = this.getCatalogAvailabilityEndpoint();
        const readyCart = await Promise.race([Promise.resolve(window.HalfpipedBoardIntegrity?.ready).catch(() => null), cancelled]);
        const cartToken = this.latestCart?.token || window.HalfpipedBoardCheckout?.latestCart?.token || readyCart?.token || null;
        while (this.catalogPendingIds.size && current()) {
          const ids = [...this.catalogPendingIds];
          this.catalogPendingIds.clear();
          this.catalogCheckingIds = new Set(ids);
          const batches = [];
          for (let offset = 0; offset < ids.length; offset += 100) batches.push(ids.slice(offset, offset + 100));
          const results = [];
          const observedAt = Date.now();
          let nextBatch = 0;
          const worker = async () => {
            while (nextBatch < batches.length && current()) {
              const variantIds = batches[nextBatch++];
              const response = await Promise.race([window.fetch(endpoint, {
                method: 'POST', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
                headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ version: 1, mode: 'catalog-availability', variantIds, cartToken })
              }), cancelled]);
              const result = await Promise.race([response.json(), cancelled]);
              const rows = result?.variants;
              if (!response.ok || result?.ok !== true || result.mode !== 'catalog-availability' || !Array.isArray(rows) || rows.length !== variantIds.length
                || new Set(rows.map(row => row?.variantId)).size !== variantIds.length
                || rows.some(row => !row || typeof row.variantId !== 'string' || !variantIds.includes(row.variantId) || typeof row.available !== 'boolean'
                  || (row.available ? row.reason !== null : !['VARIANT_NOT_FOUND', 'VARIANT_UNAVAILABLE'].includes(row.reason)))) throw new Error('Availability could not be checked. Please retry.');
              results.push(...rows);
            }
          };
          await Promise.race([Promise.all(Array.from({ length: Math.min(2, batches.length) }, worker)), cancelled]);
          if (!current()) return false;
          // Apply a complete verified sweep atomically; a failed or malformed
          // batch cannot hide products or clear a customer's selections.
          this.applyCatalogAvailability(results, { observedAt });
          this.catalogCheckingIds = null;
        }
        return true;
      } catch (error) {
        if (generation === this.catalogRequestGeneration && !this.catalogSuspended && this.isConnected !== false) this.catalogAvailabilityError = 'Availability could not be checked. Your choices are unchanged. Please retry.';
        controller.abort();
        return false;
      } finally {
        window.clearTimeout(timeout);
        if (generation === this.catalogRequestGeneration) {
          this.catalogAvailabilityChecking = false;
          this.catalogCheckingIds = null;
          this.catalogPendingIds.clear();
          this.updateCatalogAvailabilityFeedback();
        }
      }
    };
    const promise = operation();
    this.catalogRefreshPromise = promise;
    void promise.finally(() => {
      if (this.catalogRefreshPromise === promise) this.catalogRefreshPromise = null;
      if (this.catalogRequestController === controller) this.catalogRequestController = null;
    });
    return promise;
  }
  applyCatalogAvailability(entries, { synchronize = true, notify = true, observedAt = Date.now() } = {}) {
    this.catalogAvailability ||= new Map();
    const changedIds = new Set(entries.filter(entry => this.catalogUnverifiedIds?.has(entry.variantId) || this.isCatalogVariantAvailable(entry.variantId) !== entry.available).map(entry => entry.variantId));
    this.catalogObservedAt ||= new Map();
    for (const entry of entries) {
      this.catalogObservedAt.set(entry.variantId, observedAt);
      this.catalogUnverifiedIds?.delete(entry.variantId);
      this.catalogAvailability.set(entry.variantId, { available: entry.available, reason: entry.reason });
      if (entry.available) {
        this.pendingCatalogSelectionCleanup?.delete(entry.variantId);
        this.catalogSilentRecoveryIds?.delete(entry.variantId);
      }
    }
    this.catalogAvailabilityError = '';
    const types = ['deck', 'grip', 'wheel', 'bearing', 'truck', 'hardware', 'skate_tool', 'assembly'];
    const affectedTypes = changedIds.size ? types.filter(type => this.getCatalogItems(type).some(item => changedIds.has(String(item.variantId)) || (item.deckVariants || []).some(variant => changedIds.has(String(variant.variantId))))
      || [...this.querySelectorAll(`.bc-product-card[data-type="${type}"]`)].some(card => changedIds.has(card.getAttribute('data-variant-id')))
      || this.getSelectedPartDetails().some(part => part.component === type && changedIds.has(part.variantId))) : [];
    const viewports = changedIds.size ? types.map(type => this.captureDrawerViewport(type, changedIds)) : [];
    if (changedIds.size) this.catalogAvailabilityRevision = (this.catalogAvailabilityRevision || 0) + 1;
    for (const type of affectedTypes) this.refreshDrawerFilterChoices(type);
    // Fresh SKU checks and current global-stock cleanup evidence supply this
    // overlay. Allocation failures and historical receipts never blacklist a SKU.
    this.stageUnavailableCatalogSelections(entries.filter(entry => !entry.available).map(entry => entry.variantId), { synchronize, notify });
    for (const type of affectedTypes) this.updateCatalogAvailabilityUI(type);
    this.updateCatalogAvailabilityFeedback();
    viewports.forEach(viewport => this.restoreDrawerViewport(viewport));
  }
  catalogSelectionReference(part) {
    // Catalog hydration replaces item objects when names/prices change. The
    // selected component and exact variant remain the customer's choice.
    return `${part.component}:${part.variantId}`;
  }
  stageUnavailableCatalogSelections(variantIds, { synchronize = true, notify = true } = {}) {
    const checkout = window.HalfpipedBoardCheckout;
    if (synchronize && typeof checkout?.synchronizeCatalogAvailability !== 'function') {
      const silent = variantIds.filter(id => this.catalogSilentRecoveryIds?.has(String(id)));
      this.clearUnavailableCatalogSelections(silent, { notify: false });
      this.clearUnavailableCatalogSelections(variantIds.filter(id => !silent.includes(id)), { notify });
      silent.forEach(id => this.catalogSilentRecoveryIds.delete(String(id)));
      return;
    }
    const unavailable = new Set(variantIds.map(String));
    const details = this.getSelectedPartDetails().filter(part => unavailable.has(part.variantId));
    this.invalidateUnavailableCatalogAdd(unavailable, details);
    this.pendingCatalogSelectionCleanup ||= new Map();
    for (const part of details) {
      const previous = this.pendingCatalogSelectionCleanup.get(part.variantId);
      this.pendingCatalogSelectionCleanup.set(part.variantId, {
        part, reference: this.catalogSelectionReference(part), notify: notify && !this.catalogSilentRecoveryIds?.has(part.variantId) && previous?.notify !== false
      });
    }
    if (synchronize && this.pendingCatalogSelectionCleanup.size) void this.synchronizeCatalogSelectionCleanup();
  }
  flushCatalogSelectionCleanup(cart, { notify = true } = {}) {
    if (!Array.isArray(cart?.items) || this.catalogSuspended || !this.isConnected) return false;
    const remaining = new Set();
    for (const item of cart.items) {
      if (!item.properties?._hp_board_bundle_id) continue;
      remaining.add(String(item.variant_id || item.variantId || item.id || ''));
      for (const [, id] of window.HalfpipedBoardIntegrity?.manifestOf?.(item)?.parts || []) remaining.add(id);
    }
    const selected = this.getSelectedPartDetails();
    const clear = [], messages = [];
    for (const [id, pending] of this.pendingCatalogSelectionCleanup || []) {
      const current = selected.find(part => part.component === pending.part.component && part.variantId === id);
      if (this.catalogAvailability?.get(id)?.available !== false || !current
        || this.catalogSelectionReference(current) !== pending.reference) {
        this.pendingCatalogSelectionCleanup.delete(id);
        continue;
      }
      // A cart read can precede the stock observation. It never authorizes a
      // selection reset while any surviving board still contains that part.
      if (remaining.has(id)) continue;
      clear.push(current);
      this.catalogSilentRecoveryIds?.delete(id);
      if (notify && pending.notify) messages.push(current);
      this.pendingCatalogSelectionCleanup.delete(id);
    }
    if (!clear.length) return false;
    this.clearSelectedBuilderParts(clear);
    this.catalogSelectionMessage = this.formatUnavailableBoardParts(clear);
    if (messages.length) {
      const message = this.formatUnavailableBoardParts(messages);
      const generation = this.catalogSelectionSyncGeneration || 0;
      // Let both surfaces paint before a native dialog can stop the browser.
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        if (this.isConnected && !this.catalogSuspended && generation === (this.catalogSelectionSyncGeneration || 0)) this.showCatalogSelectionPopup(message);
      }));
    }
    return true;
  }
  synchronizeCatalogSelectionCleanup() {
    if (this.catalogSelectionSyncPromise) return this.catalogSelectionSyncPromise;
    const generation = this.catalogSelectionSyncGeneration || 0;
    const current = () => this.isConnected && !this.catalogSuspended && generation === (this.catalogSelectionSyncGeneration || 0);
    const operation = (async () => {
      for (let attempt = 0; attempt < 2 && current(); attempt++) {
        const cart = await window.HalfpipedBoardCheckout.synchronizeCatalogAvailability({ forceFresh: attempt > 0 });
        if (!cart || !current()) return;
        this.flushCatalogSelectionCleanup(cart);
        if (!this.pendingCatalogSelectionCleanup?.size) return;
      }
    })().catch(() => { /* Failed verification keeps selections until a later safe check. */ });
    this.catalogSelectionSyncPromise = operation;
    void operation.finally(() => { if (this.catalogSelectionSyncPromise === operation) this.catalogSelectionSyncPromise = null; });
    return operation;
  }
  getSelectedPartDetails() {
    const parts = [['deck', 'Deck', this.selectedDeck], ['grip', 'Grip', this.selectedGrip], ['wheel', 'Wheels', this.selectedWheel],
      ['bearing', 'Bearings', this.selectedBearing], ['truck', 'Trucks', this.selectedTruck], ['hardware', 'Hardware', this.selectedHardware],
      ['skate_tool', 'Skate Tool', this.selectedSkateTool], ['assembly', 'Assembly', this.selectedAssembly]];
    const result = parts.filter(([, , item]) => item && !item.isPlaceholder).map(([component, label, item]) => ({
      component, label, variantId: String(item.variantId || ''), name: String(item.productTitle || item.name || item.title || label),
      size: component === 'deck' ? (item.selectedSize || this.getItemWidth(item) || '') : ''
    }));
    const assembly = this.querySelector('#bc-assembly');
    if (!result.some(part => part.component === 'assembly') && assembly?.value === 'service') result.push({
      component: 'assembly', label: 'Assembly', variantId: String(assembly.getAttribute('data-variant-id') || ''),
      name: assembly.getAttribute('data-product-title') || 'Assemble it', size: ''
    });
    return result;
  }
  formatUnavailableBoardParts(parts) {
    const helpers = parts.filter(part => ['assembly', 'skate_tool'].includes(part.component)).length;
    const action = helpers === parts.length ? 'another option' : helpers ? 'replacements' : 'another design';
    const lines = parts.map(part => `• ${part.label}: ${part.name}${part.size ? ` — ${this.formatSpecValue('width', part.size)}` : ''}`);
    return `One or more selected board parts are no longer available. Please choose ${action} for:\n\n${lines.join('\n')}`;
  }
  showCatalogSelectionPopup(message) {
    const work = this.boardAddWork;
    // A native dialog must not interrupt an in-flight add or its recovery timers.
    // The current add finishes first, then shows any separate selection warning.
    if (this.isAddingToCart && work && this.isCurrentBoardAdd(work)) {
      work.catalogSelectionMessages ||= new Set();
      work.catalogSelectionMessages.add(message);
      return;
    }
    window.alert(message);
  }
  invalidateUnavailableCatalogAdd(unavailable, details) {
    const attempt = this.boardAddAttempt;
    // The frozen add may differ from the build being edited.
    if (attempt?.status === 'new' && attempt.items.some(item => unavailable.has(String(item.id)))) {
      attempt.catalogInvalidated = true;
      const parts = (attempt.selectionNames || details).filter(part => unavailable.has(part.variantId));
      attempt.unavailableMessage = parts.length ? this.formatUnavailableBoardParts(parts) : 'One or more selected board parts are no longer available. Please choose another option.';
    }
  }
  clearUnavailableCatalogSelections(variantIds, { notify = true } = {}) {
    const unavailable = new Set(variantIds.map(String));
    const details = this.getSelectedPartDetails().filter(part => unavailable.has(part.variantId));
    this.invalidateUnavailableCatalogAdd(unavailable, details);
    if (!details.length) return;
    this.clearSelectedBuilderParts(details);
    this.catalogSelectionMessage = this.formatUnavailableBoardParts(details);
    if (notify) this.showCatalogSelectionPopup(this.catalogSelectionMessage);
  }
  getUnavailableRequiredHelpers() {
    return ['skate_tool', 'assembly'].filter(type => {
      const drawer = this.querySelector(`#bc-drawer-${type}`);
      if (!drawer) return false;
      const cards = [...drawer.querySelectorAll('.bc-product-card')];
      return !cards.some(card => this.isCatalogCardAvailable(card, type));
    });
  }
  updateCatalogAvailabilityFeedback() {
    const unavailableHelpers = this.getUnavailableRequiredHelpers();
    for (const drawer of this.querySelectorAll('.bc-drawer')) {
      if (!this.isCollectionDrawer(drawer)) continue;
      const body = drawer.querySelector('.bc-drawer__body');
      if (!body) continue;
      const type = drawer.id.replace('bc-drawer-', '');
      const catalogState = this.getDrawerCatalogState(type);
      const checking = this.catalogAvailabilityChecking || Boolean(catalogState?.controller);
      const drawerError = catalogState?.error || '';
      const unavailableHelper = unavailableHelpers.includes(type);
      // Routine background work must not insert status text or change drawer
      // geometry. Only an actionable failure needs the existing retry control.
      const message = checking ? '' : drawerError || this.catalogAvailabilityError
        || (unavailableHelper ? 'No options are available right now. Retry availability to check again.' : '');
      let feedback = body.querySelector('[data-catalog-availability-feedback]');
      if (!feedback && !message) continue;
      if (!feedback) {
        feedback = document.createElement('div');
        feedback.setAttribute('data-catalog-availability-feedback', '');
        const message = document.createElement('p'); message.setAttribute('role', 'status');
        const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Retry availability';
        retry.addEventListener('click', () => { void this.refreshDrawerOptions(drawer.id.replace('bc-drawer-', ''), { force: true }); });
        feedback.append(message, retry); body.prepend(feedback);
      }
      feedback.hidden = !message;
      const status = feedback.querySelector('p');
      if (status.textContent !== message) status.textContent = message;
      feedback.querySelector('button').hidden = !message;
    }
  }
  updateCatalogAvailabilityUI(changedType = null) {
    for (const type of changedType ? [changedType] : ['deck', 'grip', 'wheel', 'bearing', 'truck', 'hardware', 'skate_tool', 'assembly']) {
      this.querySelectorAll(`.bc-product-card[data-type="${type}"]`).forEach(card => {
        const unavailable = !this.isCatalogCardAvailable(card, type);
        if (card.hasAttribute('data-catalog-unavailable') !== unavailable) card.toggleAttribute('data-catalog-unavailable', unavailable);
        if (card.disabled !== unavailable) card.disabled = unavailable;
        const disabledValue = unavailable ? 'true' : 'false';
        if (card.getAttribute('aria-disabled') !== disabledValue) card.setAttribute('aria-disabled', disabledValue);
        const choose = card.querySelector('.bc-product-card__choose');
        const label = unavailable ? 'Unavailable' : card.classList.contains('is-active') ? 'Selected' : 'Choose part';
        if (choose && choose.textContent !== label) choose.textContent = label;
      });
      this.applyDrawerFilters(type);
    }
    const helpers = this.getUnavailableRequiredHelpers();
    this.querySelectorAll('[data-open-drawer]').forEach(trigger => {
      const unavailable = helpers.includes(trigger.getAttribute('data-open-drawer'));
      trigger.toggleAttribute('data-catalog-empty', unavailable);
      if (['assembly', 'skate_tool'].includes(trigger.getAttribute('data-open-drawer'))) {
        const status = trigger.querySelector('.bc-builder-status');
        if (status) status.textContent = unavailable ? 'Unavailable' : this.getSelectedPartDetails().some(part => part.component === trigger.getAttribute('data-open-drawer')) ? 'Chosen' : 'Choose';
      }
    });
    const assembly = this.querySelector('#bc-assembly');
    const serviceUnavailable = !this.isCatalogVariantAvailable(assembly?.getAttribute('data-variant-id'));
    for (const button of this.querySelectorAll('[data-assembly-choice-option="service"]')) {
      button.disabled = serviceUnavailable;
      button.setAttribute('aria-disabled', serviceUnavailable ? 'true' : 'false');
    }
    const serviceOption = assembly?.querySelector('option[value="service"]');
    if (serviceOption) serviceOption.disabled = serviceUnavailable;
    const add = this.querySelector('#bc-add-to-cart-btn');
    if (add && !this.isAddingToCart) add.disabled = helpers.length > 0;
    this.updateCatalogAvailabilityFeedback();
  }
  initCollectionDrawers() {
    this.querySelectorAll('[data-open-drawer]').forEach(trigger => {
      const type = trigger.getAttribute('data-open-drawer');
      const drawer = this.querySelector(`#bc-drawer-${type}`);
      if (this.isCollectionDrawer(drawer)) {
        const isOpen = drawer.classList.contains('is-open');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', drawer.id);
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        drawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        if (isOpen) drawer.removeAttribute('inert');
        else drawer.setAttribute('inert', '');
      }
      const prewarmDrawer = () => this.prewarmDrawerFirstImages(type);
      trigger.addEventListener('pointerenter', prewarmDrawer, { passive: true });
      trigger.addEventListener('pointerdown', prewarmDrawer, { passive: true });
      trigger.addEventListener('focus', prewarmDrawer);
      trigger.addEventListener('click', () => this.openDrawer(type, trigger));
    });
    this.querySelectorAll('[data-close-drawer]').forEach(trigger => {
      trigger.addEventListener('click', (event) => {
        const drawer = event.target.closest('.bc-drawer');
        if (drawer) this.closeDrawer(drawer);
      });
    });
    document.removeEventListener('keydown', this.onCollectionDrawerKeydown);
    document.addEventListener('keydown', this.onCollectionDrawerKeydown);
    const preloadFromEvent = (event, immediate = false) => {
      const card = event.target.closest('.bc-product-card');
      if (card) {
        if (immediate) {
          this.preloadCardTexture(card);
        } else {
          const type = card.getAttribute('data-type');
          this.queueDrawerTextureWarm(type, card, this.drawerTextureGenerations[type]);
        }
      }
      const zoomTrigger = event.target.closest('.bc-product-card__zoom');
      const imageWrapper = zoomTrigger?.closest('.bc-product-card__image-wrapper');
      if (imageWrapper) {
        this.preloadDrawerImage(imageWrapper.getAttribute('data-full-image'));
      }
    };
    this.addEventListener('pointermove', event => {
      if (event.pointerType !== 'mouse') return;
      const card = event.target.closest?.('.bc-product-card');
      this.setHoveredProductCard(card || null);
    }, { passive: true });
    this.addEventListener('mouseover', event => {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const card = event.target.closest?.('.bc-product-card');
      this.setHoveredProductCard(card || null);
    });
    this.addEventListener('pointerleave', () => this.clearHoveredProductCard());
    this.addEventListener('mouseleave', () => this.clearHoveredProductCard());
    this.querySelectorAll('.bc-drawer__grid').forEach(grid => {
      this.hydrateProductCardDisplayNames(grid);
      grid.addEventListener('pointerdown', event => preloadFromEvent(event, true), { passive: true });
      grid.addEventListener('pointerover', event => preloadFromEvent(event));
      grid.addEventListener('focusin', event => preloadFromEvent(event));
      grid.addEventListener('touchstart', event => preloadFromEvent(event, true), { passive: true });
      grid.addEventListener('pointerover', event => {
        if (event.pointerType !== 'mouse') return;
        const card = event.target.closest('.bc-product-card');
        if (card && grid.contains(card)) this.setHoveredProductCard(card);
      });
      grid.addEventListener('pointerout', event => {
        const card = event.target.closest('.bc-product-card');
        if (!card || !grid.contains(card) || card.contains(event.relatedTarget)) return;
        this.clearHoveredProductCard();
      });
      grid.addEventListener('focusin', event => {
        const card = event.target.closest('.bc-product-card');
        if (card && grid.contains(card)) this.setHoveredProductCard(card);
      });
      grid.addEventListener('focusout', event => {
        const card = event.target.closest('.bc-product-card');
        if (!card || !grid.contains(card) || card.contains(event.relatedTarget)) return;
        this.clearHoveredProductCard();
      });
      grid.addEventListener('click', event => {
        const card = event.target.closest('.bc-product-card');
        if (!card || !grid.contains(card)) return;
        const type = card.getAttribute('data-type');
        const zoomTrigger = event.target.closest('.bc-product-card__zoom');
        const imageWrapper = event.target.closest('.bc-product-card__image-wrapper');
        if (zoomTrigger && imageWrapper && card.contains(imageWrapper)) {
          const img = imageWrapper.querySelector('img');
          if (img) {
            const item = this.getProductCardItem(card);
            event.preventDefault();
            event.stopPropagation();
            this.clearHoveredProductCard();
            this.openImageModal(
              imageWrapper.getAttribute('data-full-image') || img.currentSrc || img.src || img.getAttribute('data-bc-src'),
              this.getItemDisplayName(item) || card.getAttribute('data-display-title') || card.getAttribute('data-title') || img.alt || 'Part Image',
              img.currentSrc || img.src || img.getAttribute('data-bc-src'),
              item?.description || '',
              item?.descriptionHtml || '',
              item?.images || []
            );
            return;
          }
        }
        const item = { ...this.getProductCardItem(card) };
        const cardImage = card.querySelector('img');
        item.imageUrl = item.imageUrl || cardImage?.currentSrc || cardImage?.src || cardImage?.getAttribute('data-bc-src') || null;
        item.fullImageUrl = item.fullImageUrl || card.querySelector('.bc-product-card__image-wrapper')?.getAttribute('data-full-image') || item.imageUrl;
        const didSelect = this.handleOptionClick(null, type, item);
        if (didSelect === false) return;
        const drawer = card.closest('.bc-drawer');
        if (drawer) this.closeDrawer(drawer);
      });
    });
  }
  isCollectionDrawer(drawer) {
    return Boolean(drawer?.querySelector('[data-close-drawer]'));
  }
  getOpenCollectionDrawer() {
    const drawers = Array.from(this.querySelectorAll('.bc-drawer.is-open, .bc-drawer.is-opening'))
      .filter(drawer => this.isCollectionDrawer(drawer));
    return drawers[drawers.length - 1] || null;
  }
  getOverlayFocusableElements(overlay) {
    if (!overlay) return [];
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    return Array.from(overlay.querySelectorAll(selector)).filter(element => {
      if (element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
      return element.getClientRects().length > 0;
    });
  }
  trapOverlayFocus(event, overlay) {
    const focusableElements = this.getOverlayFocusableElements(overlay);
    if (!focusableElements.length) {
      event.preventDefault();
      overlay.querySelector('[data-close-modal], [data-close-drawer]')?.focus({ preventScroll: true });
      return;
    }
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;
    if (!overlay.contains(activeElement)) {
      event.preventDefault();
      (event.shiftKey ? lastFocusable : firstFocusable).focus({ preventScroll: true });
    } else if (event.shiftKey && activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus({ preventScroll: true });
    } else if (!event.shiftKey && activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus({ preventScroll: true });
    }
  }
  handleCollectionDrawerKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return;
    const imageModal = this.querySelector('#bc-image-modal.is-open');
    if (imageModal) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.closeImageModal();
      } else if (event.key === 'Tab') {
        this.trapOverlayFocus(event, imageModal);
      }
      return;
    }
    const drawer = this.getOpenCollectionDrawer();
    if (!drawer) return;
    const activeDrawer = event.target?.closest?.('.bc-drawer.is-open');
    if (activeDrawer && activeDrawer !== drawer) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.closeDrawer(drawer);
      return;
    }
    if (event.key !== 'Tab') return;

    this.trapOverlayFocus(event, drawer);
  }
  getCatalogItems(type) {
    const map = {
      deck: this.decks,
      grip: this.grips,
      wheel: this.wheels,
      bearing: this.bearings,
      truck: this.trucks,
      hardware: this.hardware
    };
    return map[type] || [];
  }
  findCatalogItem(type, uid) {
    const items = this.getCatalogItems(type);
    this.catalogItemIndexes ||= new Map();
    let cached = this.catalogItemIndexes.get(type);
    const entry = cached?.byUid.get(uid);
    if (cached?.items === items && cached.length === items.length && entry &&
        items[entry.index] === entry.item && entry.item.uid === uid) {
      return entry.item;
    }
    // Validate the indexed slot so a replaced/reordered catalogue cannot return an old item.
    if (!cached || cached.items !== items || cached.length !== items.length || entry ||
        items.some(item => item.uid === uid)) {
      const byUid = new Map();
      items.forEach((item, index) => {
        if (!byUid.has(item.uid)) byUid.set(item.uid, { item, index });
      });
      cached = { items, length: items.length, byUid };
      this.catalogItemIndexes.set(type, cached);
    }
    return cached.byUid.get(uid)?.item || null;
  }
  getProductCardFilterData(card, type) {
    this.productCardFilterCache ||= new WeakMap();
    const cached = this.productCardFilterCache.get(card);
    if (!['deck', 'truck', 'wheel'].includes(type)) {
      if (cached?.signature === type) return cached;
      const data = { signature: type, item: {}, values: {} };
      this.productCardFilterCache.set(card, data);
      return data;
    }
    // Filtering needs only sizes/fit, never descriptions, galleries, prices or the current image URL.
    // Keep the selection payload live in getProductCardItem instead of caching it here.
    const catalogItem = this.findCatalogItem(type, card.getAttribute('data-uid'));
    const source = catalogItem ? {
      name: catalogItem.name,
      variantTitle: catalogItem.variantTitle,
      tags: catalogItem.tags,
      specs: catalogItem.specs,
      fitRange: catalogItem.fitRange,
      variantId: catalogItem.variantId,
      deckVariants: Array.isArray(catalogItem.deckVariants) ? catalogItem.deckVariants.map(variant => ({
        variantId: variant.variantId,
        title: variant.title,
        options: variant.options,
        width: variant.width
      })) : undefined
    } : {
      name: card.getAttribute('data-title'),
      variantTitle: card.getAttribute('data-variant-title'),
      tags: card.getAttribute('data-tags'),
      specs: {
        width: card.getAttribute('data-spec-width'),
        fitRange: card.getAttribute('data-fit-range'),
        diameter: card.getAttribute('data-spec-diameter'),
        hardness: card.getAttribute('data-spec-hardness')
      }
    };
    const signature = JSON.stringify([type, source, this.catalogAvailabilityRevision || 0]);
    if (cached?.signature === signature) return cached;
    const item = this.enrichFilterItem(source, type);
    const values = Object.fromEntries(this.getFilterConfig(type).map(({ field }) => [
      field, this.getItemFilterValues(item, field, type)
    ]));
    const data = { signature, item, values };
    this.productCardFilterCache.set(card, data);
    return data;
  }
  findProductCardSelection(type, uid) {
    if (!type || !uid) return null;
    const card = Array.from(this.querySelectorAll(`.bc-product-card[data-type="${type}"]`))
      .find(candidate => candidate.getAttribute('data-uid') === uid);
    return card && this.isCatalogCardAvailable(card, type) ? this.getProductCardItem(card) : null;
  }
  getProductCardPriceData(card) {
    if (!card) return { displayPriceCents: null, variantPriceCents: null };
    const legacyDisplayPriceCents = this.parsePriceCents(card.getAttribute('data-price-cents'));
    const displayPriceCents = this.parsePriceCents(card.getAttribute('data-display-price-cents'));
    const variantPriceCents = this.parsePriceCents(card.getAttribute('data-variant-price-cents'));
    return {
      displayPriceCents: displayPriceCents ?? legacyDisplayPriceCents,
      variantPriceCents
    };
  }
  getProductCardItem(card) {
    const type = card?.getAttribute('data-type');
    if (!card || !type) return null;
    const image = card.querySelector('img');
    const imageUrl = image ? (image.currentSrc || image.src || image.getAttribute('data-bc-src')) : null;
    const imageWrapper = card.querySelector('.bc-product-card__image-wrapper');
    const catalogItem = this.findCatalogItem(type, card.getAttribute('data-uid'));
    const cardVariantId = card.getAttribute('data-variant-id');
    const cardFormattedPrice = card.getAttribute('data-price');
    const cardPriceData = this.getProductCardPriceData(card);
    const catalogVariantMatchesCard =
      !cardVariantId ||
      !catalogItem?.variantId ||
      String(cardVariantId) === String(catalogItem.variantId);
    const cardImages = catalogItem?.images?.length
      ? catalogItem.images
      : this.parseImageList(imageWrapper?.getAttribute('data-images'));
    if (catalogItem) {
      return this.enrichFilterItem({
        ...catalogItem,
        variantId: cardVariantId || catalogItem.variantId,
        price: cardFormattedPrice ?? catalogItem.price,
        displayPriceCents:
          cardPriceData.displayPriceCents ?? (catalogVariantMatchesCard ? catalogItem.displayPriceCents : null),
        variantPriceCents:
          cardPriceData.variantPriceCents ?? (catalogVariantMatchesCard ? catalogItem.variantPriceCents : null),
        imageUrl: catalogItem.imageUrl || imageUrl,
        fullImageUrl: catalogItem.fullImageUrl || imageWrapper?.getAttribute('data-full-image') || null,
        images: catalogItem.images?.length ? catalogItem.images : cardImages
      }, type);
    }
    return this.enrichFilterItem({
      uid: card.getAttribute('data-uid'),
      name: card.getAttribute('data-title'),
      productTitle: card.getAttribute('data-product-title'),
      variantTitle: card.getAttribute('data-variant-title'),
      texture: card.getAttribute('data-texture'),
      color: card.getAttribute('data-color'),
      baseplateColor: card.getAttribute('data-baseplate-color'),
      bushingTopColor: card.getAttribute('data-bushing-top-color'),
      bushingBottomColor: card.getAttribute('data-bushing-bottom-color'),
      washerTopColor: card.getAttribute('data-washer-top-color'),
      washerBottomColor: card.getAttribute('data-washer-bottom-color'),
      axleNutColor: card.getAttribute('data-axle-nut-color'),
      kingpinNutColor: card.getAttribute('data-kingpin-nut-color'),
      variantId: cardVariantId,
      price: cardFormattedPrice,
      displayPriceCents: cardPriceData.displayPriceCents,
      variantPriceCents: cardPriceData.variantPriceCents,
      description: card.getAttribute('data-description'),
      descriptionHtml: card.getAttribute('data-description-html'),
      tags: this.normalizeTagList(card.getAttribute('data-tags')),
      specs: {
        width: card.getAttribute('data-spec-width'),
        fitRange: card.getAttribute('data-fit-range'),
        diameter: card.getAttribute('data-spec-diameter'),
        hardness: card.getAttribute('data-spec-hardness')
      },
      imageUrl,
      fullImageUrl: imageWrapper?.getAttribute('data-full-image') || null,
      images: cardImages,
      isCollection: true
    }, type);
  }
  getFilterConfig(type) {
    if (type === 'deck') return [{ field: 'width', label: 'Width' }];
    if (type === 'truck') return [{ field: 'width', label: 'Width' }];
    if (type === 'wheel') return [
      { field: 'diameter', label: 'Diameter' },
      { field: 'hardness', label: 'Hardness' }
    ];
    return [];
  }
  formatSpecValue(field, value) {
    if (field === 'width') {
      const label = this.normalizeWidthValue(value);
      if (!label) return value;
      return `${label}"`;
    }
    return value;
  }
  sortSpecValues(field, values) {
    return values.sort((a, b) => {
      const aNum = Number.parseFloat(a);
      const bNum = Number.parseFloat(b);
      if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    });
  }
  getItemWidth(item) {
    return this.normalizeWidthValue(item?.specs?.width);
  }
  getTruckFitRange(item) {
    return this.parseDeckFitRange(item?.specs?.fitRange || item?.fitRange);
  }
  isChosenSelection(item) {
    return Boolean(item && !item.isPlaceholder);
  }
  getFitReference(type) {
    if (type === 'truck' && this.isChosenSelection(this.selectedDeck)) {
      const width = this.getItemWidth(this.selectedDeck);
      return width ? {
        width,
        sourceType: 'deck',
        targetType: 'trucks',
        sourceName: this.selectedDeck.name
      } : null;
    }
    return null;
  }
  widthsFit(deckWidth, truckWidth) {
    const deck = Number.parseFloat(deckWidth);
    const truck = Number.parseFloat(truckWidth);
    if (!Number.isFinite(deck) || !Number.isFinite(truck)) return false;
    return Math.abs(deck - truck) <= 0.13 + 0.0001;
  }
  truckFitsDeck(deckWidth, truck) {
    const deck = Number.parseFloat(deckWidth);
    if (!Number.isFinite(deck)) return false;
    const fitRange = this.getTruckFitRange(truck);
    if (fitRange) {
      return deck >= fitRange.min - 0.0001 && deck <= fitRange.max + 0.0001;
    }
    const truckWidth = this.getItemWidth(truck);
    return Boolean(truckWidth && this.widthsFit(deckWidth, truckWidth));
  }
  deckFitsSelectedTruck(deck) {
    if (!this.isChosenSelection(this.selectedTruck)) return true;
    const deckWidth = this.getItemWidth(deck);
    return Boolean(deckWidth && this.truckFitsDeck(deckWidth, this.selectedTruck));
  }
  clearTruckSelection() {
    this.selectedTruck = this.createEmptySelection('truck');
    this.removeUrlParam('truck');
    this.updateTruckColor(this.selectedTruck.color, this.selectedTruck.baseplateColor, this.selectedTruck);
  }
  itemFitsSelectedCounterpart(type, item) {
    const reference = this.getFitReference(type);
    if (!reference) return true;
    if (type === 'truck') return this.truckFitsDeck(reference.width, item);
    return true;
  }
  ensureFitNote(type, grid) {
    if (type !== 'deck' && type !== 'truck') return null;
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    let note = drawer?.querySelector('.bc-fit-note');
    if (!note && grid) {
      note = document.createElement('p');
      note.className = 'bc-fit-note';
      note.setAttribute('data-fit-note', type);
      note.hidden = true;
      grid.before(note);
    }
    return note || null;
  }
  updateFitNote(type, reference) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const note = drawer?.querySelector('.bc-fit-note');
    if (!note) return;
    if (!reference) {
      note.hidden = true;
      note.textContent = '';
      return;
    }
    const widthLabel = this.formatSpecValue('width', reference.width);
    const changeTarget = reference.sourceType === 'deck' ? 'deck' : 'trucks';
    note.textContent = `Based on your ${widthLabel} ${reference.sourceType}, these are the ${reference.targetType} that fit. Change your ${changeTarget} to see more designs.`;
    note.hidden = false;
  }
  isFilterOptionAllowed(type, field, value) {
    if (!value || value === 'all') return true;
    const available = this.getCatalogItems(type).filter(item => this.isCatalogItemAvailable(type, item));
    if (available.length && !available.some(item => this.getItemFilterValues(item, field, type).includes(value))) return false;
    if (this.getCatalogItems(type).length && !available.length) return false;
    const reference = this.getFitReference(type);
    if (type === 'truck' && field === 'width' && reference) {
      const width = this.normalizeWidthValue(value);
      const matchingTrucks = this.getCatalogItems('truck').filter(item => this.getItemWidth(item) === width);
      return matchingTrucks.some(item => this.truckFitsDeck(reference.width, item)) || this.widthsFit(reference.width, width);
    }
    return true;
  }
  updateFilterOptionAvailability(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    if (!drawer) return;
    const reference = this.getFitReference(type);
    const fitSignature = JSON.stringify([
      reference,
      type === 'truck' && reference ? this.getCatalogItems(type).map(item => [
        item.uid, item.specs?.width, item.specs?.fitRange, item.fitRange
      ]) : null
    ]);
    drawer.querySelectorAll('.bc-filter-select[data-filter-field]').forEach(select => {
      const field = select.getAttribute('data-filter-field');
      const selectedValue = select.value || 'all';
      let selectedWasDisabled = false;
      if (!select._bcFilterOptions) {
        select._bcFilterOptions = Array.from(select.options).map(option => ({
          value: option.value,
          label: option.textContent || option.value
        }));
      }
      const availabilitySignature = JSON.stringify([field, fitSignature, select._bcFilterOptions, this.catalogAvailabilityRevision || 0]);
      if (select._bcFilterAvailabilitySignature === availabilitySignature) return;
      select.innerHTML = '';
      select._bcFilterOptions.forEach(optionData => {
        const allowed = this.isFilterOptionAllowed(type, field, optionData.value);
        if (!allowed) {
          if (optionData.value === selectedValue) selectedWasDisabled = true;
          return;
        }
        const option = new Option(optionData.label, optionData.value);
        select.add(option);
      });
      const hasSelectedValue = Array.from(select.options).some(option => option.value === selectedValue);
      if (hasSelectedValue && !selectedWasDisabled) {
        select.value = selectedValue;
      } else {
        select.value = 'all';
        if (this.activeDrawerFilters[type]) {
          this.activeDrawerFilters[type][field] = 'all';
        }
      }
      select._bcFilterAvailabilitySignature = availabilitySignature;
    });
  }
  getDrawerSearchQuery(type) {
    return String(this.activeDrawerSearch?.[type] || '').trim().toLowerCase();
  }
  getProductCardSearchData(card) {
    // Use the displayed variant's title/options, not a sibling size's title.
    // The Liquid payload includes the same fields as collection-page search.
    const source = [
      card.getAttribute('data-display-title') || card.getAttribute('data-title'),
      card.getAttribute('data-search-text') || [
        card.getAttribute('data-variant-title'), card.getAttribute('data-tags'),
        card.getAttribute('data-spec-width'), card.getAttribute('data-spec-diameter'),
        card.getAttribute('data-spec-hardness')
      ].filter(Boolean).join(' ')
    ].filter(Boolean).join(' ');
    this.productCardSearchCache ||= new WeakMap();
    const cached = this.productCardSearchCache.get(card);
    if (cached?.source === source) return cached;
    const tokens = [...new Set(String(source).toLowerCase().match(/[a-z0-9]+(?:\.[a-z0-9]+)?/g) || [])];
    const data = { source, tokens, tokenSet: new Set(tokens) };
    this.productCardSearchCache.set(card, data);
    return data;
  }
  matchesDrawerSearch(card, query) {
    const terms = String(query || '').toLowerCase().match(/[a-z0-9]+(?:\.[a-z0-9]+)?/g) || [];
    if (!terms.length) return true;
    const { tokens, tokenSet } = this.getProductCardSearchData(card);
    return terms.every(term => tokenSet.has(term) || tokens.some(token => token.startsWith(term)));
  }
  clearDrawerSearch(type, { focus = false } = {}) {
    this.activeDrawerSearch ||= {};
    this.activeDrawerSearch[type] = '';
    const input = this.querySelector(`#bc-drawer-${type} [data-drawer-search]`);
    if (input) input.value = '';
    this.applyDrawerFilters(type);
    if (focus) input?.focus({ preventScroll: true });
  }
  initDrawerFilters() {
    ['deck', 'grip', 'wheel', 'bearing', 'truck', 'hardware', 'skate_tool', 'assembly'].forEach(type => {
      const drawer = this.querySelector(`#bc-drawer-${type}`);
      const grid = drawer?.querySelector('.bc-drawer__grid');
      if (!drawer || !grid) return;
      const groups = (['deck', 'wheel', 'truck'].includes(type) ? this.getFilterConfig(type) : [])
        .map(group => {
          const options = [...new Set(this.getCatalogItems(type).flatMap(item => this.getItemFilterValues(item, group.field, type)).filter(Boolean))];
          return { ...group, options: this.sortSpecValues(group.field, options) };
        })
        .filter(group => group.options.length > 1);
      this.activeDrawerFilters[type] = groups.reduce((filters, group) => {
        filters[group.field] = 'all';
        return filters;
      }, {});
      this.activeDrawerSort[type] = 'manual';
      const searchLabel = { deck: 'decks', grip: 'griptape', wheel: 'wheels', bearing: 'bearings', truck: 'trucks', hardware: 'hardware', skate_tool: 'skate tools', assembly: 'assembly options' }[type];
      const panel = document.createElement('div');
      panel.className = 'bc-filter-panel';
      panel.setAttribute('data-filter-panel', type);
      const sortControlHtml = `
        <div class="bc-filter-group" data-sort-group>
          <label class="bc-filter-label" for="bc-sort-${this.escapeHtml(type)}">Sort</label>
          <span class="bc-filter-select-wrap">
            <select class="bc-filter-select" id="bc-sort-${this.escapeHtml(type)}" data-sort-select aria-label="${this.escapeHtml(`${type} sort`)}">
              <option value="manual">Featured</option>
              <option value="price-asc">Price low to high</option>
              <option value="price-desc">Price high to low</option>
            </select>
          </span>
        </div>`;
      const filterControlHtml = groups.map(group => `
        <div class="bc-filter-group" data-filter-group="${this.escapeHtml(group.field)}">
          <label class="bc-filter-label" for="bc-filter-${this.escapeHtml(type)}-${this.escapeHtml(group.field)}">${this.escapeHtml(group.label)}</label>
          <span class="bc-filter-select-wrap">
            <select class="bc-filter-select" id="bc-filter-${this.escapeHtml(type)}-${this.escapeHtml(group.field)}" data-filter-field="${this.escapeHtml(group.field)}" aria-label="${this.escapeHtml(`${group.label} filter`)}">
              <option value="all">All ${this.escapeHtml(group.label)}</option>
              ${group.options.map(value => `
                <option value="${this.escapeHtml(value)}">${this.escapeHtml(this.formatSpecValue(group.field, value))}</option>
              `).join('')}
            </select>
          </span>
        </div>
      `).join('');
      panel.innerHTML = `
        ${filterControlHtml}
        ${sortControlHtml}
        <div class="bc-drawer-search">
          <label class="visually-hidden" for="bc-search-${type}">Search ${searchLabel}</label>
          <svg class="bc-drawer-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"></circle><path d="m16 16 4.5 4.5"></path></svg>
          <input id="bc-search-${type}" class="bc-drawer-search__input" type="search" inputmode="search" enterkeyhint="search" autocomplete="off" placeholder="Search ${type === 'assembly' ? 'assembly' : searchLabel}" data-drawer-search>
          <button type="button" class="bc-drawer-search__clear" data-drawer-search-clear aria-label="Clear search" hidden><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"></path></svg></button>
          <span class="visually-hidden" role="status" aria-live="polite" aria-atomic="true" data-drawer-search-status></span>
        </div>
        <div class="bc-filter-group bc-filter-group--reset">
          <button type="button" class="bc-filter-reset" data-filter-reset>Reset filters</button>
        </div>`;
      const empty = document.createElement('p');
      empty.className = 'bc-filter-empty';
      empty.textContent = 'No parts match these filters.';
      drawer.querySelector('.bc-drawer__body').before(panel);
      this.ensureFitNote(type, grid);
      grid.after(empty);
      const searchInput = panel.querySelector('[data-drawer-search]');
      // Text inputs match :focus-visible even after a mouse/touch click.
      // Keep a keyboard cue without changing the field when clicked or typed in.
      searchInput.addEventListener('pointerdown', () => searchInput.classList.add('is-pointer-focused'));
      searchInput.addEventListener('blur', () => searchInput.classList.remove('is-pointer-focused'));
      searchInput.addEventListener('input', () => {
        this.activeDrawerSearch ||= {};
        this.activeDrawerSearch[type] = searchInput.value;
        this.applyDrawerFilters(type);
        const body = drawer.querySelector('.bc-drawer__body');
        if (body) body.scrollTop = 0;
      });
      searchInput.addEventListener('keydown', event => {
        if (event.isComposing) return;
        if (event.key === 'Escape' && searchInput.value) {
          event.preventDefault();
          event.stopPropagation();
          this.clearDrawerSearch(type, { focus: true });
        } else if (event.key === 'Enter') {
          event.preventDefault();
          searchInput.blur();
        }
      });
      panel.querySelector('[data-drawer-search-clear]').addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.detail > 0) searchInput.classList.add('is-pointer-focused');
        this.clearDrawerSearch(type, { focus: true });
      });
      panel.addEventListener('pointerdown', event => {
        const select = event.target.closest('.bc-filter-select');
        if (select) select.classList.add('is-pointer-focused');
      });
      panel.addEventListener('keydown', () => {
        panel.querySelectorAll('.bc-filter-select.is-pointer-focused').forEach(select => {
          select.classList.remove('is-pointer-focused');
        });
      });
      panel.addEventListener('focusout', event => {
        event.target.closest?.('.bc-filter-select')?.classList.remove('is-pointer-focused');
      });
      panel.addEventListener('click', event => {
        const resetButton = event.target.closest('[data-filter-reset]');
        if (!resetButton) return;
        this.activeDrawerSearch ||= {};
        this.activeDrawerSearch[type] = '';
        searchInput.value = '';
        this.activeDrawerSort[type] = 'manual';
        Object.keys(this.activeDrawerFilters[type] || {}).forEach(field => {
          this.activeDrawerFilters[type][field] = 'all';
        });
        const sortSelect = panel.querySelector('[data-sort-select]');
        if (sortSelect) sortSelect.value = 'manual';
        panel.querySelectorAll('[data-filter-field]').forEach(select => {
          select.value = 'all';
        });
        this.applyDrawerFilters(type);
        resetButton.classList.add('is-pressed');
        resetButton.blur?.();
        window.setTimeout(() => {
          resetButton.classList.remove('is-pressed');
          resetButton.blur?.();
        }, 180);
      });
      panel.addEventListener('change', event => {
        const sortSelect = event.target.closest('[data-sort-select]');
        if (sortSelect) {
          this.activeDrawerSort[type] = sortSelect.value || 'manual';
          this.applyDrawerFilters(type);
          return;
        }
        const select = event.target.closest('.bc-filter-select');
        if (!select) return;
        const field = select.getAttribute('data-filter-field');
        if (!field) return;
        const value = select.value || 'all';
        this.activeDrawerFilters[type][field] = value;
        this.applyDrawerFilters(type);
      });
    });
  }
  getCardPriceCents(card) {
    const explicitPrice = this.getProductCardPriceData(card).displayPriceCents;
    if (explicitPrice !== null) return explicitPrice;
    const priceText = card.getAttribute('data-price') || '';
    const parsed = Number.parseFloat(priceText.replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  }
  sortDrawerCards(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const grid = drawer?.querySelector('.bc-drawer__grid');
    if (!grid) return;
    const sortMode = this.activeDrawerSort[type] || 'manual';
    const cards = Array.from(grid.querySelectorAll(`.bc-product-card[data-type="${type}"]`));
    const entries = cards.map((card, index) => {
      if (!card.hasAttribute('data-sort-index')) card.setAttribute('data-sort-index', String(index));
      return {
        card,
        index: Number(card.getAttribute('data-sort-index') || 0),
        price: sortMode === 'price-asc' || sortMode === 'price-desc' ? this.getCardPriceCents(card) : null
      };
    });
    this.drawerSortCache ||= new WeakMap();
    const cached = this.drawerSortCache.get(grid);
    if (cached?.mode === sortMode && cached.entries.length === entries.length &&
        entries.every((entry, index) => {
          const previous = cached.entries[index];
          return entry.card === previous.card && entry.index === previous.index && entry.price === previous.price;
        })) return;
    const sortedEntries = [...entries].sort((a, b) => {
      const originalOrder = () => a.index - b.index;
      if (sortMode === 'price-asc' || sortMode === 'price-desc') {
        const aPrice = a.price;
        const bPrice = b.price;
        if (aPrice === null && bPrice === null) return originalOrder();
        if (aPrice === null) return 1;
        if (bPrice === null) return -1;
        const priceOrder = sortMode === 'price-asc' ? aPrice - bPrice : bPrice - aPrice;
        return priceOrder || originalOrder();
      }
      return originalOrder();
    });
    this.drawerSortCache.set(grid, { mode: sortMode, entries: sortedEntries });
    const sortedCards = sortedEntries.map(entry => entry.card);
    if (sortedCards.every((card, index) => card === cards[index])) return;
    sortedCards.forEach(card => grid.appendChild(card));
  }
  applyDrawerFilters(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    if (!drawer) return;
    this.sortDrawerCards(type);
    this.updateFilterOptionAvailability(type);
    const filters = this.activeDrawerFilters[type] || {};
    const query = this.getDrawerSearchQuery(type);
    const clearSearch = drawer.querySelector('[data-drawer-search-clear]');
    if (clearSearch) clearSearch.hidden = !query;
    const fitReference = this.getFitReference(type);
    const cards = Array.from(drawer.querySelectorAll(`.bc-product-card[data-type="${type}"]`));
    const activeFilters = Object.entries(filters).filter(([, value]) => value && value !== 'all');
    const hasActiveFilters = Boolean(query || activeFilters.length || (this.activeDrawerSort[type] || 'manual') !== 'manual');
    drawer.querySelector('[data-filter-reset]')?.classList.toggle('is-active-filter', hasActiveFilters);
    const needsMetadata = activeFilters.length > 0 || Boolean(fitReference);
    const data = needsMetadata ? cards.map(card => this.getProductCardFilterData(card, type)) : null;
    const searchData = query ? cards.map(card => this.getProductCardSearchData(card)) : null;
    const signature = JSON.stringify([type, filters, fitReference, this.catalogAvailabilityRevision || 0, query]);
    this.drawerFilterCache ||= new WeakMap();
    const cached = this.drawerFilterCache.get(drawer);
    const unchanged = cached?.signature === signature && cached.cards.length === cards.length &&
      cards.every((card, index) => cached.cards[index] === card && cached.data?.[index] === data?.[index] && cached.searchData?.[index] === searchData?.[index]);
    if (!unchanged) {
      let visibleCount = 0;
      cards.forEach((card, index) => {
        const { item, values } = data?.[index] || { item: null, values: {} };
        const matchesFilters = activeFilters.every(([field, value]) => (values[field] || []).includes(value));
        const matchesFit = !fitReference || this.itemFitsSelectedCounterpart(type, item);
        const available = this.isCatalogCardAvailable(card, type);
        const helperOption = type === 'skate_tool' || (type === 'assembly' && !card.hasAttribute('data-assembly-vacation'));
        const matches = matchesFilters && matchesFit && this.matchesDrawerSearch(card, query) && (available || helperOption);
        card.classList.toggle('is-filtered-out', !matches);
        if (matches && available) visibleCount += 1;
      });
      this.updateFitNote(type, fitReference);
      const empty = drawer.querySelector('.bc-filter-empty');
      if (empty) {
        empty.classList.toggle('is-visible', visibleCount === 0);
        empty.textContent = query ? 'No available parts match your search and filters.' : (type === 'assembly' || type === 'skate_tool') && visibleCount === 0
          ? 'No options are available right now. Please retry availability. You can keep editing the other parts.'
          : 'No available designs match these filters.';
      }
      const status = drawer.querySelector('[data-drawer-search-status]');
      if (status) status.textContent = query ? `${visibleCount} ${visibleCount === 1 ? 'result' : 'results'}` : '';
      this.drawerFilterCache.set(drawer, { signature, cards, data, searchData });
      this.persistentScrollbarSyncs?.forEach(sync => sync());
    }
    this.primeDrawerImages(type);
    if (this.drawerTextureObservers.has(type)) {
      this.scheduleDrawerTextureWarmup(type);
    }
  }
  openDrawer(type, trigger = null) {
    void this.refreshDrawerOptions(type, { reason: 'drawer-open' });
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    if (!drawer) {
      alert(`Add a ${type} collection in this section setting first.`);
      return;
    }
    if (!this.isCollectionDrawer(drawer)) return;
    const openDrawer = this.getOpenCollectionDrawer();
    if (openDrawer && openDrawer !== drawer) this.closeDrawer(openDrawer);
    const fallbackOpener = Array.from(this.querySelectorAll('[data-open-drawer]'))
      .find(candidate => candidate.getAttribute('data-open-drawer') === type);
    const activeElement = document.activeElement;
    const opener = trigger && !drawer.contains(trigger)
      ? trigger
      : (activeElement && activeElement !== document.body && !drawer.contains(activeElement)
        ? activeElement
        : fallbackOpener);
    if (opener && typeof opener.focus === 'function') {
      this.collectionDrawerOpeners.set(drawer, opener);
    }
    const requestId = ++this.drawerOpenRequestId;
    drawer.classList.remove('is-open', 'is-ready');
    drawer.classList.add('is-opening');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    this.setCollectionDrawerTriggerExpanded(type, true);
    this.prewarmDrawerFirstImages(type);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (requestId !== this.drawerOpenRequestId || !drawer.classList.contains('is-opening')) return;
        this.updateActiveProductCards(type);
        this.applyDrawerFilters(type);
        drawer.classList.remove('is-opening');
        drawer.classList.add('is-open');
        drawer.querySelector('.bc-drawer__close[data-close-drawer]')?.focus({ preventScroll: true });
        window.requestAnimationFrame(() => {
          if (!drawer.classList.contains('is-open')) return;
          drawer.classList.add('is-ready');
          this.scheduleDrawerTextureWarmup(type, true);
        });
      });
    });
  }
  closeDrawer(drawer) {
    if (!this.isCollectionDrawer(drawer)) return;
    const type = drawer.id?.replace('bc-drawer-', '');
    const storedOpener = this.collectionDrawerOpeners.get(drawer);
    const fallbackOpener = Array.from(this.querySelectorAll('[data-open-drawer]'))
      .find(candidate => candidate.getAttribute('data-open-drawer') === type);
    const opener = storedOpener?.isConnected && !storedOpener.closest?.('[inert]')
      ? storedOpener
      : fallbackOpener;
    if (opener && typeof opener.focus === 'function') {
      opener.focus({ preventScroll: true });
    }
    this.collectionDrawerOpeners.delete(drawer);
    this.drawerOpenRequestId += 1;
    drawer.classList.remove('is-opening', 'is-open', 'is-ready');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    if (type) this.cancelDrawerTextureWarmup(type);
    this.setCollectionDrawerTriggerExpanded(type, false);
    const observer = type ? this.drawerImageObservers.get(type) : null;
    if (observer) {
      observer.disconnect();
      this.drawerImageObservers.delete(type);
    }
  }
  setCollectionDrawerTriggerExpanded(type, isExpanded) {
    this.querySelectorAll('[data-open-drawer]').forEach(trigger => {
      if (trigger.getAttribute('data-open-drawer') !== type) return;
      trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    });
  }
  updateActiveProductCards(type = null) {
    const selectedByType = {
      deck: this.selectedDeck,
      grip: this.selectedGrip,
      wheel: this.selectedWheel,
      bearing: this.selectedBearing,
      truck: this.selectedTruck,
      hardware: this.selectedHardware,
      skate_tool: this.selectedSkateTool,
      assembly: this.selectedAssembly
    };
    const selector = type ? `.bc-product-card[data-type="${type}"]` : '.bc-product-card';
    this.querySelectorAll(selector).forEach(card => {
      const cardType = card.getAttribute('data-type');
      const selected = selectedByType[cardType];
      const cardUid = card.getAttribute('data-uid');
      const cardVariantId = card.getAttribute('data-variant-id');
      const sameVariant = !cardVariantId || cardVariantId === String(selected?.variantId || '');
      const isActive = selected && !selected.isPlaceholder && sameVariant && (cardUid === selected.uid || (cardType === 'deck' && cardUid === selected.productUid));
      if (card.classList.contains('is-active') !== Boolean(isActive)) {
        card.classList.toggle('is-active', Boolean(isActive));
      }
      const pressedValue = isActive ? 'true' : 'false';
      if (card.getAttribute('aria-pressed') !== pressedValue) card.setAttribute('aria-pressed', pressedValue);
      const chooseLabel = card.querySelector('.bc-product-card__choose');
      const nextLabel = !this.isCatalogCardAvailable(card, cardType) ? 'Unavailable' : isActive ? 'Selected' : 'Choose part';
      if (chooseLabel && chooseLabel.textContent !== nextLabel) chooseLabel.textContent = nextLabel;
    });
  }
  handleOptionClick(e, type, item) {
    if (!this.isCatalogItemAvailable(type, item)) {
      this.showCatalogSelectionPopup('This option is no longer available. Please choose another option.');
      return false;
    }
    this.catalogSelectionMessage = '';
    if (type === 'deck') {
      item = this.resolveDeckVariantSelection(item, item.productUid && item.deckVariants?.length ? item.variantId : null);
      if (!item) return false;
      this.selectedDeck = item;
      this.updateUrlParam('deck', item.uid);
      if (!this.deckFitsSelectedTruck(item)) {
        this.clearTruckSelection();
      }
      this.updateDeckTexture(item.texture);
      this.animateCamera(this.defaultCameraTarget.clone().add(new THREE.Vector3(2.25, 0.75, 0)), this.defaultCameraTarget.clone());
    } else if (type === 'grip') {
      this.selectedGrip = item;
      this.updateUrlParam('grip', item.uid);
      this.updateGripTexture(item.texture);
      this.updateGripColor(item.color);
      this.animateCamera(this.defaultCameraTarget.clone().add(new THREE.Vector3(-2.472, 0.66, 0.336)), this.defaultCameraTarget.clone());
    } else if (type === 'wheel') {
      this.selectedWheel = item;
      this.updateUrlParam('wheel', item.uid);
      this.updateWheelTexture(item.texture);
      this.spinWheels();
      this.animateCamera(new THREE.Vector3(0.09, 1, 0.9), new THREE.Vector3(-0.08, 0.54, 0.64));
    } else if (type === 'bearing') {
      this.selectedBearing = item;
      this.updateUrlParam('bearing', item.uid);
      this.updateBearingColor(item.color);
      this.animateCamera(new THREE.Vector3(0.005, 0.77, 0.77), new THREE.Vector3(-0.08, 0.54, 0.64));
    } else if (type === 'truck') {
      this.selectedTruck = item;
      this.updateUrlParam('truck', item.uid);
      this.updateTruckColor(item.color, item.baseplateColor, item);
      this.animateCamera(new THREE.Vector3(0.21, 0.23, 1.06), new THREE.Vector3(-0.12, 0.29, 0.57));
    } else if (type === 'hardware') {
      this.selectedHardware = item;
      this.updateUrlParam('hardware', item.uid);
      this.updateHardwareColors(item);
      this.animateCamera(new THREE.Vector3(-0.62, 0.38, 0.89), new THREE.Vector3(-0.25, 0.3, 0.62));
    } else if (type === 'skate_tool') {
      this.selectedSkateTool = item;
      this.updateUrlParam('skate_tool', item.uid);
      this.resetCamera();
    } else if (type === 'assembly') {
      this.selectedAssembly = item;
      this.updateUrlParam('assembly_item', item.uid);
      this.resetCamera();
    }
    this.renderUI(type);
    return true;
  }
  updateUrlParam(key, value) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.replaceState({}, '', url.href);
  }
  removeUrlParam(key) {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    window.history.replaceState({}, '', url.href);
  }
  initThree() {
    const container = this.querySelector('#bc-canvas-container');
    const width = container.clientWidth;
    const height = container.clientHeight;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color('#3B3A3A'); 
    this.scene.fog = new THREE.Fog('#3B3A3A', 3, 10);
    this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
    this.camera.position.copy(this.defaultCameraPosition);
    this.renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      powerPreference: "high-performance"
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // Camera and colour changes reuse the same shadows. Geometry changes below
    // explicitly refresh them, including animated wheels and context recovery.
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.domElement.style.touchAction = 'none';
    this.renderer.domElement.style.userSelect = 'none';
    this.renderer.domElement.addEventListener('webglcontextrestored', () => this.requestSceneRender(true));
    container.appendChild(this.renderer.domElement);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.enablePan = false;
    // Preserve the original trailing feel during normal camera movement.
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 0.035;
    this.controls.maxDistance = 6; // Allowed zooming out further
    this.controls.maxPolarAngle = Math.PI / 2;
    this.controls.target.copy(this.defaultCameraTarget);
    this.controls.update();
    this.controls.addEventListener('start', () => {
      this.cancelCameraTransition();
    });
    this.controls.addEventListener('change', () => this.requestSceneRender());
    this.handleSceneVisibility = () => {
      if (document.hidden) {
        if (this.animationFrameId !== null) window.cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      } else {
        this.requestSceneRender();
      }
    };
    document.addEventListener('visibilitychange', this.handleSceneVisibility);
    const ambientLight = new THREE.AmbientLight('#ffffff', 0.8);
    this.scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight('#ffffff', 1.6);
    directionalLight.position.set(1, 1, 1);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    directionalLight.shadow.bias = -0.001;
    this.scene.add(directionalLight);
    const floorGeo = new THREE.PlaneGeometry(6, 6);
    const floorMat = new THREE.MeshBasicMaterial({ visible: false });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    this.scene.add(floor);
    this.buildStageFloor();
    this.requestSceneRender(true);
  }
  requestSceneRender(shadowsChanged = false) {
    // Keep invalidation even if a frame is already queued or the tab is hidden.
    if (shadowsChanged && this.renderer?.shadowMap) this.renderer.shadowMap.needsUpdate = true;
    if (!this.isConnected || document.hidden || !this.renderer || !this.scene || !this.camera || !this.controls) return;
    if (this.animationFrameId !== null) return;
    this.animationFrameId = window.requestAnimationFrame(() => {
      this.animationFrameId = null;
      if (!this.isConnected || document.hidden || !this.renderer || !this.scene || !this.camera || !this.controls) return;
      // OrbitControls emits change while its damping is moving the camera. Each
      // change requests the next frame; a settled board has no running loop.
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    });
  }
  buildStageFloor() {
    this.textureLoader.load('https://cdn.shopify.com/s/files/1/0767/1719/5522/files/metal-normal.avif?v=1777292849', (normalMap) => {
      normalMap.wrapS = THREE.RepeatWrapping;
      normalMap.wrapT = THREE.RepeatWrapping;
      normalMap.repeat.set(30, 30);
      normalMap.anisotropy = 8;
      const floorMat = new THREE.MeshStandardMaterial({
        roughness: 0.75,
        color: '#3B3A3A',
        normalMap: normalMap
      });
      const circleGeo = new THREE.CircleGeometry(20, 32);
      const stageFloor = new THREE.Mesh(circleGeo, floorMat);
      stageFloor.position.set(0, -0.005, 0);
      stageFloor.rotation.x = -Math.PI / 2;
      stageFloor.receiveShadow = true;
      stageFloor.castShadow = true;
      if (!this.isConnected || !this.scene) return;
      this.scene.add(stageFloor);
      this.requestSceneRender(true);
    });
  }
  loadAssets() {
    const draco = new DRACOLoader();
    const pendingDecodes = new Set();
    const decodeGeometry = draco.decodeGeometry.bind(draco);
    let releaseRequested = false;
    draco.decodeGeometry = (...args) => {
      if (releaseRequested) return Promise.reject(new Error('The custom board model load has ended.'));
      const pending = decodeGeometry(...args);
      pendingDecodes.add(pending);
      pending.then(() => pendingDecodes.delete(pending), () => pendingDecodes.delete(pending));
      return pending;
    };
    const releaseDecoder = () => {
      if (releaseRequested) return;
      releaseRequested = true;
      // A failed GLTF parse can leave sibling decodes running. In pinned Three r160,
      // preload() can also create the worker URL after an early parse error, so wait
      // for decoderPending before releasing this model's workers and URL.
      Promise.allSettled([draco.decoderPending, ...pendingDecodes]).then(() => draco.dispose());
    };
    const onLoad = (gltf) => {
      releaseDecoder();
      const model = gltf.scene;
      model.rotation.set(0, 0, Math.PI / 2);
      model.position.set(0, 0.295, 0);
      model.traverse(child => {
        if (!child.isMesh) return;
        child.castShadow = true;
        child.receiveShadow = true;
        const name = child.name.toLowerCase();
        if (name === 'griptape') {
          this.gripTapeMesh = child;
          this.gripMaterial = new THREE.MeshStandardMaterial({
            bumpScale: 3.5,
            roughness: 0.82,
            color: this.selectedGrip.color || this.defaultGripColor || '#555555'
          });
          child.material = this.gripMaterial;
          this.textureLoader.load('https://cdn.shopify.com/s/files/1/0767/1719/5522/files/griptape-roughness.webp?v=1777292849', (roughness) => {
            roughness.wrapS = roughness.wrapT = THREE.RepeatWrapping;
            roughness.repeat.set(9, 9);
            roughness.anisotropy = 8;
            roughness.needsUpdate = true;
            this.gripMaterial.bumpMap = roughness;
            this.gripMaterial.roughnessMap = roughness;
            this.gripMaterial.needsUpdate = true;
            this.requestSceneRender();
          });
          this.updateGripTexture(this.selectedGrip.texture);
          this.updateGripColor(this.selectedGrip.color);
        } else if (name === 'deck') {
          this.deckMesh = child;
          this.deckMaterial = new THREE.MeshStandardMaterial({
            roughness: 0.1,
            side: THREE.DoubleSide
          });
          this.deckMesh.material = this.deckMaterial;
          this.updateDeckTexture(this.selectedDeck.texture);
        } else if (name.includes('wheel')) {
          this.wheelRefs.push(child);
          if (!this.wheelMaterial) {
            this.wheelMaterial = new THREE.MeshStandardMaterial({ roughness: 0.35 });
          }
          child.material = this.wheelMaterial;
          this.addBearingCaps(child);
        } else if (name.includes('bearing')) {
          this.bearingRefs.push(child);
          if (!this.bearingMaterial) {
            this.bearingMaterial = new THREE.MeshStandardMaterial({ metalness: 0.85, roughness: 0.22 });
          }
          child.material = this.bearingMaterial;
          this.updateBearingColor(this.selectedBearing.color);
        } else if (name.includes('bushing')) {
          const isBottom = name.includes('bottom') || name.includes('lower');
          const materialKey = isBottom ? 'truckBushingBottomMaterial' : 'truckBushingTopMaterial';
          if (!this[materialKey]) {
            this[materialKey] = new THREE.MeshStandardMaterial({ metalness: 0.04, roughness: 0.55 });
          }
          child.material = this[materialKey];
        } else if (name.includes('washer')) {
          const isBottom = name.includes('bottom') || name.includes('lower');
          const materialKey = isBottom ? 'truckWasherBottomMaterial' : 'truckWasherTopMaterial';
          if (!this[materialKey]) {
            this[materialKey] = new THREE.MeshStandardMaterial({ metalness: 0.82, roughness: 0.24 });
          }
          child.material = this[materialKey];
        } else if (name.includes('truck') || name.includes('baseplate')) {
          if (name.includes('truck1')) this.truck1Mesh = child;
          if (name.includes('truck2')) this.truck2Mesh = child;
          if (name.includes('baseplate')) this.baseplatesMesh = child;
          if (!this.truckHangerMaterial) {
            this.truckHangerMaterial = new THREE.MeshStandardMaterial({ metalness: 0.8, roughness: 0.25 });
          }
          if (!this.truckBaseplateMaterial) {
            this.truckBaseplateMaterial = new THREE.MeshStandardMaterial({ metalness: 0.75, roughness: 0.28 });
          }
          if (!this.truckHangerMaterial.normalMap || !this.truckBaseplateMaterial.normalMap) {
            this.textureLoader.load('https://cdn.shopify.com/s/files/1/0767/1719/5522/files/metal-normal.avif?v=1777292849', (normal) => {
              normal.wrapS = normal.wrapT = THREE.RepeatWrapping;
              normal.repeat.set(8, 8);
              normal.anisotropy = 8;
              [this.truckHangerMaterial, this.truckBaseplateMaterial].forEach(material => {
                if (!material) return;
                material.normalMap = normal;
                material.normalScale.set(0.3, 0.3);
                material.needsUpdate = true;
              });
              this.requestSceneRender();
            });
          }
          child.material = name.includes('baseplate') ? this.truckBaseplateMaterial : this.truckHangerMaterial;
        } else if (name === 'bolts') {
          this.boltsMesh = child;
          this.boltMaterial = new THREE.MeshStandardMaterial({ metalness: 0.5, roughness: 0.3 });
          this.axleNutMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color('#050505'), metalness: 0.9, roughness: 0.22 });
          this.kingpinNutMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color('#050505'), metalness: 0.9, roughness: 0.22 });
          this.applyBoltMaterialGroups(this.boltsMesh);
          this.updateHardwareColors(this.selectedHardware);
        }
      });
      this.updateGripTexture(this.selectedGrip.texture);
      this.updateGripColor(this.selectedGrip.color);
      this.updateWheelTexture(this.selectedWheel.texture);
      this.updateBearingColor(this.selectedBearing.color);
      this.updateTruckColor(this.selectedTruck.color, this.selectedTruck.baseplateColor, this.selectedTruck);
      this.updateHardwareColors(this.selectedHardware);
      this.scene.add(model);
      this.centerCameraOnModel(model);
      this.requestSceneRender(true);
      this.revealCustomizerWhenPainted();
    };
    try {
      draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
      const loader = new GLTFLoader();
      loader.setDRACOLoader(draco);
      loader.load(this.modelUrl, onLoad, undefined, error => {
        releaseDecoder();
        console.error(error);
      });
    } catch (error) {
      releaseDecoder();
      throw error;
    }
  }
  revealCustomizerWhenPainted() {
    if (this.hasRevealedCustomizer) return;
    this.hasRevealedCustomizer = true;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.setAttribute('aria-busy', 'false');
        this.querySelector('#bc-preloader')?.classList.add('is-hidden');
        document.getElementById('bc-page-boot')?.remove();
        this.scheduleInitialDrawerImages();
      });
    });
  }
  addBearingCaps(wheelMesh) {
    if (!wheelMesh) return;
    if (!this.bearingMaterial) {
      this.bearingMaterial = new THREE.MeshStandardMaterial({ metalness: 0.85, roughness: 0.22 });
    }
    if (!this.bearingCapGeometry) {
      this.bearingCapGeometry = new THREE.CylinderGeometry(0.028, 0.028, 0.012, 32);
    }
    [-1, 1].forEach(side => {
      const cap = new THREE.Mesh(this.bearingCapGeometry, this.bearingMaterial);
      cap.name = 'GeneratedBearingCap';
      cap.position.x = side * 0.032;
      cap.rotation.z = Math.PI / 2;
      cap.castShadow = true;
      cap.receiveShadow = true;
      wheelMesh.add(cap);
      this.bearingRefs.push(cap);
    });
    this.updateBearingColor(this.selectedBearing.color);
  }
  preloadCardTexture(card) {
    const type = card?.getAttribute('data-type');
    if (type !== 'deck' && type !== 'grip' && type !== 'wheel') return Promise.resolve(null);
    const sourceUrl = String(card.getAttribute('data-texture') || '').trim();
    const url = this.getFastTextureUrl(type, sourceUrl);
    if (!url) return Promise.resolve(null);
    // A touch can be the beginning of a swipe. Warm this card without stopping
    // the drawer's observers or slowing down the rest of the visible pictures.
    this.textureWarmQueue = this.textureWarmQueue.filter(item => item.url !== url);
    this.textureWarmQueuedUrls.delete(url);
    return this.preloadTexture(url, 'high');
  }
  getFastTextureUrl(type, url) {
    const sourceUrl = String(url || '').trim();
    if (!sourceUrl || (type !== 'deck' && type !== 'grip')) return sourceUrl;
    try {
      const parsed = new URL(sourceUrl, window.location.href);
      const isShopifyImage = parsed.hostname === 'cdn.shopify.com' || parsed.pathname.includes('/cdn/shop/');
      const currentWidth = Number.parseInt(parsed.searchParams.get('width') || '', 10);
      if (!isShopifyImage || (Number.isFinite(currentWidth) && currentWidth <= this.fastSurfaceTextureWidth)) {
        return sourceUrl;
      }
      parsed.searchParams.set('width', String(this.fastSurfaceTextureWidth));
      return parsed.href;
    } catch (error) {
      return sourceUrl;
    }
  }
  getDrawerTextureWarmLead(body) {
    const viewportHeight = body?.clientHeight || window.innerHeight || 720;
    return Math.round(Math.min(800, Math.max(420, viewportHeight)));
  }
  getDrawerTextureWarmDistance(type, card) {
    const body = this.querySelector(`#bc-drawer-${type} .bc-drawer__body`);
    if (!body || !card) return Infinity;
    const rootRect = body.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    return Math.max(0, rootRect.top - cardRect.bottom, cardRect.top - rootRect.bottom);
  }
  isDrawerTextureWarmCandidate(type, card, generation) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const body = drawer?.querySelector('.bc-drawer__body');
    if (!drawer?.classList.contains('is-open') || !body || !card?.isConnected) return false;
    if (this.drawerTextureGenerations[type] !== generation || card.classList.contains('is-filtered-out')) return false;
    const rootRect = body.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    if (cardRect.width <= 0 || cardRect.height <= 0) return false;
    const lead = this.getDrawerTextureWarmLead(body);
    return cardRect.bottom >= rootRect.top - lead && cardRect.top <= rootRect.bottom + lead;
  }
  queueDrawerTextureWarm(type, card, generation) {
    if (!this.isDrawerTextureWarmCandidate(type, card, generation)) return;
    const sourceUrl = String(card.getAttribute('data-texture') || '').trim();
    const url = this.getFastTextureUrl(type, sourceUrl);
    if (!url) return;
    if (this.textureCache[url]) {
      this.markTextureUsed(url);
      return;
    }
    if (this.texturePromises[url] || this.textureWarmQueuedUrls.has(url)) return;
    // A quick scroll can leave a full queue of offscreen cards. Make space for
    // the new viewport before admitting more speculative work.
    this.textureWarmQueue = this.textureWarmQueue.filter(item =>
      this.isDrawerTextureWarmCandidate(item.type, item.card, item.generation)
    );
    this.textureWarmQueuedUrls = new Set(this.textureWarmQueue.map(item => item.url));
    this.textureWarmQueue.sort((a, b) =>
      this.getDrawerTextureWarmDistance(a.type, a.card) - this.getDrawerTextureWarmDistance(b.type, b.card)
    );
    if (this.textureWarmQueue.length >= this.textureWarmQueueLimit) {
      const last = this.textureWarmQueue[this.textureWarmQueue.length - 1];
      if (this.getDrawerTextureWarmDistance(type, card) >= this.getDrawerTextureWarmDistance(last.type, last.card)) return;
      this.textureWarmQueue.pop();
      this.textureWarmQueuedUrls.delete(last.url);
    }
    this.textureWarmQueue.push({ type, card, generation, url });
    this.textureWarmQueuedUrls.add(url);
    this.textureWarmQueue.sort((a, b) =>
      this.getDrawerTextureWarmDistance(a.type, a.card) - this.getDrawerTextureWarmDistance(b.type, b.card)
    );
    this.processTextureWarmQueue();
  }
  processTextureWarmQueue() {
    while (this.textureWarmActive < this.textureWarmConcurrency && this.textureWarmQueue.length) {
      const item = this.textureWarmQueue.shift();
      this.textureWarmQueuedUrls.delete(item.url);
      if (!this.isDrawerTextureWarmCandidate(item.type, item.card, item.generation)) continue;
      this.textureWarmActive += 1;
      // fetch() cannot promote an in-flight request. Auto keeps a preview that
      // becomes the selected design from being stuck behind low-priority work.
      this.preloadTexture(item.url, 'auto').finally(() => {
        this.textureWarmActive = Math.max(0, this.textureWarmActive - 1);
        this.processTextureWarmQueue();
      });
    }
  }
  cancelDrawerTextureWarmup(type) {
    if (!type) return;
    this.drawerTextureWarmSchedules?.get(type)?.();
    this.drawerTextureWarmSchedules?.delete(type);
    this.drawerTextureObservers.get(type)?.disconnect();
    this.drawerTextureObservers.delete(type);
    this.drawerTextureGenerations[type] = (this.drawerTextureGenerations[type] || 0) + 1;
    this.textureWarmQueue = this.textureWarmQueue.filter(item => item.type !== type);
    this.textureWarmQueuedUrls = new Set(this.textureWarmQueue.map(item => item.url));
  }
  scheduleDrawerTextureWarmup(type, afterTransition = false) {
    if (!this.isConnected || !['deck', 'grip', 'wheel'].includes(type)) return;
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    if (!drawer?.classList.contains('is-open')) return;
    this.drawerTextureWarmSchedules ||= new Map();
    // Keep an opening drawer's existing transition wait when filters update.
    if (this.drawerTextureWarmSchedules.has(type)) return;
    const content = drawer.querySelector('.bc-drawer__content');
    let timer = null;
    let idle = null;
    let cancelled = false;
    const cancel = () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
      if (idle !== null) window.cancelIdleCallback?.(idle);
      content?.removeEventListener('transitionend', onTransitionEnd);
    };
    const warm = () => {
      if (cancelled) return;
      cancel();
      this.drawerTextureWarmSchedules.delete(type);
      if (this.isConnected && drawer.classList.contains('is-open')) this.preloadDrawerTextures(type);
    };
    const yieldToInput = () => {
      if (timer !== null) window.clearTimeout(timer);
      content?.removeEventListener('transitionend', onTransitionEnd);
      if (typeof window.requestIdleCallback === 'function') {
        idle = window.requestIdleCallback(warm, { timeout: 150 });
      } else {
        timer = window.setTimeout(warm, 32);
      }
    };
    const onTransitionEnd = event => {
      if (event.target === content && event.propertyName === 'transform') yieldToInput();
    };
    this.drawerTextureWarmSchedules.set(type, cancel);
    if (afterTransition) {
      content?.addEventListener('transitionend', onTransitionEnd);
      // Covers reduced motion, missing transition events and background tabs.
      timer = window.setTimeout(yieldToInput, 250);
    } else {
      yieldToInput();
    }
  }
  preloadDrawerTextures(type) {
    if (!this.isConnected || (type !== 'deck' && type !== 'grip' && type !== 'wheel')) return;
    this.cancelDrawerTextureWarmup(type);
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const body = drawer?.querySelector('.bc-drawer__body');
    if (!drawer?.classList.contains('is-open') || !body) return;
    const generation = this.drawerTextureGenerations[type];
    const cards = this.getPrimeableDrawerCards(type);
    const initialLimit = window.matchMedia('(hover: none) and (pointer: coarse)').matches ? 4 : 6;
    cards
      .filter(card => this.isDrawerTextureWarmCandidate(type, card, generation))
      .sort((a, b) => this.getDrawerTextureWarmDistance(type, a) - this.getDrawerTextureWarmDistance(type, b))
      .slice(0, initialLimit)
      .forEach(card => this.queueDrawerTextureWarm(type, card, generation));
    if (!('IntersectionObserver' in window)) return;
    const lead = this.getDrawerTextureWarmLead(body);
    const observer = new IntersectionObserver(entries => {
      entries
        .filter(entry => entry.isIntersecting || entry.intersectionRatio > 0)
        .sort((a, b) => this.getDrawerTextureWarmDistance(type, a.target) - this.getDrawerTextureWarmDistance(type, b.target))
        .forEach(entry => this.queueDrawerTextureWarm(type, entry.target, generation));
    }, { root: body, rootMargin: `${lead}px 0px`, threshold: 0 });
    cards.forEach(card => observer.observe(card));
    this.drawerTextureObservers.set(type, observer);
  }
  preloadTexture(url, priority = 'auto') {
    if (!url) return Promise.resolve(null);
    return this.loadTexture(url, priority).catch(error => {
      console.error('Board customizer failed to preload texture:', url, error);
      return null;
    });
  }
  prepareTexture(tex) {
    tex.flipY = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    if (this.renderer) {
      tex.anisotropy = Math.min(this.renderer.capabilities.getMaxAnisotropy(), 8);
    }
    tex.needsUpdate = true;
    return tex;
  }
  markTextureUsed(url) {
    if (!url) return;
    this.textureUsageSequence += 1;
    this.textureUsage.set(url, this.textureUsageSequence);
  }
  getProtectedTextureUrls() {
    const urls = new Set([
      this.defaultDeckUrl,
      this.defaultGripUrl,
      this.defaultWheelUrl,
      this.selectedDeck?.texture,
      this.selectedGrip?.texture,
      this.selectedWheel?.texture
    ].filter(Boolean));
    [this.deckMaterial, this.gripMaterial, this.wheelMaterial].forEach(material => {
      const sourceUrl = material?.map?.userData?.bcSourceUrl;
      if (sourceUrl) urls.add(sourceUrl);
    });
    return urls;
  }
  trimTextureCache() {
    const protectedUrls = this.getProtectedTextureUrls();
    while (Object.keys(this.textureCache).length > this.textureCacheLimit) {
      const candidate = Object.keys(this.textureCache)
        .filter(url => !protectedUrls.has(url) && !this.texturePromises[url])
        .sort((a, b) => (this.textureUsage.get(a) || 0) - (this.textureUsage.get(b) || 0))[0];
      if (!candidate) break;
      const texture = this.textureCache[candidate];
      delete this.textureCache[candidate];
      this.textureUsage.delete(candidate);
      texture?.dispose?.();
      THREE.Cache.remove(candidate);
    }
  }
  loadTextureResource(url, priority = 'auto') {
    const loadWithTextureLoader = (textureUrl) => new Promise((resolve, reject) => {
      this.textureLoader.load(textureUrl, resolve, undefined, reject);
    });
    if (typeof window.fetch !== 'function' || typeof URL.createObjectURL !== 'function') {
      return loadWithTextureLoader(url);
    }
    const requestOptions = {
      cache: 'default',
      headers: { Accept: this.textureFetchAccept }
    };
    if (priority === 'high' || priority === 'low') requestOptions.priority = priority;
    return window.fetch(url, requestOptions)
      .then(response => {
        if (!response.ok) throw new Error(`Texture request failed with ${response.status}`);
        return response.blob();
      })
      .then(blob => new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(blob);
        const releaseObjectUrl = () => {
          THREE.Cache.remove(objectUrl);
          URL.revokeObjectURL(objectUrl);
        };
        this.textureLoader.load(objectUrl, (texture) => {
          releaseObjectUrl();
          resolve(texture);
        }, undefined, (error) => {
          releaseObjectUrl();
          reject(error);
        });
      }))
      .catch(() => loadWithTextureLoader(url));
  }
  loadTexture(url, priority = 'auto') {
    if (!url) return Promise.reject(new Error('Missing texture URL'));
    if (this.textureCache[url]) {
      this.markTextureUsed(url);
      return Promise.resolve(this.textureCache[url]);
    }
    if (this.texturePromises[url]) return this.texturePromises[url];
    const lifecycleGeneration = this.textureLifecycleGeneration;
    let texturePromise;
    texturePromise = new Promise((resolve, reject) => {
      this.loadTextureResource(url, priority).then((tex) => {
        if (!this.isConnected || lifecycleGeneration !== this.textureLifecycleGeneration) {
          if (this.texturePromises[url] === texturePromise) delete this.texturePromises[url];
          THREE.Cache.remove(url);
          tex.dispose?.();
          resolve(null);
          return;
        }
        this.prepareTexture(tex);
        tex.userData.bcSourceUrl = url;
        this.textureCache[url] = tex;
        this.markTextureUsed(url);
        if (this.texturePromises[url] === texturePromise) delete this.texturePromises[url];
        this.trimTextureCache();
        resolve(tex);
      }).catch((error) => {
        if (this.texturePromises[url] === texturePromise) delete this.texturePromises[url];
        if (!this.isConnected || lifecycleGeneration !== this.textureLifecycleGeneration) {
          resolve(null);
          return;
        }
        reject(error);
      });
    });
    this.texturePromises[url] = texturePromise;
    return texturePromise;
  }
  updateDeckTexture(url) {
    const targetUrl = String(url || this.defaultDeckUrl || '').trim();
    const requestId = ++this.deckTextureRequestId;
    if (!this.deckMaterial) return;
    if (!targetUrl) {
      this.deckMaterial.map = null;
      this.deckMaterial.needsUpdate = true;
      this.requestSceneRender();
      return;
    }
    const applyTexture = (tex) => {
      if (!tex || requestId !== this.deckTextureRequestId || !this.deckMaterial) return false;
      this.deckMaterial.map = tex;
      this.deckMaterial.needsUpdate = true;
      this.requestSceneRender();
      return true;
    };
    const cachedTexture = this.textureCache[targetUrl];
    if (cachedTexture) {
      this.markTextureUsed(targetUrl);
      applyTexture(cachedTexture);
      return;
    }
    const fastUrl = this.getFastTextureUrl('deck', targetUrl);
    const fastTexturePromise = fastUrl !== targetUrl
      ? this.loadTexture(fastUrl, 'high').catch(() => null)
      : Promise.resolve(null);
    fastTexturePromise.then((fastTexture) => {
      if (requestId !== this.deckTextureRequestId || !this.deckMaterial) return null;
      if (fastTexture) applyTexture(fastTexture);
      return this.loadTexture(targetUrl, fastTexture ? 'low' : 'high');
    }).then((tex) => {
      applyTexture(tex);
    }).catch((error) => {
      if (requestId !== this.deckTextureRequestId || !this.deckMaterial) return;
      console.error('Failed to load deck texture:', error);
      if (targetUrl !== this.defaultDeckUrl) this.updateDeckTexture(this.defaultDeckUrl);
    });
  }
  updateGripTexture(url) {
    if (!url || !this.gripMaterial) return;
    const targetUrl = String(url).trim();
    const requestId = ++this.gripTextureRequestId;
    const applyTexture = (tex) => {
      if (!tex || requestId !== this.gripTextureRequestId || !this.gripMaterial) return false;
      const usesDefaultGripTexture = this.defaultGripUrl && targetUrl === this.defaultGripUrl;
      const wrapping = usesDefaultGripTexture ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
      // Reusing a prepared texture does not require another image upload.
      // Only sampler changes need an update; repeat/offset update as uniforms.
      if (tex.wrapS !== wrapping || tex.wrapT !== wrapping) {
        tex.wrapS = wrapping;
        tex.wrapT = wrapping;
        tex.needsUpdate = true;
      }
      tex.repeat.set(usesDefaultGripTexture ? 9 : 1, usesDefaultGripTexture ? 9 : 1);
      tex.offset.set(0, 0);
      if (this.gripMaterial.map !== tex) {
        this.gripMaterial.map = tex;
        this.gripMaterial.needsUpdate = true;
      }
      this.requestSceneRender();
      return true;
    };
    const cachedTexture = this.textureCache[targetUrl];
    if (cachedTexture) {
      this.markTextureUsed(targetUrl);
      applyTexture(cachedTexture);
      return;
    }
    const fastUrl = this.getFastTextureUrl('grip', targetUrl);
    const fastTexturePromise = fastUrl !== targetUrl
      ? this.loadTexture(fastUrl, 'high').catch(() => null)
      : Promise.resolve(null);
    fastTexturePromise.then((fastTexture) => {
      if (requestId !== this.gripTextureRequestId || !this.gripMaterial) return null;
      if (fastTexture) applyTexture(fastTexture);
      return this.loadTexture(targetUrl, fastTexture ? 'low' : 'high');
    }).then((tex) => {
      applyTexture(tex);
    }).catch((error) => {
      if (requestId !== this.gripTextureRequestId || !this.gripMaterial) return;
      console.error('Failed to load grip texture:', error);
    });
  }
  updateWheelTexture(url) {
    const targetUrl = String(url || '').trim();
    const requestId = ++this.wheelTextureRequestId;
    if (!this.wheelMaterial) return;
    if (!targetUrl) {
      this.wheelMaterial.map = null;
      this.wheelMaterial.needsUpdate = true;
      this.requestSceneRender();
      return;
    }
    this.loadTexture(targetUrl, 'high').then((tex) => {
      if (!tex || requestId !== this.wheelTextureRequestId || !this.wheelMaterial) return;
      this.wheelMaterial.map = tex;
      this.wheelMaterial.needsUpdate = true;
      this.requestSceneRender();
    }).catch((error) => console.error('Failed to load wheel texture:', error));
  }
  normalizeColorValue(colorValue) {
    const color = String(colorValue || '').trim().toLowerCase();
    const namedColors = {
      'light blue': '#7DD3FC',
      lightblue: '#7DD3FC',
      'sky blue': '#7DD3FC',
      skyblue: '#7DD3FC',
      blue: '#1D4ED8',
      black: '#111111',
      gold: '#D4A72C',
      silver: '#C9C9C9',
      white: '#FFFFFF',
      red: '#C62828',
      green: '#15803D',
      yellow: '#FACC15',
      purple: '#7E22CE',
      orange: '#F97316'
    };
    return namedColors[color] || colorValue;
  }
  applyTruckPaint(material, colorHex, metalness, roughness) {
    if (!material || !colorHex) return;
    const color = new THREE.Color(this.normalizeColorValue(colorHex));
    const hsl = {};
    color.getHSL(hsl);
    const isWhitePaint = hsl.l > 0.86 && hsl.s < 0.18;
    material.color.copy(color);
    material.metalness = isWhitePaint ? 0.02 : metalness;
    material.roughness = isWhitePaint ? 0.42 : roughness;
    if (isWhitePaint) {
      material.emissive.copy(color);
      material.emissiveIntensity = 0.08;
    } else {
      material.emissive.set('#000000');
      material.emissiveIntensity = 0;
    }
    material.needsUpdate = true;
    this.requestSceneRender();
  }
  updateTruckColor(hangerColorHex, baseplateColorHex = hangerColorHex, parts = {}) {
    this.applyTruckPaint(this.truckHangerMaterial, hangerColorHex, 0.8, 0.25);
    this.applyTruckPaint(this.truckBaseplateMaterial, baseplateColorHex, 0.75, 0.28);
    this.applyTruckPaint(this.truckBushingTopMaterial, parts.bushingTopColor || parts.bushingColor || '#111111', 0.04, 0.55);
    this.applyTruckPaint(this.truckBushingBottomMaterial, parts.bushingBottomColor || parts.bushingTopColor || '#111111', 0.04, 0.55);
    this.applyTruckPaint(this.truckWasherTopMaterial, parts.washerTopColor || baseplateColorHex, 0.82, 0.24);
    this.applyTruckPaint(this.truckWasherBottomMaterial, parts.washerBottomColor || parts.washerTopColor || baseplateColorHex, 0.82, 0.24);
  }
  updateGripColor(colorHex) {
    if (!this.gripMaterial || !colorHex) return;
    const hasCustomGripTexture = this.selectedGrip?.texture && this.defaultGripUrl && this.selectedGrip.texture !== this.defaultGripUrl;
    this.gripMaterial.color.set(hasCustomGripTexture ? '#ffffff' : this.normalizeColorValue(colorHex));
    this.gripMaterial.needsUpdate = true;
    this.requestSceneRender();
  }
  updateBearingColor(colorHex) {
    if (!this.bearingMaterial || !colorHex) return;
    this.applyTruckPaint(this.bearingMaterial, colorHex, 0.85, 0.22);
  }
  applyBoltMaterialGroups(mesh) {
    if (!mesh?.geometry || !this.boltMaterial) return;
    if (!this.axleNutMaterial) {
      this.axleNutMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color('#050505'), metalness: 0.9, roughness: 0.22 });
    }
    if (!this.kingpinNutMaterial) {
      this.kingpinNutMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color('#050505'), metalness: 0.9, roughness: 0.22 });
    }
    const geometry = mesh.geometry;
    const position = geometry.attributes.position;
    if (!position) {
      mesh.material = this.boltMaterial;
      return;
    }
    if (geometry.userData.bcHardwareSplit) {
      mesh.material = [this.boltMaterial, this.axleNutMaterial, this.kingpinNutMaterial];
      return;
    }
    const index = geometry.index;
    const boltIndices = [];
    const axleNutIndices = [];
    const kingpinNutIndices = [];
    const triangleCount = Math.floor((index ? index.count : position.count) / 3);
    const nutThresholdY = -0.02;
    const axleNutThresholdX = 0.18;
    const getIndex = index
      ? (offset) => index.getX(offset)
      : (offset) => offset;
    for (let tri = 0; tri < triangleCount; tri += 1) {
      const a = getIndex(tri * 3);
      const b = getIndex(tri * 3 + 1);
      const c = getIndex(tri * 3 + 2);
      const centerX = (position.getX(a) + position.getX(b) + position.getX(c)) / 3;
      const centerY = (position.getY(a) + position.getY(b) + position.getY(c)) / 3;
      let target = boltIndices;
      if (centerY < nutThresholdY) {
        target = Math.abs(centerX) > axleNutThresholdX ? axleNutIndices : kingpinNutIndices;
      }
      target.push(a, b, c);
    }
    if (!boltIndices.length || (!axleNutIndices.length && !kingpinNutIndices.length)) {
      mesh.material = this.boltMaterial;
      return;
    }
    const groups = [
      { indices: boltIndices, materialIndex: 0 },
      { indices: axleNutIndices, materialIndex: 1 },
      { indices: kingpinNutIndices, materialIndex: 2 }
    ].filter(group => group.indices.length > 0);
    geometry.setIndex(groups.flatMap(group => group.indices));
    geometry.clearGroups();
    let start = 0;
    groups.forEach(group => {
      geometry.addGroup(start, group.indices.length, group.materialIndex);
      start += group.indices.length;
    });
    geometry.computeBoundingSphere();
    geometry.userData.bcHardwareSplit = true;
    mesh.material = [this.boltMaterial, this.axleNutMaterial, this.kingpinNutMaterial];
  }
  paintHardwareMaterial(material, colorHex) {
    if (material && colorHex) {
      const color = new THREE.Color(this.normalizeColorValue(colorHex));
      const hsl = {};
      color.getHSL(hsl);
      if (hsl.l <= 0.15) {
        material.metalness = 0.9;
        material.roughness = 0.2;
        color.setHSL(hsl.h, hsl.s, 0.15); // Lighten pure black slightly for specularity
      } else {
        material.metalness = 0.5;
        material.roughness = 0.3;
      }
      material.color.copy(color);
      material.needsUpdate = true;
      this.requestSceneRender();
    }
  }
  updateHardwareColors(hardware) {
    const colors = typeof hardware === 'object' && hardware
      ? hardware
      : { color: hardware };
    this.paintHardwareMaterial(this.boltMaterial, colors.color);
    this.paintHardwareMaterial(this.axleNutMaterial, colors.axleNutColor || '#050505');
    this.paintHardwareMaterial(this.kingpinNutMaterial, colors.kingpinNutColor || '#050505');
  }
  updateHardwareColor(colorHex) {
    this.updateHardwareColors({ color: colorHex });
  }
  updateBoltColor(colorHex) {
    this.updateHardwareColor(colorHex);
  }
  centerCameraOnModel(model) {
    const bounds = new THREE.Box3().setFromObject(model, true);
    if (bounds.isEmpty()) return;
    const center = bounds.getCenter(new THREE.Vector3());
    const offset = center.clone().sub(this.defaultCameraTarget);
    // Fit the orbit to the transformed board, excluding the floor and scenery.
    // Shift the camera equally to retain the existing viewing angle and distance.
    this.defaultCameraTarget.copy(center);
    this.defaultCameraPosition.add(offset);
    this.camera.position.add(offset);
    this.controls.target.add(offset);
    this.controls.update();
  }
  resetCamera() {
    this.animateCamera(this.defaultCameraPosition.clone(), this.defaultCameraTarget.clone());
  }
  cancelCameraTransition() {
    const motion = getMotion();
    if (this.camera) motion.killTweensOf(this.camera.position);
    if (this.controls) motion.killTweensOf(this.controls.target);
  }
  animateCamera(targetPos, targetLookAt, duration = 0.85) {
    if (!this.camera || !this.controls) return;
    this.cancelCameraTransition();
    const motion = getMotion();
    this.camera.up.set(0, 1, 0);
    // Update controls once, in the render callback, after both tweens advance.
    // Its change event keeps the next frame queued while the camera is moving.
    const requestFrame = () => this.requestSceneRender();
    motion.to(this.camera.position, {
      x: targetPos.x,
      y: targetPos.y,
      z: targetPos.z,
      duration,
      ease: 'power2.out',
      overwrite: true,
      onUpdate: requestFrame,
      onComplete: requestFrame
    });
    motion.to(this.controls.target, {
      x: targetLookAt.x,
      y: targetLookAt.y,
      z: targetLookAt.z,
      duration,
      ease: 'power2.out',
      overwrite: true,
      onUpdate: requestFrame,
      onComplete: requestFrame
    });
  }
  spinWheels() {
    if (!this.wheelRefs.length) return;
    const motion = getMotion();
    this.wheelRefs.forEach(wheel => {
      motion.killTweensOf(wheel.rotation);
      motion.to(wheel.rotation, {
        x: '-=30', duration: 2.5, ease: 'circ.out',
        onUpdate: () => this.requestSceneRender(true),
        onComplete: () => this.requestSceneRender(true)
      });
    });
  }
  updateDesktopScale() {
    const width = Math.max(1, window.innerWidth || document.documentElement.clientWidth || 390);
    const height = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 844);
    const isDesktop = window.matchMedia('(min-width: 1024px) and (orientation: landscape) and (hover: hover) and (pointer: fine)').matches;
    const isTablet = !isDesktop && width >= 750;
    const profile = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile';
    const basis = {
      desktop: { width: 1920, height: 1080, min: 0.5, max: 1.12 },
      tablet: { width: 1024, height: 768, min: 0.62, max: 1.08 },
      mobile: { width: 390, height: 844, min: 0.72, max: 1.08 }
    }[profile];
    const scale = Math.max(basis.min, Math.min(basis.max, Math.min(width / basis.width, height / basis.height)));
    const scaleValue = scale.toFixed(3);
    const copyLines = profile === 'desktop' && height >= 760 && width >= 1180 ? '3' : '2';
    const scaleSignature = `${profile}:${scaleValue}:${copyLines}`;
    if (scaleSignature === this.lastScaleSignature) return;
    this.lastScaleSignature = scaleSignature;
    const setPx = (name, value) => {
      this.style.setProperty(name, `${(value * scale).toFixed(2)}px`);
    };
    const bases = {
      desktop: {
        headerPadY: 36, headerPadX: 52, logo: 152, menu: 46, cart: 92, badge: 32, badgeFont: 16, badgeOffset: -10,
        panelPadX: 34, panelPadY: 28, panelGap: 14, title: 44,
        headGapY: 7, headGapX: 12, headPadB: 13, kickerFont: 13, kickerPadY: 5, kickerPadX: 11,
        progressFont: 13, progressPadY: 5, progressPadX: 10, optionsGap: 13,
        partPadX: 17, partPadY: 15, partGap: 7, partBorderLeft: 8, partRadius: 8, partIndex: 31, partLabel: 28, partLabelPad: 54,
        copyFont: 15, statusMin: 92, statusFont: 14, statusPadY: 6, statusPadX: 10, statusRadius: 7,
        ctaHeight: 58, ctaFont: 20, disclaimerFont: 12, disclaimerMaxH: 32,
        controlSide: 42, controlBottom: 38, controlHeight: 64, controlPadX: 22, controlGap: 14, controlRadius: 11, controlFont: 22, countFont: 16,
        controlIcon: 22, chevron: 10, chevronBorder: 3, summaryWidth: 520, resetWidth: 330,
        rowHeight: 76, rowPadY: 12, rowPadX: 18, rowRadius: 10, rowLabelFont: 19, chooseMin: 104, chooseHeight: 42, chooseFont: 14,
        assemblyHeight: 72, assemblyPadY: 12, assemblyPadX: 18
      },
      tablet: {
        headerPadY: 26, headerPadX: 34, logo: 132, menu: 42, cart: 76, badge: 28, badgeFont: 14, badgeOffset: -8,
        panelPadX: 24, panelPadY: 22, panelGap: 12, title: 36,
        headGapY: 7, headGapX: 10, headPadB: 12, kickerFont: 12, kickerPadY: 4, kickerPadX: 10,
        progressFont: 12, progressPadY: 5, progressPadX: 9, optionsGap: 11,
        partPadX: 14, partPadY: 13, partGap: 6, partBorderLeft: 7, partRadius: 8, partIndex: 27, partLabel: 23, partLabelPad: 46,
        copyFont: 14, statusMin: 84, statusFont: 13, statusPadY: 5, statusPadX: 9, statusRadius: 7,
        ctaHeight: 54, ctaFont: 18, disclaimerFont: 11, disclaimerMaxH: 30,
        controlSide: 28, controlBottom: 28, controlHeight: 56, controlPadX: 18, controlGap: 12, controlRadius: 10, controlFont: 18, countFont: 15,
        controlIcon: 18, chevron: 9, chevronBorder: 3, summaryWidth: 390, resetWidth: 260,
        rowHeight: 70, rowPadY: 11, rowPadX: 16, rowRadius: 9, rowLabelFont: 17, chooseMin: 96, chooseHeight: 38, chooseFont: 13,
        assemblyHeight: 66, assemblyPadY: 11, assemblyPadX: 16
      },
      mobile: {
        headerPadY: 18, headerPadX: 18, logo: 96, menu: 36, cart: 58, badge: 24, badgeFont: 12, badgeOffset: -6,
        panelPadX: 14, panelPadY: 14, panelGap: 8, title: 24,
        headGapY: 5, headGapX: 8, headPadB: 8, kickerFont: 10, kickerPadY: 3, kickerPadX: 8,
        progressFont: 10, progressPadY: 4, progressPadX: 8, optionsGap: 8,
        partPadX: 10, partPadY: 9, partGap: 5, partBorderLeft: 5, partRadius: 7, partIndex: 22, partLabel: 17, partLabelPad: 34,
        copyFont: 12, statusMin: 68, statusFont: 11, statusPadY: 4, statusPadX: 8, statusRadius: 6,
        ctaHeight: 44, ctaFont: 15, disclaimerFont: 10, disclaimerMaxH: 24,
        controlSide: 16, controlBottom: 16, controlHeight: 46, controlPadX: 12, controlGap: 8, controlRadius: 9, controlFont: 13, countFont: 12,
        controlIcon: 13, chevron: 8, chevronBorder: 2.4, summaryWidth: 220, resetWidth: 188,
        rowHeight: 56, rowPadY: 9, rowPadX: 12, rowRadius: 8, rowLabelFont: 14, chooseMin: 78, chooseHeight: 32, chooseFont: 11,
        assemblyHeight: 52, assemblyPadY: 9, assemblyPadX: 12
      }
    };
    const b = bases[profile];
    const scaledVars = {
      '--bc-header-pad-y': b.headerPadY,
      '--bc-header-pad-x': b.headerPadX,
      '--bc-header-logo-w': b.logo,
      '--bc-menu-icon': b.menu,
      '--bc-cart-button': b.cart,
      '--bc-cart-badge': b.badge,
      '--bc-cart-badge-offset': b.badgeOffset,
      '--bc-cart-badge-font': b.badgeFont,
      '--bc-panel-pad-x': b.panelPadX,
      '--bc-panel-pad-y': b.panelPadY,
      '--bc-panel-gap': b.panelGap,
      '--bc-builder-head-gap-y': b.headGapY,
      '--bc-builder-head-gap-x': b.headGapX,
      '--bc-builder-head-pad-b': b.headPadB,
      '--bc-builder-kicker-font': b.kickerFont,
      '--bc-builder-kicker-pad-y': b.kickerPadY,
      '--bc-builder-kicker-pad-x': b.kickerPadX,
      '--bc-title-font': b.title,
      '--bc-progress-font': b.progressFont,
      '--bc-progress-pad-y': b.progressPadY,
      '--bc-progress-pad-x': b.progressPadX,
      '--bc-options-gap': b.optionsGap,
      '--bc-part-pad-x': b.partPadX,
      '--bc-part-pad-y': b.partPadY,
      '--bc-scaled-part-pad-x': b.partPadX,
      '--bc-scaled-part-pad-y': b.partPadY,
      '--bc-part-gap': b.partGap,
      '--bc-part-border-left': b.partBorderLeft,
      '--bc-part-radius': b.partRadius,
      '--bc-part-index-font': b.partIndex,
      '--bc-part-label-font': b.partLabel,
      '--bc-part-label-pad': b.partLabelPad,
      '--bc-copy-font': b.copyFont,
      '--bc-status-min': b.statusMin,
      '--bc-status-font': b.statusFont,
      '--bc-status-pad-y': b.statusPadY,
      '--bc-status-pad-x': b.statusPadX,
      '--bc-status-radius': b.statusRadius,
      '--bc-cta-height': b.ctaHeight,
      '--bc-cta-font': b.ctaFont,
      '--bc-disclaimer-font': b.disclaimerFont,
      '--bc-disclaimer-max-h': b.disclaimerMaxH,
      '--bc-control-side': b.controlSide,
      '--bc-control-bottom': b.controlBottom,
      '--bc-control-height': b.controlHeight,
      '--bc-control-pad-x': b.controlPadX,
      '--bc-control-gap': b.controlGap,
      '--bc-control-radius': b.controlRadius,
      '--bc-control-font': b.controlFont,
      '--bc-control-count-font': b.countFont,
      '--bc-control-icon': b.controlIcon,
      '--bc-chevron-size': b.chevron,
      '--bc-chevron-border': b.chevronBorder,
      '--bc-summary-width': b.summaryWidth,
      '--bc-reset-width': b.resetWidth,
      '--bc-row-height': b.rowHeight,
      '--bc-row-pad-y': b.rowPadY,
      '--bc-row-pad-x': b.rowPadX,
      '--bc-row-radius': b.rowRadius,
      '--bc-row-label-font': b.rowLabelFont,
      '--bc-choose-min': b.chooseMin,
      '--bc-choose-height': b.chooseHeight,
      '--bc-choose-font': b.chooseFont,
      '--bc-assembly-height': b.assemblyHeight,
      '--bc-assembly-pad-y': b.assemblyPadY,
      '--bc-assembly-pad-x': b.assemblyPadX
    };
    this.style.setProperty('--bc-scale', scaleValue);
    this.style.setProperty('--bc-desktop-scale', scaleValue);
    this.style.setProperty('--bc-profile', profile);
    this.style.setProperty('--bc-copy-lines', copyLines);
    Object.entries(scaledVars).forEach(([name, value]) => setPx(name, value));
    this.style.setProperty('--bc-count-pad-y', `${Math.max(2, b.statusPadY * scale * 0.62).toFixed(2)}px`);
    this.style.setProperty('--bc-count-pad-x', `${Math.max(6, b.statusPadX * scale * 1.05).toFixed(2)}px`);
    this.style.setProperty('--bc-part-border', `${Math.max(1, 2 * scale).toFixed(2)}px`);
    this.style.setProperty('--bc-tool-border', `${Math.max(1, 2 * scale).toFixed(2)}px`);
    this.classList.toggle('is-desktop-scaled', isDesktop);
    this.classList.toggle('is-tablet-scaled', profile === 'tablet');
    this.classList.toggle('is-mobile-scaled', profile === 'mobile');
  }
  scheduleResize() {
    if (this.resizeFrameId) return;
    this.resizeFrameId = window.requestAnimationFrame(() => {
      this.resizeFrameId = 0;
      this.handleResize();
    });
  }
  handleResize() {
    this.updateDesktopScale();
    this.syncSummaryLayout?.();
    const container = this.querySelector('#bc-canvas-container');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width || container.clientWidth || 1));
    const height = Math.max(1, Math.round(rect.height || container.clientHeight || 1));
    if (this.camera && this.renderer) {
      if (width === this.lastResizeWidth && height === this.lastResizeHeight) return;
      this.lastResizeWidth = width;
      this.lastResizeHeight = height;
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height, false);
      this.requestSceneRender();
    }
  }
  async readCartAddError(response) {
    try {
      const payload = await response.clone().json();
      return payload.description || payload.message || payload.status || `Shopify returned ${response.status}`;
    } catch (jsonError) {
      try {
        const text = await response.text();
        return text || `Shopify returned ${response.status}`;
      } catch (textError) {
        return `Shopify returned ${response.status}`;
      }
    }
  }
  createBoardBundleId() {
    if (typeof window.crypto?.randomUUID === 'function') {
      return `hp-board-${window.crypto.randomUUID()}`;
    }
    const randomPart = Math.random().toString(36).slice(2, 12);
    return `hp-board-${Date.now().toString(36)}-${randomPart}`;
  }
  getBoardBundleProperties(bundleId, component) {
    return {
      '_hp_board_bundle_id': bundleId,
      '_hp_board_bundle_component': component,
      '_hp_board_bundle_version': '1'
    };
  }
  bindBoardAddLifecycle() {
    if (this.boardAddLifecycleBound) return;
    this.boardAddLifecycleBound = true;
    this.boardAddSuspended = false;
    this.handleBoardAddPageHide ||= () => this.suspendBoardAdd();
    this.handleBoardAddPageShow ||= event => {
      if (!event?.persisted && !this.boardAddSuspended) return;
      this.suspendBoardAdd();
      this.boardAddSuspended = false;
    };
    window.addEventListener('pagehide', this.handleBoardAddPageHide);
    window.addEventListener('pageshow', this.handleBoardAddPageShow);
    this.handleBoardCleanupStart ||= event => {
      if (this.catalogSuspended || !this.isConnected) return;
      const ids = event.detail?.unavailableVariantIds || [];
      if (ids.length && this.catalogRefreshPromise) {
        // A stock sweep begun before this confirmed failure must not restore a
        // stale healthy result. Newly started restock checks remain allowed.
        this.suspendCatalogAvailability({ preservePending: true });
        this.catalogSuspended = false;
      }
      const startedAt = event.detail?.availabilityStartedAt;
      const currentIds = ids.filter(id => !Number.isFinite(startedAt) || !(this.catalogObservedAt?.get(id) > startedAt));
      // This event comes only from fresh validation, never journal replay or
      // allocation shortages. Preserve any newer completed SKU observation.
      for (const id of currentIds) {
        this.catalogSilentRecoveryIds?.delete(id);
        const pending = this.pendingCatalogSelectionCleanup?.get(id);
        if (pending) pending.notify = true;
      }
      this.applyCatalogAvailability(currentIds.map(variantId => ({ variantId, available: false, reason: 'VARIANT_UNAVAILABLE' })), { synchronize: false });
    };
    this.handleBoardsRemoved ||= event => {
      const live = event.detail?.liveCleanup === true;
      this.catalogSilentRecoveryIds ||= new Set();
      for (const board of event.detail?.removed || []) {
        const staged = (board.unavailableVariantIds || []).some(id => this.pendingCatalogSelectionCleanup?.has(String(id)));
        // A current cleanup keeps its named replacement popup even when its
        // verification response was lost. Cold historical receipts stay silent.
        if (!live && !staged) for (const id of board.unavailableVariantIds || []) this.catalogSilentRecoveryIds.add(String(id));
        this.recoverUnavailableBoardSelections([board], { clearSelections: !live && !staged });
      }
      if (live || this.pendingCatalogSelectionCleanup?.size) this.flushCatalogSelectionCleanup(event.detail.cart);
      void this.refreshCatalogAvailability({ reason: 'cart-cleanup' });
    };
    this.handleCatalogCartVerified ||= event => {
      if (!this.pendingCatalogSelectionCleanup?.size || this.catalogSuspended || !this.isConnected) return;
      const checkout = window.HalfpipedBoardCheckout;
      const detail = event.detail;
      if (!checkout?.isLatestCartSettled?.() || detail?.cartRevision !== checkout.latestCartRevision
        || detail.signature !== checkout.latestCartSignature || window.HalfpipedBoardIntegrity?.isCleanupPending?.()) return;
      // An add can temporarily prevent a catalog/cart join. Its verified cart
      // publication resumes that work without ever making the add wait for it.
      this.flushCatalogSelectionCleanup(detail.cart);
      if (this.pendingCatalogSelectionCleanup.size) void this.synchronizeCatalogSelectionCleanup();
    };
    document.addEventListener?.('hp:cart:priced', this.handleCatalogCartVerified);
    document.addEventListener?.('hp:boards:cleanup-start', this.handleBoardCleanupStart);
    document.addEventListener?.('hp:boards:removed', this.handleBoardsRemoved);
  }
  recoverUnavailableBoardSelections(removed, { clearSelections = true } = {}) {
    const selections = [ ['deck', 'selectedDeck', 'deck'], ['grip', 'selectedGrip', 'grip'], ['wheel', 'selectedWheel', 'wheel'],
      ['bearing', 'selectedBearing', 'bearing'], ['truck', 'selectedTruck', 'truck'], ['hardware', 'selectedHardware', 'hardware'],
      ['skate_tool', 'selectedSkateTool', 'skate_tool'], ['assembly', 'selectedAssembly', 'assembly_item'] ];
    for (const board of removed) {
      const original = board.manifest?.parts || [];
      const active = selections.filter(([, field]) => this[field] && !this[field].isPlaceholder);
      const assemblyChoice = this.querySelector('#bc-assembly');
      const controlAssembly = assemblyChoice?.value === 'service' && !active.some(([component]) => component === 'assembly')
        ? String(assemblyChoice.getAttribute('data-variant-id') || '') : '';
      const matchesBuild = original.length === active.length + (controlAssembly ? 1 : 0) && original.length > 0 && active.every(([component, field]) =>
        original.some(part => part[0] === component && part[1] === String(this[field].variantId)))
        && original.every(([component, variantId]) => {
          const entry = selections.find(([key]) => key === component);
          const selected = entry && this[entry[1]];
          return component === 'assembly' && controlAssembly ? controlAssembly === variantId : selected && !selected.isPlaceholder && String(selected.variantId) === variantId;
        });
      const ownPartialCleanup = this.boardAddAttempt?.cleaningPartial && board.reason === 'MISSING_SELECTION'
        && board.unavailableVariantIds?.length === 0 && board.unavailableComponents?.length === 0;
      if (this.boardAddAttempt?.bundleId === board.bundleId && !ownPartialCleanup) {
        this.boardAddAttempt.status = 'invalidated';
        this.suspendBoardAdd();
        this.boardAddSuspended = false;
        this.boardAddAttempt = null;
      }
      if (!matchesBuild || !clearSelections) {
        // The initialized builder deliberately keeps its unrelated edits. The
        // historical receipt must not later clear a newly chosen/restocked build.
        window.HalfpipedBoardIntegrity?.acknowledgeRemovals?.([board.bundleId]);
        continue;
      }
      const globalFailure = ['VARIANT_NOT_FOUND', 'VARIANT_UNAVAILABLE'].includes(board.reason);
      const unavailable = new Set((board.unavailableVariantIds || []).filter(id => {
        const available = this.catalogAvailability?.get(String(id))?.available;
        return available !== true && (globalFailure || available === false);
      }));
      for (const [component, field, param] of selections) {
        if (!unavailable.has(String(this[field]?.variantId))) continue;
        this[field] = this.createEmptySelection(component);
        this.removeUrlParam(param);
        if (component === 'hardware') this.removeUrlParam('bolt');
        if (component === 'assembly') { this.setAssemblyChoiceValue(''); this.removeUrlParam('assembly'); }
      }
      const assemblyControl = this.querySelector('#bc-assembly');
      if (assemblyControl && unavailable.has(String(assemblyControl.getAttribute('data-variant-id')))) {
        this.setAssemblyChoiceValue(''); this.removeUrlParam('assembly');
      }
      this.updateDeckTexture?.(this.selectedDeck?.texture);
      this.updateGripTexture?.(this.selectedGrip?.texture);
      this.updateGripColor?.(this.selectedGrip?.color);
      this.updateWheelTexture?.(this.selectedWheel?.texture);
      this.updateBearingColor?.(this.selectedBearing?.color);
      this.updateTruckColor?.(this.selectedTruck?.color, this.selectedTruck?.baseplateColor, this.selectedTruck);
      this.updateHardwareColors?.(this.selectedHardware);
      this.renderUI();
      window.HalfpipedBoardIntegrity?.acknowledgeRemovals?.([board.bundleId]);
    }
  }
  async cleanPartialBoardAdd(attempt, cart, signal) {
    const helper = window.HalfpipedBoardIntegrity;
    if (!helper) throw new Error('Please reload your cart to remove the incomplete board.');
    attempt.cleaningPartial = true;
    try {
      for (let retry = 0; retry < 3; retry += 1) {
        if (this.getBoardAddMatch(cart, attempt) === 'absent') {
          if (!this.publishBoardAddCart(cart)) throw new Error('Your cart changed while checking the incomplete board. Please reopen it.');
          return cart;
        }
        try {
          return await helper.removeBoards(cart, { cartFingerprint: await helper.fingerprint(helper.payloadOf(cart)), removeBoards: [{
            bundleId: attempt.bundleId, reason: 'MISSING_SELECTION', message: 'The board could not be added completely.', unavailableVariantIds: [], unavailableComponents: []
          }] }, signal, { recoverFailureThroughRevision: attempt.mutationRevision });
        } catch (error) {
          if (error?.code !== 'AVAILABILITY_STALE' || retry === 2) throw error;
          // A cart edit can overtake rollback's fingerprint check. Re-read and
          // remove only this failed bundle; other boards and extras stay intact.
          cart = (await this.readBoardAddRecoveryCart(attempt, signal)).cart;
        }
      }
    } finally { attempt.cleaningPartial = false; }
  }
  boardAddStockError(items = []) {
    const error = window.HalfpipedBoardCartQuantity?.stockError?.({ exhausted: true, items })
      || new Error('Hey there! So sorry, there’s no more extra stock of this option available.');
    error.availabilityMessage = true;
    return error;
  }
  async identifyBoardAddStockItems(attempt, cart, signal) {
    const quantities = window.HalfpipedBoardCartQuantity;
    const checkout = window.HalfpipedBoardCheckout;
    if (!quantities?.stockItemsFromAvailability || typeof checkout?.requestServer !== 'function') return [];
    // Recheck the intended complete, excluding its partially added rows. Missing
    // receipt rows alone cannot tell us which product caused Shopify's rejection.
    const candidate = { ...cart, integrityPreview: true, items: [
      ...cart.items.filter(item => item.properties?._hp_board_bundle_id !== attempt.bundleId),
      ...attempt.items.map(item => ({ ...item, variant_id: item.id })),
    ] };
    const availability = await checkout.requestServer('availability', candidate, signal);
    return quantities.stockItemsFromAvailability(availability, [...(attempt.stockItems || []), ...cart.items], { includeUnavailable: true });
  }
  suspendBoardAdd() {
    this.boardAddGeneration = (this.boardAddGeneration || 0) + 1;
    this.boardAddSuspended = true;
    const work = this.boardAddWork;
    work?.controller?.abort();
    this.endBoardAddPresentation(work);
    if (work) this.restoreBoardAddButton(work);
    this.boardAddWork = null;
    this.isAddingToCart = false;
  }
  isCurrentBoardAdd(work) {
    return this.boardAddWork === work && work.generation === this.boardAddGeneration && !this.boardAddSuspended;
  }
  restoreBoardAddButton(work) {
    work.label.textContent = work.originalText;
    work.button.disabled = work.originalDisabled || this.getUnavailableRequiredHelpers().length > 0;
    work.button.classList.remove('is-added');
  }
  boardAddAbortError() {
    return Object.assign(new Error('Board add was cancelled.'), { name: 'AbortError' });
  }
  async withBoardAddDeadline(task, work, message, timeoutMs = 15000) {
    const controller = new AbortController();
    work.controller = controller;
    let expired = false;
    let timer;
    let rejectAbort;
    const aborted = new Promise((resolve, reject) => { rejectAbort = () => reject(this.boardAddAbortError()); });
    controller.signal.addEventListener('abort', rejectAbort, { once: true });
    const deadline = new Promise((resolve, reject) => {
      timer = window.setTimeout(() => {
        expired = true;
        controller.abort();
        reject(Object.assign(new Error(message), { name: 'TimeoutError' }));
      }, Math.max(1, Math.min(15000, timeoutMs)));
    });
    try {
      return await Promise.race([task(controller.signal), deadline, aborted]);
    } catch (error) {
      if (expired) throw Object.assign(new Error(message), { name: 'TimeoutError' });
      throw error;
    } finally {
      window.clearTimeout(timer);
      controller.signal.removeEventListener('abort', rejectAbort);
      if (work.controller === controller) work.controller = null;
    }
  }
  createBoardAddAttempt() {
    const bundleId = this.createBoardBundleId();
    const selectedItems = this.getSelectedBoardCartItems(bundleId);
    if (!selectedItems) return null;
    const items = Object.freeze(selectedItems.map(item => Object.freeze({
      ...item, properties: Object.freeze({ ...item.properties })
    })));
    const selections = { deck: this.selectedDeck, grip: this.selectedGrip, wheel: this.selectedWheel,
      bearing: this.selectedBearing, truck: this.selectedTruck, hardware: this.selectedHardware,
      skate_tool: this.selectedSkateTool, assembly: this.selectedAssembly };
    const selectionNames = this.getSelectedPartDetails();
    const stockItems = items.map(item => {
      const component = item.properties._hp_board_bundle_component;
      const selection = selections[component] || {};
      const detail = selectionNames.find(part => part.variantId === String(item.id));
      const options = selection.options_with_values || selection.selectedOptions;
      return Object.freeze({ variant_id: item.id,
        product_title: selection.productTitle || selection.name || selection.title || detail?.name || '',
        variant_title: this.getVariantTitle(selection),
        options_with_values: Object.freeze((Array.isArray(options) ? options : []).filter(option => option && typeof option === 'object')
          .map(option => Object.freeze({ name: option.name, value: option.value }))),
        variantOptions: Object.freeze(Array.isArray(selection.variantOptions) ? selection.variantOptions.filter(option => typeof option === 'string' || typeof option === 'number') : []),
        selectedSize: component === 'deck' ? detail?.size || '' : '',
      });
    });
    // Display data stays separate from the immutable Shopify request and receipt.
    const previewItems = items.map((item, index) => {
      const component = item.properties._hp_board_bundle_component;
      const selection = selections[component] || {};
      const raw = this.parsePriceCents(selection.variantPriceCents);
      const price = this.getBoardPromotionPriceState(component, raw).finalCents;
      return { ...item, key: `board-preview:${bundleId}:${index}`, variant_id: item.id,
        pending: true, optimistic: false, product_title: selection.productTitle || selection.name || selection.title || item.properties['Board Customizer Part'],
        // Keep every pending row in the same simple layout; Shopify supplies
        // the complete option labels when the confirmed cart replaces it.
        variant_title: '', image: selection.imageUrl || selection.images?.[0] || null,
        url: selection.url || window.location.pathname || '', price: raw, original_line_price: raw,
        final_line_price: price ?? 0 };
    });
    const orderedPreview = window.HalfpipedBoardIntegrity.orderBoardItems(previewItems);
    return { bundleId, items, previewItems: orderedPreview, selectionNames, stockItems: Object.freeze(stockItems), status: 'new', mutationRevision: null };
  }
  beginBoardAddPresentation(work, attempt) {
    work.presentationId = `${attempt.bundleId}:${this.createBoardBundleId()}`;
    work.presentationItems = attempt.previewItems;
    document.dispatchEvent(new CustomEvent('hp:board-add:presentation', {
      detail: { id: work.presentationId, items: work.presentationItems }
    }));
    if (this.cartDrawerBody) this.renderCart(this.latestCart || { items: [] }, { presentationOnly: true });
  }
  endBoardAddPresentation(work) {
    if (!work?.presentationId) return;
    const id = work.presentationId;
    work.presentationId = null;
    work.presentationItems = null;
    document.dispatchEvent(new CustomEvent('hp:board-add:presentation', { detail: { id, items: null } }));
    if (this.cartDrawerBody) this.renderCart(this.latestCart || { items: [] }, { presentationOnly: true });
  }
  getBoardAddMatch(cart, attempt) {
    if (!Array.isArray(cart?.items)) throw new Error('Could not verify the current cart.');
    const lines = cart.items.filter(item => item.properties?._hp_board_bundle_id === attempt.bundleId);
    if (!lines.length) return 'absent';
    const describe = item => JSON.stringify({
      variantId: String(item.variant_id || item.id || ''), quantity: Number(item.quantity),
      properties: Object.entries(item.properties || {}).map(([key, value]) => [key, String(value)]).sort(([a], [b]) => a.localeCompare(b)),
      sellingPlan: item.selling_plan_allocation?.selling_plan?.id || item.selling_plan_id || item.selling_plan || null
    });
    const expected = attempt.items.map(describe).sort();
    const actual = lines.map(describe).sort();
    return JSON.stringify(expected) === JSON.stringify(actual) ? 'complete' : 'mismatch';
  }
  validateBoardAddCart(cart) {
    const keys = new Set();
    if (!cart || !Array.isArray(cart.items) || typeof cart.token !== 'string' || !cart.token
      || typeof cart.currency !== 'string' || !/^[A-Z]{3}$/.test(cart.currency)
      || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0
      || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) {
      throw new Error('Could not verify the current cart. Please reopen it and try again.');
    }
    for (const item of cart.items) {
      if (!item || typeof item.key !== 'string' || !item.key || keys.has(item.key)
        || !/^[1-9]\d*$/.test(String(item.variant_id || item.id || ''))
        || !Number.isSafeInteger(item.quantity) || item.quantity < 1
        || !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0
        || !Object.prototype.hasOwnProperty.call(item, 'properties')
        || (item.properties !== null && (typeof item.properties !== 'object' || Array.isArray(item.properties)))) {
        throw new Error('Could not verify every current cart item. Please reopen your cart and try again.');
      }
      keys.add(item.key);
    }
    if (cart.items.reduce((sum, item) => sum + item.quantity, 0) !== cart.item_count) {
      throw new Error('The current cart quantities could not be verified. Please reopen your cart and try again.');
    }
  }
  isBoardAddSnapshotCurrent(snapshot) {
    const mutations = window.HalfpipedCartMutations;
    const stamped = snapshot?.cart && this.confirmedCartReads?.get(snapshot.cart);
    return Boolean(stamped?.confirmed && stamped.cartRevision === snapshot.revision
      && typeof mutations?.isPending === 'function' && !mutations.isPending()
      && snapshot.revision === mutations.getRevision());
  }
  async readBoardAddRecoveryCart(attempt, signal) {
    const mutations = window.HalfpipedCartMutations;
    if (typeof mutations?.whenIdle !== 'function' || typeof mutations.getRevision !== 'function'
      || typeof mutations.isPending !== 'function') throw new Error('Please refresh the page before trying again.');
    for (let read = 0; read < 3; read += 1) {
      await mutations.whenIdle({ signal, recoverFailureThroughRevision: attempt.mutationRevision });
      if (signal.aborted) throw this.boardAddAbortError();
      const revision = mutations.getRevision();
      const cart = await this.fetchCart(signal);
      if (signal.aborted) throw this.boardAddAbortError();
      if (!mutations.isPending() && revision === mutations.getRevision()) return { cart, revision };
    }
    throw new Error('Your cart is still changing. Please check it and try again.');
  }
  async postBoardAddAttempt(attempt, signal) {
    const helper = window.HalfpipedBoardIntegrity;
    const mutations = window.HalfpipedCartMutations;
    await mutations.whenIdle({ signal, recoverFailureThroughRevision: attempt.mutationRevision });
    return helper.withCartLock(async lockToken => {
      if (mutations.isPending()) throw new Error('Your cart is changing. Please try adding the board again.');
      const revision = mutations.getRevision();
      const before = await this.fetchCart(signal);
      if (signal.aborted || mutations.isPending() || mutations.getRevision() !== revision) throw new Error('Your cart changed before adding your board. Please try again.');
      helper.prepare(before);
      const checkout = window.HalfpipedBoardCheckout;
      if (!checkout?.config?.endpoint || typeof checkout.requestServer !== 'function') {
        throw Object.assign(new Error('Please reload this page before adding your board so we can check its stock.'), { availabilityMessage: true });
      }
      {
        const candidate = { ...before, integrityPreview: true, items: [...before.items, ...attempt.items.map(item => ({ ...item, variant_id: item.id }))] };
        const availability = await checkout.requestServer('availability', candidate, signal);
        if (signal.aborted || mutations.isPending() || mutations.getRevision() !== revision) throw new Error('Your cart changed before adding your board. Please try again.');
        const invalid = availability.removeBoards.find(board => board.bundleId === attempt.bundleId);
        if (invalid) {
          if (invalid.reason === 'INSUFFICIENT_STOCK'
            && availability.removeBoards.every(board => board.reason === 'INSUFFICIENT_STOCK')
            && availability.blockingIssues.every(issue => issue.code === 'CART_VARIANT_INSUFFICIENT_STOCK')) {
            attempt.status = 'failed';
            this.boardAddAttempt = null;
            // Allocation exhaustion is not a globally unavailable selection.
            // Keep the current build and every existing cart row unchanged.
            throw this.boardAddStockError(window.HalfpipedBoardCartQuantity?.stockItemsFromAvailability?.(availability,
              [...(attempt.stockItems || []), ...before.items]) || []);
          }
          const ids = new Set(invalid.unavailableVariantIds || []);
          const components = new Set(invalid.unavailableComponents || []);
          const affected = (attempt.selectionNames || this.getSelectedPartDetails()).filter(part => ids.has(part.variantId) || components.has(part.component));
          const message = affected.length ? this.formatUnavailableBoardParts(affected) : 'Your selected board could not be verified. Please review its parts and try again.';
          attempt.status = 'failed';
          this.boardAddAttempt = null;
          this.recoverUnavailableBoardSelections([{ ...invalid, bundleId: '', manifest: helper.manifestOf(attempt.items[0]) }]);
          void this.refreshCatalogAvailability({ reason: 'add-unavailable' });
          throw Object.assign(new Error(message), { availabilityMessage: true });
        }
        if (availability.removeBoards.length || availability.blockingIssues.length) throw new Error('Your cart needs to be checked before adding this board. Please open your cart and try again.');
      }
      if (signal.aborted || mutations.isPending() || mutations.getRevision() !== revision) throw new Error('Your cart changed before adding your board. Please try again.');
      const ineligibleAttemptParts = (attempt.selectionNames || []).filter(part => !this.isBuilderVariantEligible(part.component, part.variantId) || (part.component === 'grip' && /transparent/i.test(part.name)));
      if (ineligibleAttemptParts.length) { attempt.catalogInvalidated = true; attempt.unavailableMessage = this.formatIneligibleBuilderParts(ineligibleAttemptParts); }
      const unavailableAttemptIds = new Set(attempt.items.filter(item => !this.isCatalogVariantAvailable(item.id)).map(item => String(item.id)));
      if (attempt.catalogInvalidated || unavailableAttemptIds.size) {
        const affected = (attempt.selectionNames || []).filter(part => unavailableAttemptIds.has(part.variantId));
        if (!attempt.unavailableMessage && affected.length) attempt.unavailableMessage = this.formatUnavailableBoardParts(affected);
        attempt.status = 'failed';
        if (this.boardAddAttempt === attempt) this.boardAddAttempt = null;
        throw Object.assign(new Error(attempt.unavailableMessage || 'One or more selected board parts are no longer available. Please choose another option.'), { availabilityMessage: true });
      }
      const receipt = await this.submitBoardAddAttempt(attempt, signal, lockToken);
      return receipt;
    }, signal);
  }
  async submitBoardAddAttempt(attempt, signal, lockToken) {
    const finish = window.HalfpipedCartMutations?.begin();
    attempt.status = 'pending';
    try {
      const request = fetch(this.getCartEndpoint('/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: attempt.items }),
        hpCartLockToken: lockToken,
        signal
      });
      attempt.mutationRevision = window.HalfpipedCartMutations?.getRevision();
      const response = await request;
      const receipt = await response.json();
      if (!response.ok) {
        const error = new Error(receipt?.description || receipt?.message || 'Shopify could not add this board.');
        error.definiteCartRejection = [400, 404, 422].includes(response.status)
          && Number(receipt?.status) === response.status
          && typeof (receipt?.description || receipt?.message) === 'string';
        error.stockLimited = error.definiteCartRejection && response.status === 422
          && /\bstock\b|\bsold out\b|can(?:not|'t) add more|only\b.+\bavailable/i.test(error.message);
        throw error;
      }
      const receiptCart = { items: Array.isArray(receipt?.items) ? receipt.items : receipt?.id ? [receipt] : [] };
      if (this.getBoardAddMatch(receiptCart, attempt) !== 'complete') {
        throw new Error('Shopify did not confirm every selected board part. Please check your cart.');
      }
      // A late response may settle this receipt, but only the current UI owner
      // can render, open a drawer or show a popup.
      attempt.status = 'confirmed';
      attempt.receipt = receipt;
      return receipt;
    } catch (error) {
      attempt.status = error?.definiteCartRejection ? 'failed' : 'uncertain';
      finish?.(error);
      throw error;
    } finally {
      finish?.();
    }
  }
  publishBoardAddCart(cart) {
    const snapshot = { cart, revision: this.confirmedCartReads?.get(cart)?.cartRevision };
    if (!this.isBoardAddSnapshotCurrent(snapshot)) return false;
    this.cartLastFetchedAt = Date.now();
    this.renderCart(cart, { cartConfirmed: true });
    if (!this.isBoardAddSnapshotCurrent(snapshot)) return false;
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
    return this.isBoardAddSnapshotCurrent(snapshot);
  }
  captureBoardAddLoadingNode(work) {
    const drawer = this.cartDrawer || document.querySelector('.site-header #CartDrawer') || document.getElementById('CartDrawer');
    work.loadingNode = drawer?.querySelector('.cart-drawer-loading') || null;
  }
  showBoardAddFailure(work, message) {
    if (!this.isCurrentBoardAdd(work)) return;
    const loading = work.loadingNode;
    if (loading?.isConnected) {
      loading.textContent = 'Please close and reopen your cart to check your items.';
      loading.className = 'cart-drawer-empty';
    }
    work.catalogSelectionMessages?.delete(message);
    window.alert(message);
  }
  getSelectedBoardCartItems(bundleId) {
    if (this.clearIneligibleBuilderSelections()) return null;
    if (this.getUnavailableRequiredHelpers().length) { this.updateCatalogAvailabilityUI(); return null; }
    const knownUnavailable = this.getSelectedPartDetails().filter(part => !this.isCatalogVariantAvailable(part.variantId));
    if (knownUnavailable.length) {
      const message = this.formatUnavailableBoardParts(knownUnavailable);
      this.clearUnavailableCatalogSelections(knownUnavailable.map(part => part.variantId), { notify: false });
      window.alert(message);
      return null;
    }
    const selectedParts = [
      { type: 'Deck', key: 'deck', selection: this.selectedDeck, options: this.decks },
      { type: 'Grip', key: 'grip', selection: this.selectedGrip, options: this.grips },
      { type: 'Wheels', key: 'wheel', selection: this.selectedWheel, options: this.wheels },
      { type: 'Bearings', key: 'bearing', selection: this.selectedBearing, options: this.bearings },
      { type: 'Trucks', key: 'truck', selection: this.selectedTruck, options: this.trucks },
      { type: 'Hardware', key: 'hardware', selection: this.selectedHardware, options: this.hardware },
      { type: 'Skate Tool', key: 'skate_tool', selection: this.selectedSkateTool },
      { type: 'Assembly', key: 'assembly', selection: this.selectedAssembly }
    ];
    const missingParts = selectedParts
      .filter(part => this.querySelector(`#bc-drawer-${part.key}`) && (!part.selection || part.selection.isPlaceholder))
      .map(part => part.type);
    if (missingParts.length > 0) {
      alert(`Choose these parts first: ${missingParts.join(', ')}.`);
      return null;
    }
    const unavailableParts = selectedParts
      .filter(part => this.querySelector(`#bc-drawer-${part.key}`) && part.selection && !part.selection.isPlaceholder)
      .filter(part => !Number.isFinite(Number.parseInt(part.selection.variantId, 10)))
      .map(part => `${part.type}: ${this.getItemDisplayName(part.selection) || 'Unavailable item'}`);
    if (unavailableParts.length > 0) {
      alert(`These selections are unavailable right now: ${unavailableParts.join(', ')}.`);
      return null;
    }
    const assemblyControl = this.querySelector('#bc-assembly');
    const assemblyValue = assemblyControl?.value || '';
    const hasVisibleAssemblySelection = this.isChosenSelection(this.selectedAssembly);
    if (this.querySelector('[data-assembly-choice]') && !assemblyValue && !hasVisibleAssemblySelection) {
      this.dispatchEvent(new CustomEvent('bc:assembly-required', { bubbles: true }));
      alert('Choose how this board should arrive before adding it to cart.');
      this.querySelector('[data-assembly-choice]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      this.querySelector('[data-assembly-choice-option]')?.focus({ preventScroll: true });
      return null;
    }
    const wantsAssembly = assemblyValue === 'service';
    const assemblyVariantId = assemblyControl ? assemblyControl.getAttribute('data-variant-id') : null;
    if (wantsAssembly && !Number.isFinite(Number.parseInt(assemblyVariantId, 10))) {
      alert('The assembly service product is unavailable right now.');
      return null;
    }
    const cartItems = selectedParts
      .filter(part => part.selection && !part.selection.isPlaceholder)
      .map(part => ({
        partType: part.type,
        item: {
          id: Number.parseInt(part.selection.variantId, 10),
          quantity: 1,
          properties: {
            "Board Customizer Part": part.type,
            ...this.getBoardBundleProperties(bundleId, part.key)
          }
        }
      }))
      .filter(entry => Number.isFinite(entry.item.id));
    if (!this.isChosenSelection(this.selectedAssembly) && wantsAssembly && assemblyVariantId) {
      cartItems.push({
        partType: 'Assembly',
        item: {
          id: Number.parseInt(assemblyVariantId, 10),
          quantity: 1,
          properties: {
            "Board Customizer Part": 'Assembly',
            ...this.getBoardBundleProperties(bundleId, 'assembly')
          }
        }
      });
    }
    if (cartItems.length === 0) {
      alert('Choose at least one product part before adding to cart.');
      return null;
    }
    const manifest = JSON.stringify({ v: 1, created: Date.now(), parts: cartItems.map(entry => [entry.item.properties._hp_board_bundle_component, String(entry.item.id), entry.item.quantity]) });
    if (manifest.length > 1024) throw new Error('The board selection is too large. Please build it again.');
    return cartItems.map(entry => ({ ...entry.item, properties: { ...entry.item.properties, _hp_board_manifest: manifest } }));
  }
  async handleAddToCart() {
    if (this.boardAddLockPending || this.isAddingToCart) return;
    this.boardAddLockPending = true;
    try {
      const helper = window.HalfpipedBoardIntegrity;
      if (!helper) throw new Error('Please refresh this page before adding your board.');
      await this.performBoardAddToCart();
    } catch (error) { window.alert(error?.message || 'Could not add your board safely. Please try again.'); }
    finally { this.boardAddLockPending = false; }
  }
  async performBoardAddToCart() {
    if (this.isAddingToCart) return;
    const checkoutBusy = () => {
      const checkout = window.HalfpipedBoardCheckout;
      return Boolean(checkout?.checkoutPromise || checkout?.checkoutRecoveryPromise || checkout?.checkoutNavigationStarted);
    };
    if (checkoutBusy()) {
      window.alert('Your checkout is being prepared. Please return to your cart before adding another board.');
      return;
    }
    this.bindBoardAddLifecycle();
    if (this.boardAddSuspended) return;
    const button = this.querySelector('#bc-add-to-cart-btn');
    const label = button?.querySelector('[data-add-to-cart-label]');
    if (!button || !label) return;
    let attempt = this.boardAddAttempt;
    if (!attempt) {
      attempt = this.createBoardAddAttempt();
      if (!attempt) return;
      this.boardAddAttempt = attempt;
    }
    const work = { generation: this.boardAddGeneration || 0, button, label,
      originalText: label.textContent, originalDisabled: button.disabled, controller: null, initialMutationRevision: attempt.mutationRevision };
    this.boardAddGeneration = work.generation;
    this.boardAddWork = work;
    this.isAddingToCart = true;
    button.classList.remove('is-added');
    button.disabled = true;
    label.textContent = 'CHECKING...';
    try {
      if (attempt.status !== 'new') {
        const recoveryDeadline = Date.now() + 15000;
        let snapshot;
        for (let read = 0; read < 3; read += 1) {
          const candidate = await this.withBoardAddDeadline(signal => this.readBoardAddRecoveryCart(attempt, signal), work,
            'Checking your previous board add timed out. Please check your cart and try again.', recoveryDeadline - Date.now());
          if (!this.isCurrentBoardAdd(work)) return;
          if (!this.isBoardAddSnapshotCurrent(candidate)) continue;
          if (!this.publishBoardAddCart(candidate.cart) || !this.isBoardAddSnapshotCurrent(candidate)) continue;
          snapshot = candidate;
          break;
        }
        if (!snapshot) throw new Error('Your cart is still changing. Please check it and try again.');
        const match = this.getBoardAddMatch(snapshot.cart, attempt);
        if (match === 'complete') {
          this.boardAddAttempt = null;
          this.openCartDrawer({ load: false, showLoading: false, force: true, revealBundleId: attempt.bundleId });
          window.alert('Your previous board was already added. Please review it in your cart. No second board was added.');
          return;
        }
        if (match === 'mismatch') {
          await this.withBoardAddDeadline(signal => this.cleanPartialBoardAdd(attempt, snapshot.cart, signal), work, 'Removing the incomplete board timed out. Please reopen your cart.');
          if (!this.isCurrentBoardAdd(work)) return;
          this.boardAddAttempt = null;
          window.alert('Your incomplete board was removed. Your available selections are still here; please add the board again.');
          return;
        }
        if (!['failed', 'confirmed'].includes(attempt.status)) {
          window.alert('We could not confirm your previous board add. Please check your cart and try again shortly.');
          return;
        }
        // A definite rejection, or a completed add later removed from the cart,
        // is resolved. This explicit click may now submit the current selection.
        this.boardAddAttempt = null;
        attempt = this.createBoardAddAttempt();
        if (!attempt) return;
        this.boardAddAttempt = attempt;
      }
      label.textContent = 'ADDING...';
      // Previous-add recovery may have yielded to an already-started checkout.
      if (checkoutBusy()) {
        window.alert('Your checkout is being prepared. Please return to your cart before adding another board.');
        return;
      }
      this.beginBoardAddPresentation(work, attempt);
      this.openCartDrawer({ load: false, showLoading: true, force: true, revealBundleId: attempt.bundleId });
      this.captureBoardAddLoadingNode(work);
      await this.withBoardAddDeadline(signal => this.postBoardAddAttempt(attempt, signal), work,
        'Adding items to cart timed out. Please try again.', 8000);
      if (!this.isCurrentBoardAdd(work)) return;
      button.classList.add('is-added');
      label.textContent = 'ADDED';
      const refreshDeadline = Date.now() + 15000;
      let refreshed;
      for (let read = 0; read < 3; read += 1) {
        const snapshot = await this.withBoardAddDeadline(signal => this.refreshCartAfterAdd({ signal, work,
          recoverFailureThroughRevision: attempt.mutationRevision }), work,
        'Your board was added, but refreshing the cart timed out. Please reopen your cart and try again.', refreshDeadline - Date.now());
        if (!this.isCurrentBoardAdd(work)) return;
        if (!this.isBoardAddSnapshotCurrent(snapshot)) continue;
        refreshed = snapshot;
        break;
      }
      if (!refreshed) throw new Error('Your cart is still changing. Please reopen it and try again.');
      const match = this.getBoardAddMatch(refreshed.cart, attempt);
      if (match === 'mismatch') {
        await this.withBoardAddDeadline(signal => this.cleanPartialBoardAdd(attempt, refreshed.cart, signal), work, 'Removing the incomplete board timed out. Please reopen your cart.');
        if (!this.isCurrentBoardAdd(work)) return;
        this.boardAddAttempt = null;
        window.alert('Your incomplete board was removed. Please review your selections and add it again.');
        return;
      }
      this.boardAddAttempt = null;
      if (match === 'absent') window.alert('Your board was added, but it is no longer in your cart. Please review your cart.');
    } catch (error) {
      if (!this.isCurrentBoardAdd(work)) return;
      let recoveryUnverified = false;
      if (error?.name !== 'TimeoutError' && attempt.mutationRevision !== work.initialMutationRevision
        && (attempt.status === 'failed' || attempt.status === 'uncertain')) {
        recoveryUnverified = true;
        try {
          const snapshot = await this.withBoardAddDeadline(signal => this.readBoardAddRecoveryCart(attempt, signal), work, 'Checking your previous add timed out. Please reopen your cart.');
          if (!this.isCurrentBoardAdd(work)) return;
          const match = this.getBoardAddMatch(snapshot.cart, attempt);
          if (match === 'complete') {
            if (!this.publishBoardAddCart(snapshot.cart)) throw new Error('Your cart changed while checking your board.');
            attempt.status = 'confirmed';
            this.boardAddAttempt = null;
            return;
          }
          let stockCart = snapshot.cart;
          if (match === 'mismatch') {
            stockCart = await this.withBoardAddDeadline(signal => this.cleanPartialBoardAdd(attempt, snapshot.cart, signal), work, 'Removing the incomplete board timed out. Please reopen your cart.');
            if (!this.isCurrentBoardAdd(work)) return;
            this.boardAddAttempt = null;
          } else {
            if (!this.publishBoardAddCart(snapshot.cart)) throw new Error('Your cart changed while checking your board.');
            // Keep the receipt for the next deliberate Add click, which must
            // recheck that this same bundle is still absent before a new ID.
          }
          recoveryUnverified = false;
          if (error.stockLimited) {
            try {
              error.stockItems = await this.withBoardAddDeadline(signal => this.identifyBoardAddStockItems(attempt, stockCart, signal), work,
                'The stock details could not be checked.', 2000);
            } catch { /* Names are best effort after rollback and reconciliation. */ }
            if (!this.isCurrentBoardAdd(work)) return;
          }
        } catch { /* Retain an uncertain attempt for safe same-ID recovery. */ }
      }
      if (!this.isCurrentBoardAdd(work)) return;
      const message = recoveryUnverified ? 'We could not verify whether every part of this board was added or removed. Please reopen your cart before trying again.'
        : error?.stockLimited ? this.boardAddStockError(error.stockItems).message
        : error?.availabilityMessage ? error.message
        : attempt.catalogInvalidated && attempt.unavailableMessage ? attempt.unavailableMessage
        : error?.name === 'TimeoutError' ? error.message
        : attempt.status === 'confirmed'
          ? 'Your board was added, but we could not refresh the cart. Please reopen your cart and try again.'
          : ['failed', 'new'].includes(attempt.status)
            ? `Could not add your custom board. ${error?.message || 'Please review your selections and try again.'}`
            : 'Adding items to cart timed out. Please try again.';
      this.showBoardAddFailure(work, message);
    } finally {
      this.endBoardAddPresentation(work);
      if (this.isCurrentBoardAdd(work)) {
        this.restoreBoardAddButton(work);
        this.isAddingToCart = false;
        this.boardAddWork = null;
        for (const message of work.catalogSelectionMessages || []) window.alert(message);
      }
    }
  }

}
if (!customElements.get('board-customizer')) {
  customElements.define('board-customizer', BoardCustomizer);
}
